import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import SkillEditorBody from '../../components/SkillEditorBody';
import AgentBundlePreview from '../../components/AgentBundlePreview';
import AgentBundleItemModal, { type BundleItem } from '../../components/AgentBundleItemModal';
import McpDetailBody from '../../components/McpDetailBody';
import { extractSkillDescription } from '../../utils/skillFrontmatter';
import AgentDraftPreview from '../../components/AgentDraftPreview';
import AgentShareMenuModal from '../../components/AgentShareMenuModal';
import ToolShareMenuModal from '../../components/ToolShareMenuModal';
import { resolveAgentShare, shareAgent, previewAgentShare, listDwAgents, type AgentShareSnapshot, type DwAgentRecord, } from '../claudeagents/claudeAgentApi';
import ImportStep from '../apiintegrations/ImportStep';
import EndpointsStep from '../apiintegrations/EndpointsStep';
import ConnectStep, { authPayload, connectProblem, connectSettingsFrom, type ConnectSettings } from '../apiintegrations/ConnectStep';
import IntegrationDetailBody, { authSummary } from '../apiintegrations/IntegrationDetailBody';
import { IntegrationKeyInput, IntegrationKeyPanel } from '../apiintegrations/IntegrationKeyPanel';
import { AgentEnablementModal, AgentEnablementPanel } from '../apiintegrations/AgentEnablementPanel';
import { copyMyKeyToAgency, getCredentialStatus, getIntegration, getMyCredential, listIntegrations, publishIntegration, saveMyCredential, type InstalledCredentialState, type InstallerCredentialStatus, type InstallerKeyScope } from '../apiintegrations/apiIntegrationService';
import type { IntegrationDetail } from '../apiintegrations/apiIntegrations.types';
import { ROUTES } from '../../app/routes';
import { upsertMyTool as apiUpsertTool, submitPublishRequest as apiSubmitPublishRequest, releaseMarketplaceListing, draftMarketplaceDescription, installTool as apiInstallTool, installToolForAgency as apiInstallToolForAgency, uninstallToolForAgency as apiUninstallToolForAgency, installAgentForAgency as apiInstallAgentForAgency, summarizeAgentAgencyInstall,createShare, getShares, revokeShare, regenerateShare, uploadWidgetBundle, previewWidgetBundle, parseWidgetManifest, parseConfigValues, sandboxFromManifest, getMarketplaceStore, saveMarketplaceStore, saveMarketplaceReview, type Share, type ShareScope, type WidgetManifest, type WidgetPermission, type WidgetConnection, type WidgetSurface, type WidgetBundleOverrides, } from '../../services/myToolsApi';
import WidgetFrame from '../widgets/WidgetFrame';
import { notifyWidgetInstallsChanged, useWidgetInstallsChanged } from '../widgets/widgetEvents';
import WidgetPermissionList from '../widgets/WidgetPermissionList';
import WidgetVersionHistory from '../widgets/WidgetVersionHistory';
import WidgetControlsPanel from '../widgets/WidgetControlsPanel';
import WidgetScanReport from '../widgets/WidgetScanReport';
import { applyInstallControls, CONTROL_KEYS, DEFAULT_CONTROLS, grantedFromSelection, readInstallControls, stripControlKeys, withRecordedGrants, writeInstallControls, type WidgetInstallControls } from '../widgets/widgetInstallControls';
import WidgetPreview from '../widgets/WidgetPreview';
import { widgetAcknowledgementText, widgetNeedsAcknowledgement } from '../widgets/widgetPermissions';
import { clearWidgetStorage, currentUserKey } from '../widgets/widgetStorage';
import { WIDGET_INSTALL_DOCS_URL, WIDGET_STARTER_FILENAME, WIDGET_STARTER_URL } from '../widgets/widgetSdkLinks';
import WidgetDocsModal from '../widgets/WidgetDocsModal';
import WidgetDeveloperGuide from '../widgets/WidgetDeveloperGuide';
import { isWidgetsMockEnabled, getMockWidgetBundle, getMockInstallConfig, setMockInstallConfig, replaceMockInstallConfig } from '../../services/widgetsMock';
import StorePage, { typeInSentence } from './StorePage';
import ListingIcon from './ListingIcon';
import TypeGlyph from './TypeGlyph';
import NewAgentEditor from './NewAgentEditor';
import InstallButton from './InstallButton';
import { emptyStoreDraft, getSampleListing, getStoreListing, isSampleListing, isStoreMockEnabled, sampleListings, type StoreDraft, type StoreListing } from './storeMock';
import { iconFromFile, screenshotFromFile } from './listingImages';
import { ensureDwConfigured } from '../digitalworkforce/dwAutoSetup';
import { ALL_PUBLISHERS, matchesPublisher } from './publisherFilter';
import { ALL_GROUPS, GROUP_KEYS, GroupChips, GroupPicker, SURFACE_TO_GROUP, defaultGroups, groupLabel, groupsForSave, isSurfaceGroup, validateGroups } from './marketplaceGroups';
import MarketplaceGuide from './MarketplaceGuide';
import '../../styles/MyTools.css';
import '../../styles/AmpsGridPage.css';
function authHeaders(): Record<string, string> {
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
    };
}
type ToolType = 'skill' | 'agent' | 'workflow' | 'widget' | 'mcp_server' | 'integration';
type Visibility = 'just_me' | 'my_agency' | 'published';
type WizardView = 'create' | 'installed' | 'creations' | 'marketplace' | 'guide';
const TOOL_TYPE_TO_INT: Record<ToolType, number> = {
    skill: 0, agent: 1, workflow: 2, widget: 3, mcp_server: 4, integration: 5,
};
const INT_TO_TOOL_TYPE: Record<number, ToolType> = {
    0: 'skill', 1: 'agent', 2: 'workflow', 3: 'widget', 4: 'mcp_server', 5: 'integration',
};
const VISIBILITY_TO_INT: Record<Visibility, number> = {
    just_me: 0, my_agency: 1, published: 2,
};
const INT_TO_VISIBILITY: Record<number, Visibility> = {
    0: 'just_me', 1: 'my_agency', 2: 'published',
};
type PublishStatus = 'pending' | 'approved' | 'rejected' | 'changes_requested';
interface MyTool {
    id: string;
    type: ToolType;
    name: string;
    category: string;
    icon: string;
    // Uploaded listing image; replaces the emoji wherever the tool is shown.
    iconUrl?: string;
    description: string;
    skills: string;
    kbFileNames: string[];
    visibility: Visibility;
    createdAt: string;
    publishStatus?: PublishStatus;
    publishRequestId?: string;
    isAgencyInstalled?: boolean;
    mcpUrl?: string;
    mcpHeaders?: McpHeaderField[];
    skillContent?: string;
    // API Integration type only - points at the full definition in the API
    // Integration Wizard's own tables, not stored here.
    apiIntegrationId?: number;
    agentShareToken?: string;
    // API Integration only - 'draft' until it's been published once (MyAMP api_integrations.status).
    integrationStatus?: 'draft' | 'published';
    // Browse only, for API Integrations: who supplies the API key when installing it.
    installerKeyScope?: InstallerKeyScope;
    widgetManifest?: WidgetManifest | null;
    widgetVersion?: string;
    widgetUploadedAt?: string;
    // The creator's agency - tells your own agency's Global tools apart from another agency's.
    companyId?: string;
    creatorName?: string;
    updatedAt?: string;
    /** Owning agency's name, resolved by the API (MyToolModel.PublisherName). */
    publisherName?: string;
    /** True when the owning agency is the signed-in user's own. */
    isOwnAgency?: boolean;
    /** True when the signed-in user created it (they can't review their own listing). */
    isMine?: boolean;
    /** Marketplace groups it's added to (see marketplaceGroups.tsx). */
    groups?: string[];
    skillId?: string;
}
type InstallableTool = Pick<MyTool, 'id' | 'name' | 'type' | 'icon' | 'mcpHeaders' | 'visibility' | 'description' | 'mcpUrl' | 'apiIntegrationId' | 'installerKeyScope' | 'widgetManifest' | 'companyId' | 'groups'>;
type FieldScope = 'shared' | 'per_user';
interface ConfigField {
    key: string;
    label: string;
    type: 'text' | 'toggle' | 'select';
    value: string;
    options?: string[];
    requiredFromInstaller?: boolean;
    hint?: string;
    scope?: FieldScope;
}
interface McpHeaderField {
    key: string;
    value: string;
    requiredFromInstaller: boolean;
    hint?: string;
    label?: string;
    scope?: FieldScope;
}
interface ConfigFieldSchema {
    key: string;
    label: string;
    type: 'text' | 'toggle' | 'select';
    options?: string[];
    requiredFromInstaller: boolean;
    hint?: string;
    scope?: FieldScope;
}
interface InstalledTool {
    id: string;
    installationId: string;
    name: string;
    publisherName: string;
    isOwnAgency: boolean;
    logo: React.ReactNode;
    iconUrl?: string;
    type: ToolType;
    category: string;
    description: string;
    longDescription: string;
    rating: number;
    reviewCount: number;
    installs: string;
    version: string;
    installedAt: string;
    isNew: boolean;
    tags: string[];
    features: string[];
    changelog: {
        version: string;
        date: string;
        notes: string[];
    }[];
    reviews: {
        user: string;
        rating: number;
        comment: string;
        date: string;
    }[];
    publisherInfo: {
        name: string;
        verified: boolean;
        totalTools: number;
        bio: string;
    };
    configFields: ConfigField[];
    needsUserCredentials?: boolean;
    installSource?: 'personal' | 'agency';
    skillContent?: string;
    agentShareToken?: string;
    mcpUrl?: string;
    mcpHeaders?: McpHeaderField[];
    apiIntegrationId?: number;
    // Another agency's Global integration: the key it needs from this agency or user.
    integrationKey?: InstalledCredentialState;
    widgetManifest?: WidgetManifest | null;
    configValues?: Record<string, string>;
    groups: string[];
    skillId?: string;
    externalMcpTransactionId?: string;
    /** The listing this install came from: the Installed tab shows its store page. */
    source: MyTool;
}
function mergeMcpHeaders(rawHeadersList: any, rawSchemaList: any): McpHeaderField[] {
    const headersList: Array<Record<string, string>> = Array.isArray(rawHeadersList) ? rawHeadersList : [];
    const schemaList: ConfigFieldSchema[] = Array.isArray(rawSchemaList) ? rawSchemaList : [];
    const requiredKeys = new Set(schemaList.filter(f => f.requiredFromInstaller).map(f => f.key));
    const hintByKey = new Map(schemaList.map(f => [f.key, f.hint]));
    const labelByKey = new Map(schemaList.map(f => [f.key, f.label]));
    const scopeByKey = new Map(schemaList.map(f => [f.key, f.scope]));
    return headersList.flatMap(header => Object.entries(header).map(([key, value]) => ({
        key,
        value: value ?? '',
        requiredFromInstaller: requiredKeys.has(key),
        hint: hintByKey.get(key) || undefined,
        label: labelByKey.get(key) || undefined,
        scope: scopeByKey.get(key) || 'shared',
    })));
}
function mapApiTool(raw: any): MyTool {
    let kbFileNames: string[] = [];
    if (Array.isArray(raw.kbFileNamesList)) {
        kbFileNames = raw.kbFileNamesList;
    }
    else if (typeof raw.kbFileNames === 'string') {
        try {
            kbFileNames = JSON.parse(raw.kbFileNames);
        }
        catch { }
    }
    else if (Array.isArray(raw.kbFileNames)) {
        kbFileNames = raw.kbFileNames;
    }
    const type: ToolType = INT_TO_TOOL_TYPE[raw.type] ?? 'skill';
    const mockBundle = type === 'widget' ? getMockWidgetBundle(String(raw.id)) : null;
    const widgetManifest = type === 'widget' ? (parseWidgetManifest(raw.widgetManifest) ?? mockBundle?.manifest ?? null) : null;
    const widgetSchema: ConfigFieldSchema[] = Array.isArray(raw.configSchemaList) && raw.configSchemaList.length > 0
        ? raw.configSchemaList
        : ((mockBundle?.manifest.config ?? []) as ConfigFieldSchema[]);
    // Widgets have no header template — expose their manifest config as installer fields
    // so the existing install modal and required-field checks work unchanged.
    const mcpHeaders: McpHeaderField[] = type === 'widget'
        ? widgetSchema.map(f => ({
            key: f.key, value: '', requiredFromInstaller: !!f.requiredFromInstaller,
            hint: f.hint, label: f.label, scope: f.scope || 'shared',
        }))
        : mergeMcpHeaders(raw.mcpHeadersTemplateList, raw.configSchemaList);
    return {
        id: String(raw.id),
        type,
        name: raw.name ?? '',
        category: raw.category ?? '',
        icon: raw.icon ?? '',
        iconUrl: raw.iconUrl || undefined,
        description: raw.description ?? '',
        skills: raw.skills ?? '',
        kbFileNames,
        visibility: INT_TO_VISIBILITY[raw.visibility] ?? 'just_me',
        createdAt: raw.createdAt ?? new Date().toISOString(),
        publishStatus: raw.publishStatus ?? undefined,
        publishRequestId: raw.publishRequestId ? String(raw.publishRequestId) : undefined,
        isAgencyInstalled: raw.isAgencyInstalled ?? false,
        mcpUrl: raw.mcpUrl ?? undefined,
        mcpHeaders,
        skillContent: raw.skillContent ?? undefined,
        apiIntegrationId: raw.apiIntegrationId ?? undefined,
        agentShareToken: raw.agentShareToken ?? undefined,
        installerKeyScope: raw.installerKeyScope ?? undefined,
        widgetManifest,
        widgetVersion: raw.widgetVersion ?? mockBundle?.version ?? widgetManifest?.version ?? undefined,
        widgetUploadedAt: raw.widgetUploadedAt ?? mockBundle?.uploadedAt ?? undefined,
        companyId: raw.companyId != null ? String(raw.companyId) : undefined,
        creatorName: raw.creatorName ?? undefined,
        updatedAt: raw.updatedAt ?? undefined,
        publisherName: raw.publisherName || undefined,
        isOwnAgency: isOwnCompany(raw.companyId),
        isMine: raw.userId != null && String(raw.userId) === localStorage.getItem('userId'),
        groups: Array.isArray(raw.groups) ? raw.groups : [],
        skillId: raw.skillId || undefined,
    };
}
/** Whether `companyId` is the signed-in user's own company; undefined when either side is unknown,
 *  so a missing claim or field doesn't tag every listing "Your agency". */
function isOwnCompany(companyId: unknown): boolean | undefined {
    const mine = currentCompanyId();
    if (mine == null || companyId == null)
        return undefined;
    return String(companyId) === mine;
}
/** The signed-in user's CompanyId claim, read from their own session token; null if unavailable. */
function currentCompanyId(): string | null {
    try {
        const payload = (localStorage.getItem('authToken') ?? '').split('.')[1];
        if (!payload)
            return null;
        const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return json?.CompanyId != null ? String(json.CompanyId) : null;
    }
    catch {
        return null;
    }
}
function mapApiInstalledTool(raw: any): InstalledTool {
    const tool = mapApiTool(raw);
    const schemaList: ConfigFieldSchema[] = Array.isArray(raw.configSchemaList) && raw.configSchemaList.length > 0
        ? raw.configSchemaList
        : ((tool.type === 'widget' ? tool.widgetManifest?.config ?? [] : []) as ConfigFieldSchema[]);
    const widgetConfigValues: Record<string, string> | undefined = tool.type === 'widget'
        ? { ...(getMockInstallConfig(tool.id) ?? {}), ...parseConfigValues(raw.installationConfig) }
        : undefined;
    // Installed tools can come from other agencies (Global listings, Toolbox, share links). The
    // API names the publishing agency; the generic labels only cover an older API without it.
    // The "Your agency" tag needs a known match; the fallback label keeps assuming your own agency.
    const isOwnAgency = tool.isOwnAgency === true;
    const publisherName = tool.publisherName ?? (tool.isOwnAgency === false ? 'Another agency' : 'My Agency');
    return {
        id: tool.id,
        installationId: String(raw.installationId ?? raw.id),
        name: tool.name,
        publisherName,
        isOwnAgency,
        logo: tool.icon || <TypeGlyph type={tool.type}/>,
        iconUrl: tool.iconUrl,
        type: tool.type,
        category: tool.category,
        description: tool.description,
        longDescription: tool.description,
        rating: 0,
        reviewCount: 0,
        installs: '—',
        version: tool.widgetVersion ?? '1.0.0',
        installedAt: tool.createdAt,
        isNew: false,
        tags: [],
        features: tool.skills
            ? tool.skills.split('\n').map(s => s.trim()).filter(Boolean)
            : [],
        changelog: [],
        reviews: [],
        publisherInfo: { name: publisherName, verified: false, totalTools: 0, bio: '' },
        configFields: schemaList.map(f => ({
            key: f.key, label: f.label || f.key, type: f.type, value: widgetConfigValues?.[f.key] ?? '', options: f.options,
            requiredFromInstaller: f.requiredFromInstaller, hint: f.hint, scope: f.scope || 'shared',
        })),
        needsUserCredentials: raw.needsUserCredentials ?? false,
        installSource: raw.installSource ?? 'personal',
        skillContent: tool.skillContent,
        agentShareToken: tool.agentShareToken,
        mcpUrl: tool.mcpUrl,
        mcpHeaders: tool.mcpHeaders,
        apiIntegrationId: tool.apiIntegrationId,
        widgetManifest: tool.widgetManifest,
        configValues: widgetConfigValues,
        groups: tool.groups ?? [],
        skillId: tool.skillId,
        externalMcpTransactionId: raw.externalMcpTransactionId || undefined,
        source: tool,
    };
}
async function apiFetchMyTools(): Promise<MyTool[]> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/my`, { headers: authHeaders() });
    if (!res.ok)
        throw new Error(`Failed to load tools (${res.status})`);
    const json = await res.json();
    const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
    return list.map(mapApiTool);
}
// One tool by id - the caller's own, or a colleague's integration an Agency Admin can edit.
async function apiFetchMyTool(id: number): Promise<MyTool | null> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/${id}`, { headers: authHeaders() });
    if (!res.ok)
        return null;
    const json = await res.json();
    return json?.data ? mapApiTool(json.data) : null;
}
async function apiFetchCreations(): Promise<MyTool[]> {
    const [tools, integrations] = await Promise.all([apiFetchMyTools(), listIntegrations().catch(() => [])]);
    const statusById = new Map(integrations.map(i => [i.id, i.status]));
    return tools.map(t => t.type === 'integration' && t.apiIntegrationId
        ? { ...t, integrationStatus: statusById.get(t.apiIntegrationId) ?? 'draft' }
        : t);
}
async function apiUploadSkillKbFile(toolId: string, file: File): Promise<{
    id: string;
    filename: string;
}> {
    const formData = new FormData();
    formData.append('formFile', file);
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/${toolId}/kb-files`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
        body: formData,
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Failed to upload Knowledge Base file (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? json;
}
async function apiGetSkillKbFiles(toolId: string): Promise<{
    id: string;
    filename: string;
}[]> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/${toolId}/kb-files`, { headers: authHeaders() });
    if (!res.ok)
        throw new Error(`Failed to load Knowledge Base files (${res.status})`);
    const json = await res.json();
    const list: any[] = json.data ?? json;
    return list.map(f => ({ id: String(f.id), filename: f.filename }));
}
async function apiDeleteSkillKbFile(toolId: string, fileId: string): Promise<void> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/${toolId}/kb-files/${fileId}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok)
        throw new Error(`Failed to remove Knowledge Base file (${res.status})`);
}
async function apiDeleteTool(id: string): Promise<void> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok)
        throw new Error(`Failed to delete tool (${res.status})`);
    notifyWidgetInstallsChanged();
}
async function apiFetchInstalledTools(): Promise<InstalledTool[]> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/installed`, { headers: authHeaders() });
    if (!res.ok)
        throw new Error(`Failed to load installed tools (${res.status})`);
    const json = await res.json();
    const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
    const tools = list.map(mapApiInstalledTool);
    if (!tools.some(t => t.type === 'integration'))
        return tools;
    // Another agency's Global integrations need this agency's (or this user's) own key.
    const keys = await getCredentialStatus().catch(() => [] as InstalledCredentialState[]);
    const keyById = new Map(keys.map(k => [k.integration_id, k]));
    return tools.map(t => {
        const key = t.type === 'integration' && t.apiIntegrationId ? keyById.get(t.apiIntegrationId) : undefined;
        return key ? { ...t, integrationKey: key, needsUserCredentials: !key.set } : t;
    });
}
async function apiUninstallTool(toolId: string): Promise<void> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/${toolId}/uninstall`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok)
        throw new Error(`Failed to uninstall tool (${res.status})`);
    notifyWidgetInstallsChanged();
}
async function apiFetchMarketplaceTools(): Promise<MyTool[]> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/marketplace`, { headers: authHeaders() });
    if (!res.ok)
        throw new Error(`Failed to load marketplace (${res.status})`);
    const json = await res.json();
    const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
    return list.map(mapApiTool);
}
// Mock mode only (REACT_APP_STORE_MOCK): Browse doesn't list your own listings, and staging
// has no widget or workflow listings yet. Fill the gaps with one or two of your own listings
// per missing type, then sample widget and workflow listings, so every type's store page
// can be reviewed.
async function withMockListings(tools: MyTool[]): Promise<MyTool[]> {
    const own = await apiFetchCreations().catch(() => [] as MyTool[]);
    const present = new Set(tools.map(t => t.type));
    const extra: MyTool[] = [];
    (['agent', 'integration', 'mcp_server', 'skill'] as ToolType[]).forEach(type => {
        if (present.has(type))
            return;
        own.filter(t => t.type === type).slice(0, type === 'agent' ? 2 : 1).forEach(t => extra.push(t));
    });
    const now = new Date().toISOString();
    const samples: MyTool[] = sampleListings().filter(sl => !present.has(sl.type)).map(sl => ({
        id: sl.id, type: sl.type, name: sl.name, category: sl.category, icon: sl.icon, description: sl.description,
        skills: '', kbFileNames: [], visibility: 'published', createdAt: now,
        publisherName: 'Sample publisher', isOwnAgency: false, groups: defaultGroups(sl.type),
        widgetManifest: sl.widget ? { name: sl.name, version: sl.widget.version, entry: 'index.html', permissions: sl.widget.permissions as WidgetPermission[], surfaces: sl.widget.surfaces as WidgetSurface[] } : undefined,
        widgetVersion: sl.widget?.version,
    }));
    return [...tools, ...extra, ...samples];
}
async function apiUpdateInstallationConfig(installationId: string, mcpFieldValues: Record<string, string>, connectionValues?: Record<string, string>): Promise<void> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/installation/${installationId}/config`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ mcpFieldValues, connectionValues }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body?.success === false) {
        throw new Error((body as {
            message?: string;
        }).message || `Failed to update configuration (${res.status})`);
    }
    notifyWidgetInstallsChanged();
}
async function apiUpdateMyCredentials(toolId: string, mcpFieldValues: Record<string, string>): Promise<void> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/${toolId}/my-credentials`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ mcpFieldValues }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body?.success === false) {
        throw new Error((body as {
            message?: string;
        }).message || `Failed to save your credentials (${res.status})`);
    }
}
// Read at render, not at module load: these pages are imported when the app first
// loads (usually the login screen), before login writes isAgencyOwner, so a
// module-level read stays false until a full page reload.
const readIsAgencyOwner = () => localStorage.getItem('isAgencyOwner') === 'true';
const TOOL_TYPES: {
    type: ToolType;
    label: string;
    icon: string;
    desc: string;
}[] = [
    { type: 'skill', label: 'Skill', icon: '🧠', desc: 'A reusable AI skill or capability' },
    { type: 'agent', label: 'Agent', icon: '🤖', desc: 'An autonomous AI agent' },
    { type: 'workflow', label: 'Workflow', icon: '🔄', desc: 'An automated workflow or pipeline' },
    { type: 'widget', label: 'Widget', icon: '📊', desc: 'An embeddable UI component' },
    { type: 'mcp_server', label: 'MCP Server', icon: '🖥️', desc: 'Model Context Protocol server' },
    { type: 'integration', label: 'API Integration', icon: '🔌', desc: 'Connect to external services' },
];
const CATEGORIES = ['Productivity', 'Insurance', 'Communication', 'Data', 'AI & ML', 'Finance', 'Other'];
const CREDENTIALS_BADGE = { label: '🔑 Needs your credentials', color: '#fbbf24', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' };
const ICON_OPTIONS = [
    '🧠', '🤖', '🔄', '📊', '🖥️', '🔌', '⚡', '🎯',
    '🔍', '📋', '🛠️', '💡', '🔒', '📈', '🗂️', '✨',
    '🚀', '🔗', '📧', '🏦', '🛡️', '📝', '🔔', '🌐',
];
function formatDate(iso: string): string {
    try {
        return new Date(iso).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
        });
    }
    catch {
        return iso;
    }
}
const StarRating: React.FC<{
    rating: number;
    size?: number;
}> = ({ rating, size = 13 }) => (<span className="mt-stars" style={{ fontSize: size }}>
    {[1, 2, 3, 4, 5].map(n => (<span key={n} style={{ color: n <= Math.round(rating) ? '#f59e0b' : 'rgba(255,255,255,0.15)' }}>★</span>))}
  </span>);
/** Publishing agency's name, with a "Your agency" tag when it's the viewer's own. */
const PublisherLabel: React.FC<{
    name: string;
    isOwnAgency?: boolean;
    className?: string;
    prefix?: string;
}> = ({ name, isOwnAgency, className, prefix }) => (<div className={`mt-publisher ${className ?? ''}`}>
    <span className="mt-publisher__name" title={name}>{prefix}{name}</span>
    {isOwnAgency && (<span className="mt-publisher-tag">Your agency</span>)}
  </div>);
const InstalledToolCard: React.FC<{
    tool: InstalledTool;
    onClick: () => void;
}> = ({ tool, onClick }) => {
    const typeInfo = TOOL_TYPES.find(t => t.type === tool.type)!;
    const navigate = useNavigate();
    return (<div className="mt-inst-card" onClick={onClick} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && onClick()}>
      <div className="mt-inst-card__top-row">
        <ListingIcon iconUrl={tool.iconUrl} emoji={tool.logo} className="mt-inst-card__logo" alt={`${tool.name} icon`}/>
        <div className="mt-inst-card__top-badges">
          {tool.isNew && <span className="mt-inst-badge mt-inst-badge--new">NEW</span>}
          <span className="mt-inst-badge mt-inst-badge--installed">Installed</span>
        </div>
      </div>
      {tool.needsUserCredentials && (<span style={{
                display: 'inline-block', marginTop: 6, fontSize: 12, fontWeight: 600,
                color: CREDENTIALS_BADGE.color, background: CREDENTIALS_BADGE.bg,
                border: `1px solid ${CREDENTIALS_BADGE.border}`, borderRadius: 6, padding: '2px 8px',
            }}>
          {CREDENTIALS_BADGE.label}
        </span>)}
      <div className="mt-inst-card__name">{tool.name}</div>
      <PublisherLabel name={tool.publisherName} isOwnAgency={tool.isOwnAgency} className="mt-inst-card__publisher" prefix="By "/>
      <GroupChips groups={tool.groups} type={tool.type}/>
      {tool.rating > 0 && (<div className="mt-inst-card__rating-row">
          <StarRating rating={tool.rating}/>
          <span className="mt-inst-card__rating-val">{tool.rating}</span>
          <span className="mt-inst-card__review-count">({tool.reviewCount})</span>
        </div>)}
      <p className="mt-inst-card__desc">{tool.description}</p>
      <div className="mt-inst-card__footer">
        <span className={`mt-badge mt-badge--type mt-badge--type-${tool.type}`}>{typeInfo.label}</span>
        {tool.type === 'widget' && (<span className="mt-badge mt-badge--category">v{tool.version}</span>)}
        {tool.installs !== '—' && (<span className="mt-inst-card__installs">{tool.installs} installs</span>)}
        {tool.type === 'widget' && (<button type="button" className="mt-btn mt-btn--primary mt-btn--sm" style={{ marginLeft: 'auto' }} onClick={e => { e.stopPropagation(); navigate(`${ROUTES.WIDGETS}/${tool.id}`); }}>
            Open
          </button>)}
      </div>
    </div>);
};
const InstalledToolDetail: React.FC<{
    tool: InstalledTool;
    onBack: () => void;
    onUninstall: (id: string, forAgency: boolean) => void;
    uninstalling: boolean;
    /** Called after a successful save so the list this view was opened from holds the saved
     *  values — otherwise reopening the tool showed the old ones and the next save reverted them. */
    onSaved?: (patch: Partial<InstalledTool>) => void;
}> = ({ tool, onBack, onUninstall, uninstalling, onSaved }) => {
    const isAgencyOwner = readIsAgencyOwner();
    const [configValues, setConfigValues] = useState<Record<string, string>>(() => Object.fromEntries(tool.configFields.map(f => [f.key, f.value])));
    const [savingConfig, setSavingConfig] = useState(false);
    const [configSaveMsg, setConfigSaveMsg] = useState<string | null>(null);
    // 'agency' = removes for the whole company; 'personal' = just this user's own
    // row, leaving the agency-wide install (and everyone else's access) intact.
    const [confirmUninstall, setConfirmUninstall] = useState<'agency' | 'personal' | null>(null);
    // Widgets: granted permissions / surfaces / paused, stored with the install config.
    const [controls, setControls] = useState<WidgetInstallControls>(() => readInstallControls(tool.configValues));
    // Connection secrets to write on the next Save — never pre-filled with the current value.
    const [connectionValues, setConnectionValues] = useState<Record<string, string>>({});
    const navigate = useNavigate();
    const typeInfo = TOOL_TYPES.find(t => t.type === tool.type)!;
    // The store page Browse shows for this listing (store data loads after the first render).
    const source = tool.source;
    const [listing, setListing] = useState<StoreListing>(() => fallbackStoreListing(source));
    const [listingVersion, setListingVersion] = useState(0);
    useEffect(() => {
        let cancelled = false;
        getMarketplaceStore(tool.id).then(l => { if (!cancelled) setListing(l); }).catch(() => { });
        return () => { cancelled = true; };
    }, [tool.id, listingVersion]);
    const submitReview = async (rating: number, comment: string) => {
        try {
            await saveMarketplaceReview(tool.id, rating, comment);
            setListingVersion(v => v + 1);
        }
        catch (err: any) {
            alert(err?.message || 'Failed to save the review.');
        }
    };
    const isAgencyInstalled = tool.installSource === 'agency';
    const restrictToPerUser = isAgencyInstalled && !isAgencyOwner;
    const visibleConfigFields = restrictToPerUser
        ? tool.configFields.filter(f => f.scope === 'per_user')
        : tool.configFields;
    const handleSaveConfig = async () => {
        setSavingConfig(true);
        setConfigSaveMsg(null);
        try {
            if (tool.type === 'widget') {
                // Settings + per-install controls are written together to this user's own
                // installation row (agency installs provision one row per user).
                const merged = writeInstallControls(configValues, controls, tool.widgetManifest);
                await apiUpdateInstallationConfig(tool.installationId, merged, connectionValues);
                replaceMockInstallConfig(tool.id, merged);
                setConnectionValues({});
                // configFields too: the form is seeded from configFields[].value, so patching only
                // configValues showed the old value on reopen and the next save sent it back.
                onSaved?.({ configValues: merged, configFields: tool.configFields.map(f => ({ ...f, value: configValues[f.key] ?? f.value })) });
            }
            else if (restrictToPerUser) {
                const perUserValues = Object.fromEntries(visibleConfigFields.map(f => [f.key, configValues[f.key]]));
                await apiUpdateMyCredentials(tool.id, perUserValues);
            }
            else {
                await apiUpdateInstallationConfig(tool.installationId, configValues);
            }
            if (tool.type !== 'widget')
                onSaved?.({ configFields: tool.configFields.map(f => ({ ...f, value: configValues[f.key] ?? f.value })) });
            setConfigSaveMsg('Saved.');
        }
        catch (err: any) {
            // A failed save must not leave this tab showing permissions/pause/settings that were
            // never actually persisted — every other surface (the Widgets page, sidebar, tiles)
            // reads the server's last-saved value independently, so an un-reverted local edit here
            // would silently disagree with all of them. Revert to the same baseline this
            // component derived its initial state from.
            if (tool.type === 'widget') setControls(readInstallControls(tool.configValues));
            setConfigValues(Object.fromEntries(tool.configFields.map(f => [f.key, f.value])));
            setConfigSaveMsg((err.message || 'Failed to save configuration.') + ' Reverted to your last saved settings.');
        }
        finally {
            setSavingConfig(false);
            setTimeout(() => setConfigSaveMsg(null), 4000);
        }
    };
    const pausedNow = tool.type === 'widget' && readInstallControls(tool.configValues).paused;
    const hasSettings = tool.type === 'widget' || visibleConfigFields.length > 0;
    // This install's own controls, under the store page header.
    const manage = (<section className="ms-section mt-inst-manage">
        <h2 className="ms-section__title">Your install</h2>
        <div className="mt-detail-meta mt-inst-manage__meta">
          {/* Saved state (tool.configValues), not the unsaved checkbox in Widget controls. */}
          {pausedNow ? <span className="wg-chip wg-chip--paused">Paused</span> : <span className="mt-badge mt-badge--status">Active</span>}
          <span className="mt-detail-dot">·</span>
          <span className="mt-detail-dim">Installed {formatDate(tool.installedAt)}{isAgencyInstalled ? ' for your agency' : ''}</span>
          {tool.needsUserCredentials && (<>
              <span className="mt-detail-dot">·</span>
              <span style={{
                fontSize: 12, fontWeight: 600, color: CREDENTIALS_BADGE.color,
                background: CREDENTIALS_BADGE.bg, border: `1px solid ${CREDENTIALS_BADGE.border}`,
                borderRadius: 6, padding: '2px 8px',
            }}>
                {CREDENTIALS_BADGE.label}
              </span>
            </>)}
        </div>
        {(tool.type === 'skill' || tool.type === 'mcp_server') && (<p className="mt-step-subtitle" style={{ margin: '0 0 12px' }}>
            It's in your library in Digital Workforce. Turn it on for any agent from that agent's {tool.type === 'skill' ? 'Skills' : 'Tools'} tab.
          </p>)}
        {tool.type === 'integration' && (<>
            {tool.integrationKey && tool.apiIntegrationId && (<>
                <h4 className="mt-detail-section-title">API key</h4>
                <IntegrationKeyPanel integrationId={tool.apiIntegrationId}/>
              </>)}
            {tool.apiIntegrationId && (<>
                <h4 className="mt-detail-section-title">Turn on for your agents</h4>
                <p className="mt-step-subtitle" style={{ margin: '0 0 10px' }}>It shows up in a chat only for the agents it's on for.</p>
                <AgentEnablementPanel integrationId={tool.apiIntegrationId}/>
              </>)}
          </>)}
        {hasSettings && (<div className="mt-inst-manage__settings">
            {tool.type === 'widget' && (<>
                <h4 className="mt-detail-section-title">Widget controls</h4>
                <WidgetControlsPanel toolId={tool.id} installationId={tool.installationId} manifest={tool.widgetManifest} controls={controls} onChange={setControls} connectionValues={connectionValues} onConnectionValuesChange={setConnectionValues}/>
                {visibleConfigFields.length > 0 && <h4 className="mt-detail-section-title">Settings</h4>}
              </>)}
            {restrictToPerUser && tool.type !== 'widget' && (<p className="mt-config-hint">
                This tool was installed for your whole agency by an admin. Shared credentials
                are already set up — you only need to fill in your own fields below.
              </p>)}
            {visibleConfigFields.length > 0 ? (<>
                <p className="mt-config-hint">
                  {tool.type === 'mcp_server'
                  ? 'Leave a field blank to keep its current value — only fill in fields you want to change (e.g. after rotating a key).'
                  : 'Changes are saved automatically to your workspace.'}
                </p>
                <div className="mt-config-list">
                  {visibleConfigFields.map(field => (<div key={field.key} className="mt-config-field">
                      <label className="mt-config-field__label">
                        {field.label}{field.requiredFromInstaller ? ' *' : ''}
                      </label>
                      {field.hint && <p className="mt-field-hint">{field.hint}</p>}
                      <div className="mt-config-field__control">
                        {field.type === 'toggle' && (<label className="mt-toggle">
                            <input type="checkbox" checked={configValues[field.key] === 'true'} onChange={e => setConfigValues(v => ({ ...v, [field.key]: e.target.checked ? 'true' : 'false' }))}/>
                            <span className="mt-toggle__track"/>
                          </label>)}
                        {field.type === 'select' && (<select className="mt-select" style={{ width: 220 }} value={configValues[field.key]} onChange={e => setConfigValues(v => ({ ...v, [field.key]: e.target.value }))}>
                            {field.options?.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>)}
                        {field.type === 'text' && (<input type="text" className="mt-input" style={{ width: 220 }} value={configValues[field.key]} onChange={e => setConfigValues(v => ({ ...v, [field.key]: e.target.value }))}/>)}
                      </div>
                    </div>))}
                </div>
                {(tool.type === 'mcp_server' || tool.type === 'widget') && (<div className="mt-actions" style={{ marginTop: 12 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{configSaveMsg}</span>
                    <button type="button" className="mt-btn mt-btn--primary" onClick={handleSaveConfig} disabled={savingConfig}>
                      {savingConfig ? 'Saving…' : 'Save'}
                    </button>
                  </div>)}
              </>) : tool.type === 'widget' ? (<div className="mt-actions" style={{ marginTop: 12 }}>
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{configSaveMsg}</span>
                <button type="button" className="mt-btn mt-btn--primary" onClick={handleSaveConfig} disabled={savingConfig}>
                  {savingConfig ? 'Saving…' : 'Save'}
                </button>
              </div>) : (<p className="mt-table-empty">No configuration options available for this tool.</p>)}
          </div>)}
        {tool.type === 'widget' && (<>
            <h4 className="mt-detail-section-title">Running as installed</h4>
            {/* Trimmed permissions, stripped control keys, paused state - exactly what you run. */}
            <WidgetFrame toolId={tool.id} installationId={tool.installationId} manifest={applyInstallControls(tool.widgetManifest, controls)} grantedSandbox={sandboxFromManifest(applyInstallControls(tool.widgetManifest, controls))} config={stripControlKeys(tool.configValues)} paused={controls.paused} pausedHint="resume it under Widget controls above." height={360} title={`${tool.name} preview`}/>
            <h4 className="mt-detail-section-title">Version history</h4>
            <p className="mt-config-hint">Every bundle upload is a new revision. Permission changes between revisions are highlighted — a Global widget that changes drops back to My Agency until reviewers approve it again.</p>
            <WidgetVersionHistory toolId={tool.id}/>
          </>)}
      </section>);
    return (<motion.div className="mt-inst-detail-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} transition={{ duration: 0.2 }}>
      <button type="button" className="mt-inst-back-btn" onClick={onBack}>
        ← Back to Installed
      </button>
      <StorePage mode="view" name={tool.name} emoji={tool.logo} iconUrl={listing.iconUrl} typeKey={tool.type} typeLabel={typeInfo.label} category={tool.category} visibilityLabel={visLabel(source.visibility)} publisherName={tool.publisherName} isOwnAgency={tool.isOwnAgency} onPublisherClick={source.publisherName ? () => navigate(`${ROUTES.MY_TOOLS}?view=marketplace&publisher=${encodeURIComponent(source.publisherName!)}`) : undefined} groups={<GroupChips groups={tool.groups} type={tool.type}/>} updatedAt={source.updatedAt ?? source.createdAt} description={listing.storeDescription ?? source.description} listing={listing} includes={<ListingIncludes tool={source} listing={listing}/>} whereItGoes={whereItGoes({ type: tool.type, groups: tool.groups, surfaces: tool.widgetManifest?.surfaces })} canReview={!source.isMine} onSubmitReview={submitReview} manage={manage} actions={<div className="mt-inst-detail-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
            {tool.type === 'widget' && (<button type="button" className="mt-btn mt-btn--primary" onClick={() => navigate(`${ROUTES.WIDGETS}/${tool.id}`)}>
                Open widget
              </button>)}
            {isAgencyInstalled && isAgencyOwner && (<button type="button" className="mt-btn mt-btn--secondary" onClick={() => setConfirmUninstall('personal')} disabled={uninstalling} title="Remove this from just your own Installed list - everyone else in your agency keeps it.">
                {uninstalling ? 'Removing…' : 'Remove for me only'}
              </button>)}
            <button type="button" className="mt-btn mt-btn--secondary mt-btn--danger-outline" onClick={() => setConfirmUninstall(isAgencyInstalled && isAgencyOwner ? 'agency' : 'personal')} disabled={uninstalling || (isAgencyInstalled && !isAgencyOwner)} title={isAgencyInstalled && !isAgencyOwner
                ? 'This tool was installed for your whole agency — ask an Agency Admin to remove it.'
                : undefined}>
              {uninstalling
                ? 'Uninstalling…'
                : isAgencyInstalled && isAgencyOwner ? 'Uninstall for agency' : 'Uninstall'}
            </button>
          </div>}/>

      <AnimatePresence>
        {confirmUninstall && (<motion.div className="mt-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} onClick={e => { if (e.target === e.currentTarget)
            setConfirmUninstall(null); }}>
            <motion.div className="mt-modal" initial={{ opacity: 0, y: 12, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.97 }} transition={{ duration: 0.2 }}>
              <div className="mt-modal__header">
                <div className="mt-modal__header-info">
                  <div className="mt-modal__icon">⚠️</div>
                  <h3 className="mt-modal__title">
                    {confirmUninstall === 'agency'
                ? `Uninstall "${tool.name}" for your whole agency?`
                : `Remove "${tool.name}" from your Installed list?`}
                  </h3>
                </div>
                <button type="button" className="mt-modal__close" onClick={() => setConfirmUninstall(null)} aria-label="Close">×</button>
              </div>
              <p className="mt-step-subtitle">
                {confirmUninstall === 'agency'
                ? 'This removes it for every user in your agency, not just you, and detaches it from each of their agents. This can\'t be undone.'
                : isAgencyInstalled
                    ? 'This only removes it from your own Installed list and detaches it from your agent - the rest of your agency keeps it. This can\'t be undone.'
                    : 'This detaches it from your agent and removes your saved credentials. This can\'t be undone.'}
              </p>
              <div className="mt-actions">
                <button type="button" className="mt-btn mt-btn--secondary" onClick={() => setConfirmUninstall(null)}>Cancel</button>
                <button type="button" className="mt-btn mt-btn--secondary mt-btn--danger-outline" onClick={() => { onUninstall(tool.id, confirmUninstall === 'agency'); setConfirmUninstall(null); }}>
                  {confirmUninstall === 'agency' ? 'Uninstall for agency' : 'Remove for me only'}
                </button>
              </div>
            </motion.div>
          </motion.div>)}
      </AnimatePresence>
    </motion.div>);
};
const DEFAULT_STEPS = ['Type', 'Setup', 'Info', 'Store page'];
// An API Integration needs more than the other types - importing its docs, reviewing
// endpoints, connecting auth - so it adds its own steps around the shared Info and
// Review steps instead of leaving the wizard.
const INTEGRATION_STEPS = [
    { key: 'type', label: 'Type' },
    { key: 'import', label: 'Import' },
    { key: 'endpoints', label: 'Endpoints' },
    { key: 'info', label: 'Info' },
    { key: 'connect', label: 'Connect' },
    { key: 'review', label: 'Store page' },
] as const;
type IntegrationStepKey = typeof INTEGRATION_STEPS[number]['key'];
const INTEGRATION_STEP_LABELS = INTEGRATION_STEPS.map(st => st.label);
function integrationStepNumber(key: IntegrationStepKey): number {
    return INTEGRATION_STEPS.findIndex(st => st.key === key) + 1;
}
function asIntegrationStep(value: string | null): IntegrationStepKey {
    return (INTEGRATION_STEPS.find(st => st.key === value)?.key ?? 'import');
}
// ── Publish wizard place, in the URL ──
// Like the API Integration flow, the wizard's place lives in the URL so a refresh, Back /
// Forward or a pasted link lands on the same step:
//   ?view=create&type=<type>&step=setup|info|store[&edit=<toolId>][&agent=<agentId>]
// `edit` is the listing being edited; `agent` is an agent built inside the wizard (it already
// exists in Digital Workforce, so losing it would leave a stray agent). What's been typed in
// lives in sessionStorage (see WizardDraft) until the listing is saved.
const WIZARD_STEP_KEYS = ['type', 'setup', 'info', 'store'] as const;
function wizardStepFromParam(value: string | null): number {
    const index = WIZARD_STEP_KEYS.indexOf(value as typeof WIZARD_STEP_KEYS[number]);
    return index > 0 ? index + 1 : 1;
}
function wizardStepParam(step: number): string | null {
    return step > 1 ? WIZARD_STEP_KEYS[step - 1] ?? null : null;
}
/** Types the main wizard handles (API integrations have their own flow; workflows aren't publishable yet). */
function asWizardType(value: string | null): ToolType | null {
    return value && value !== 'integration' && value !== 'workflow' && TOOL_TYPES.some(t => t.type === value) ? value as ToolType : null;
}
/** The wizard's typed-in fields, kept per browser tab so a refresh doesn't lose them. Files
 *  (Knowledge Base, widget bundles) can't be kept; only their names, to ask for them again. */
interface WizardDraft {
    key: string;
    name: string;
    category: string;
    icon: string;
    description: string;
    skills: string;
    skillContent: string;
    visibility: Visibility;
    mcpUrl: string;
    mcpHeaders: McpHeaderField[];
    agentShareToken: string;
    agentId: string;
    agentMode: 'new' | 'existing';
    groups: string[];
    widgetSettings: WidgetSettingsState;
    storeDraft: StoreDraft;
    fileNames: string[];
}
const WIZARD_DRAFT_KEY = 'mt-wizard-draft';
const wizardDraftKey = (editId: string | null, type: ToolType) => (editId ? `edit:${editId}` : `new:${type}`);
function readWizardDraft(key: string): WizardDraft | null {
    try {
        const draft = JSON.parse(sessionStorage.getItem(WIZARD_DRAFT_KEY) || 'null') as WizardDraft | null;
        return draft?.key === key ? draft : null;
    }
    catch {
        return null;
    }
}
function writeWizardDraft(draft: WizardDraft) {
    try {
        sessionStorage.setItem(WIZARD_DRAFT_KEY, JSON.stringify(draft));
    }
    catch {
        // Over the storage quota: uploaded images are the big part, so keep everything else.
        try {
            sessionStorage.setItem(WIZARD_DRAFT_KEY, JSON.stringify({ ...draft, storeDraft: { ...draft.storeDraft, iconUrl: undefined, screenshots: [] } }));
        }
        catch {
            // Storage unavailable (private mode, blocked): the URL still keeps the step.
        }
    }
}
function clearWizardDraft() {
    try {
        sessionStorage.removeItem(WIZARD_DRAFT_KEY);
    }
    catch {
        // Nothing to clear.
    }
}
const StepIndicator: React.FC<{
    current: number;
    steps?: string[];
    /** Editing an existing tool: steps from `firstSelectable` on can be jumped to. */
    onSelect?: (stepNum: number) => void;
    firstSelectable?: number;
}> = ({ current, steps = DEFAULT_STEPS, onSelect, firstSelectable = 1 }) => {
    return (<div className={`mt-steps ${steps.length > 4 ? 'mt-steps--many' : ''}`}>
      {steps.map((label, idx) => {
            const stepNum = idx + 1;
            const isCompleted = stepNum < current;
            const isActive = stepNum === current;
            const selectable = !!onSelect && !isActive && stepNum >= firstSelectable;
            const Tag = selectable ? 'button' : 'div';
            return (<React.Fragment key={label}>
            <Tag className={`mt-step ${isActive ? 'mt-step--active' : ''} ${isCompleted ? 'mt-step--done' : ''} ${selectable ? 'mt-step--link' : ''}`} {...(selectable ? { type: 'button' as const, onClick: () => onSelect!(stepNum), 'aria-label': `Go to ${label}` } : {})}>
              <div className="mt-step__circle">{isCompleted ? '✓' : stepNum}</div>
              <span className="mt-step__label">{label}</span>
            </Tag>
            {idx < steps.length - 1 && (<div className={`mt-step-line ${isCompleted ? 'mt-step-line--done' : ''}`}/>)}
          </React.Fragment>);
        })}
    </div>);
};
const Step1Type: React.FC<{
    selected: ToolType | null;
    onSelect: (t: ToolType) => void;
    onNext: () => void;
}> = ({ selected, onSelect, onNext }) => (<motion.div key="step1" className="mt-step-content" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.22 }}>
    <h2 className="mt-step-title">Choose what to publish</h2>
    <p className="mt-step-subtitle">Select the type of tool you want to create.</p>
    <div className="mt-type-grid">
      {TOOL_TYPES.map(t => {
        const enabled = t.type === 'skill' || t.type === 'mcp_server' || t.type === 'agent' || t.type === 'integration' || t.type === 'widget';
        return (<button key={t.type} type="button" className={`mt-type-card ${selected === t.type ? 'mt-type-card--selected' : ''} ${!enabled ? 'mt-type-card--disabled' : ''}`} onClick={() => enabled && onSelect(t.type)} disabled={!enabled}>
            {!enabled && <span className="mt-type-card__coming-soon">Coming Soon</span>}
            <span className="mt-type-card__icon"><TypeGlyph type={t.type}/></span>
            <span className="mt-type-card__label">{t.label}</span>
            <span className="mt-type-card__desc">{t.desc}</span>
          </button>);
    })}
    </div>
    <div className="mt-actions">
      <span />
      <button type="button" className="mt-btn mt-btn--primary" disabled={!selected} onClick={onNext}>
        Next →
      </button>
    </div>
  </motion.div>);
const WIDGET_MAX_BYTES = 10 * 1024 * 1024;
const WIDGET_ALLOWED_EXTENSIONS = ['.zip', '.html', '.htm'];
function formatBytes(bytes: number): string {
    return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${(bytes / 1024).toFixed(1)} KB`;
}
const WIDGET_MAX_CONNECTIONS = 5;
const emptyWidgetConnection = (): WidgetConnection => ({
    key: '', label: '', baseUrl: '', headerName: 'Authorization', headerPrefix: 'Bearer ',
    scope: 'shared', required: true,
});
function connectionHasProblem(c: WidgetConnection, dupe: boolean): boolean {
    return dupe || (!!c.baseUrl.trim() && !/^https:\/\/[^?#\s]+$/.test(c.baseUrl.trim()));
}
function connectionSummary(c: WidgetConnection): string {
    const parts = [c.label.trim() || c.key.trim() || 'Untitled'];
    if (c.baseUrl.trim()) parts.push(c.baseUrl.trim());
    return parts.join(' — ');
}
// Declares the third-party APIs a widget's code calls via Momentum.connect(key, ...). A bare
// `Momentum.connect('stripe', ...)` in the code has no host/header info of its own — unlike
// permissions (scanned from what the code does) this has to come from a human, so it lives in
// this UI instead of requiring a hand-authored manifest.json. Sent alongside the next bundle
// upload; editing these without also choosing/re-choosing a bundle file has nothing to attach to.
const WidgetConnectionsEditor: React.FC<{
    connections: WidgetConnection[];
    setConnections: (c: WidgetConnection[]) => void;
}> = ({ connections, setConnections }) => {
    // Collapsed is opt-in per card (default: expanded, so a freshly-added connection shows every
    // field right away) rather than opt-in to expand — the earlier version hid header/scope
    // settings by default even for a brand new connection, which was the opposite of what's needed
    // while you're actually filling one in.
    const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
    const update = (idx: number, changes: Partial<WidgetConnection>) => setConnections(connections.map((c, i) => i === idx ? { ...c, ...changes } : c));
    const remove = (idx: number) => setConnections(connections.filter((_, i) => i !== idx));
    const add = () => setConnections([...connections, emptyWidgetConnection()]);
    const keyCounts = connections.reduce<Record<string, number>>((acc, c) => {
        const k = c.key.trim().toLowerCase();
        if (k) acc[k] = (acc[k] ?? 0) + 1;
        return acc;
    }, {});
    return (<div className="mt-field mt-field--full" style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label className="mt-label" style={{ margin: 0 }}>Third-party API connections (optional)</label>
        <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={add} disabled={connections.length >= WIDGET_MAX_CONNECTIONS}>
          + Add connection
        </button>
      </div>
      <p className="mt-step-subtitle" style={{ margin: '4px 0 8px' }}>
        For code that calls <code>Momentum.connect('key', ...)</code>. Momentum AMP attaches the installer's secret server-side — your code never sees it. Up to {WIDGET_MAX_CONNECTIONS}.
      </p>
      {connections.length === 0 && (<p className="mt-step-subtitle" style={{ margin: 0, fontStyle: 'italic' }}>No connections declared.</p>)}
      {connections.map((c, idx) => {
          const dupe = !!(c.key.trim() && (keyCounts[c.key.trim().toLowerCase()] ?? 0) > 1);
          // A problem always forces the card open — collapsing must never be able to hide an error.
          const isOpen = !collapsed[idx] || connectionHasProblem(c, dupe);
          return (<div key={idx} className="mt-header-row-group" style={{ marginBottom: 10, padding: '10px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isOpen ? 8 : 0 }}>
              <button type="button" onClick={() => setCollapsed({ ...collapsed, [idx]: !collapsed[idx] })} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: 0, flex: 1, minWidth: 0, textAlign: 'left',
                  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontFamily: 'inherit',
              }}>
                <span style={{ display: 'inline-block', transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>▸</span>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>
                  Connection {idx + 1}
                </span>
                {!isOpen && (<span style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {connectionSummary(c)}
                  </span>)}
              </button>
              <button type="button" className="mt-kb-file-item__remove" onClick={() => remove(idx)} aria-label="Remove connection">×</button>
            </div>

            {isOpen && (<>
                <label className="mt-label" style={{ fontSize: 11 }}>Key</label>
                <p className="mt-step-subtitle" style={{ margin: '0 0 4px' }}>Matches the key in <code>Momentum.connect('key', ...)</code>, e.g. <code>stripe</code>.</p>
                <input className="mt-input" type="text" placeholder="e.g. stripe" value={c.key} onChange={e => update(idx, { key: e.target.value })}/>
                {dupe && (<div className="mt-error-text" style={{ marginTop: 4 }}>This key is used by another connection above — keys must be unique.</div>)}

                <label className="mt-label" style={{ fontSize: 11, marginTop: 10 }}>Label</label>
                <p className="mt-step-subtitle" style={{ margin: '0 0 4px' }}>Shown to installers, e.g. "Stripe".</p>
                <input className="mt-input" type="text" placeholder="e.g. Stripe" value={c.label} onChange={e => update(idx, { label: e.target.value })}/>

                <label className="mt-label" style={{ fontSize: 11, marginTop: 10 }}>Base URL</label>
                <p className="mt-step-subtitle" style={{ margin: '0 0 4px' }}>The fixed host your code's calls resolve against.</p>
                <input className="mt-input" type="text" placeholder="https://api.stripe.com/v1" value={c.baseUrl} onChange={e => update(idx, { baseUrl: e.target.value })}/>
                {c.baseUrl.trim() && !/^https:\/\/[^?#\s]+$/.test(c.baseUrl.trim()) && (<div className="mt-error-text" style={{ marginTop: 4 }}>Must be an https:// URL with no query string or fragment.</div>)}

                <label className="mt-label" style={{ fontSize: 11, marginTop: 10 }}>Header name</label>
                <p className="mt-step-subtitle" style={{ margin: '0 0 4px' }}>Usually <code>Authorization</code>; use the API's own name if it needs one.</p>
                <input className="mt-input" type="text" placeholder="Authorization" value={c.headerName} onChange={e => update(idx, { headerName: e.target.value })}/>

                <label className="mt-label" style={{ fontSize: 11, marginTop: 10 }}>Value prefix</label>
                <p className="mt-step-subtitle" style={{ margin: '0 0 4px' }}>Not Bearer-only — <code>"Bearer "</code>, <code>"Basic "</code>, or empty for a raw key.</p>
                <input className="mt-input" type="text" placeholder='e.g. "Bearer " — clear for none' value={c.headerPrefix} onChange={e => update(idx, { headerPrefix: e.target.value })}/>

                <label className="mt-label" style={{ fontSize: 11, marginTop: 10 }}>Hint for installers</label>
                <input className="mt-input" type="text" placeholder='e.g. "Paste your Stripe secret key"' value={c.hint ?? ''} onChange={e => update(idx, { hint: e.target.value })}/>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                    <input type="checkbox" checked={c.required} onChange={e => update(idx, { required: e.target.checked })}/>
                    Widget can't be used until this is connected
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                    <input type="checkbox" checked={c.scope === 'per_user'} onChange={e => update(idx, { scope: e.target.checked ? 'per_user' : 'shared' })}/>
                    Each user supplies their own key (default: one shared key for the whole agency)
                  </label>
                </div>
              </>)}
          </div>);
      })}
    </div>);
};

interface WidgetSettingsState {
    version: string;
    surfaces: WidgetSurface[];
    minHeight: string;
    autoResize: boolean;
}
const defaultWidgetSettings = (): WidgetSettingsState => ({
    version: '', surfaces: ['page'], minHeight: '', autoResize: true,
});
/** True when the wizard's version/surfaces/sizing/connections differ from what the stored bundle
 *  carries. These only ship with an upload, so without a new file they would be silently dropped. */
function widgetSettingsChangedSinceUpload(manifest: WidgetManifest | null, storedVersion: string, settings: WidgetSettingsState, connections: WidgetConnection[]): boolean {
    if (!manifest)
        return false;
    const norm = (list: WidgetConnection[]) => JSON.stringify(list
        .filter(c => c.key.trim() && c.baseUrl.trim())
        .map(c => [c.key.trim(), (c.label ?? '').trim(), c.baseUrl.trim(), c.headerName ?? '', c.headerPrefix ?? '', c.scope ?? 'shared', !!c.required, (c.hint ?? '').trim()])
        .sort());
    const storedSurfaces = (manifest.surfaces?.length ? manifest.surfaces : ['page']).slice().sort().join(',');
    return (settings.version.trim() || '') !== (storedVersion || manifest.version || '')
        || settings.surfaces.slice().sort().join(',') !== storedSurfaces
        || settings.minHeight.trim() !== (manifest.minHeight != null ? String(manifest.minHeight) : '')
        || settings.autoResize !== (manifest.autoResize !== false)
        || norm(connections) !== norm(manifest.connections ?? []);
}
// Version, surfaces and display sizing — the rest of what manifest.json used to be hand-authored
// for, besides connections (its own editor) and permissions (always scanned, never declared). No
// separate README field — the tool's own Description above already covers that. Sent with the
// next bundle upload the same way; nothing here is read from a manifest.json anymore.
const WidgetSettingsFields: React.FC<{
    settings: WidgetSettingsState;
    setSettings: (s: WidgetSettingsState) => void;
}> = ({ settings, setSettings }) => {
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const patch = (changes: Partial<WidgetSettingsState>) => setSettings({ ...settings, ...changes });
    return (<div className="mt-field mt-field--full" style={{ marginTop: 4 }}>
      <label className="mt-label" style={{ margin: 0 }}>Widget details</label>

      <label className="mt-label" style={{ fontSize: 11, marginTop: 10 }}>Version</label>
      <input className="mt-input" type="text" placeholder="1.0.0" value={settings.version} onChange={e => patch({ version: e.target.value })}/>

      <button type="button" onClick={() => setAdvancedOpen(o => !o)} style={{
          display: 'flex', alignItems: 'center', gap: 5, marginTop: 10, padding: 0,
          background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)',
          fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
      }}>
        <span style={{ display: 'inline-block', transform: advancedOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▸</span>
        Advanced display settings
      </button>
      {advancedOpen && (<div style={{ marginTop: 8 }}>
          <label className="mt-label" style={{ fontSize: 11 }}>Minimum height (px)</label>
          <p className="mt-step-subtitle" style={{ margin: '0 0 4px' }}>Leave blank to let it size itself.</p>
          <input className="mt-input" type="number" placeholder="e.g. 200" value={settings.minHeight} onChange={e => patch({ minHeight: e.target.value })}/>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 10 }}>
            <input type="checkbox" checked={settings.autoResize} onChange={e => patch({ autoResize: e.target.checked })}/>
            Automatically resize to fit content (default on)
          </label>
        </div>)}
    </div>);
};

// Step 2 section for Widget tools: pick the code bundle (a .zip of plain code files, or a
// single .html — no manifest.json required; permissions are scanned from the code itself).
// A bare .html can be previewed locally in the same sandbox the widget will run in; a .zip
// is validated, scanned and flattened server-side on save.
const WidgetBundleField: React.FC<{
    widgetFile: File | null;
    setWidgetFile: (f: File | null) => void;
    widgetLocalHtml: string | null;
    setWidgetLocalHtml: (html: string | null) => void;
    existingWidgetManifest: WidgetManifest | null;
    existingWidgetVersion: string;
    widgetConnections: WidgetConnection[];
    setWidgetConnections: (c: WidgetConnection[]) => void;
    widgetSettings: WidgetSettingsState;
    setWidgetSettings: (s: WidgetSettingsState) => void;
}> = ({ widgetFile, setWidgetFile, widgetLocalHtml, setWidgetLocalHtml, existingWidgetManifest, existingWidgetVersion, widgetConnections, setWidgetConnections, widgetSettings, setWidgetSettings }) => {
    const [dragOver, setDragOver] = useState(false);
    const [error, setError] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    // Dry-run scan of the chosen file, shown before Save — same scanner a real upload runs,
    // nothing persisted. Catches a typo'd origin or an unexpected permission before spending a
    // real upload+review cycle on it.
    const [preview, setPreview] = useState<{ loading: boolean; permissions: string[]; warnings: string[]; html: string | null; error: string | null }>({ loading: false, permissions: [], warnings: [], html: null, error: null });
    useEffect(() => {
        if (!widgetFile) {
            setPreview({ loading: false, permissions: [], warnings: [], html: null, error: null });
            return;
        }
        const controller = new AbortController();
        setPreview({ loading: true, permissions: [], warnings: [], html: null, error: null });
        previewWidgetBundle(widgetFile, controller.signal)
            .then(result => setPreview({ loading: false, permissions: result.permissions, warnings: result.warnings, html: result.html, error: null }))
            .catch(err => {
                if (controller.signal.aborted)
                    return;
                setPreview({ loading: false, permissions: [], warnings: [], html: null, error: err instanceof Error ? err.message : 'Failed to scan file' });
            });
        return () => controller.abort();
    }, [widgetFile]);
    const acceptFile = (incoming: FileList | null) => {
        const file = incoming?.[0];
        if (!file)
            return;
        setError('');
        const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
        if (!WIDGET_ALLOWED_EXTENSIONS.includes(ext)) {
            setError('Only .zip bundles or a single .html file are supported.');
            return;
        }
        if (file.size > WIDGET_MAX_BYTES) {
            setError('Bundle must be 10 MB or smaller.');
            return;
        }
        setWidgetFile(file);
        if (ext === '.zip') {
            setWidgetLocalHtml(null);
            return;
        }
        const reader = new FileReader();
        reader.onload = () => setWidgetLocalHtml(typeof reader.result === 'string' ? reader.result : null);
        reader.onerror = () => setWidgetLocalHtml(null);
        reader.readAsText(file);
    };
    const clear = () => {
        setWidgetFile(null);
        setWidgetLocalHtml(null);
        setError('');
        if (inputRef.current)
            inputRef.current.value = '';
    };
    const isZip = !!widgetFile && widgetFile.name.toLowerCase().endsWith('.zip');
    return (<div className="mt-field mt-field--full">
      <label className="mt-label">Widget bundle <span className="mt-required">*</span></label>
      <p className="mt-step-subtitle" style={{ margin: '0 0 8px' }}>
        Upload a <code>.zip</code> (<code>index.html</code> + any local files) or a single <code>.html</code> — no <code>manifest.json</code> needed, Momentum AMP scans your code for what it needs. Runs sandboxed, with no access to the rest of the app.
        {' '}New to widgets? <a href={WIDGET_STARTER_URL} download={WIDGET_STARTER_FILENAME} style={{ color: '#d9a7ff' }}>Download the starter template</a>.
      </p>
      <WidgetDeveloperGuide/>
      {isWidgetsMockEnabled() && (<div className="mt-error-text" style={{ marginBottom: 8, color: '#fbbf24' }}>
          Dev placeholder active (REACT_APP_WIDGETS_MOCK): bundles are processed in this browser and kept in localStorage until the widget database migration is applied.
        </div>)}
      {!widgetFile && (<div className={`mt-kb-dropzone ${dragOver ? 'mt-kb-dropzone--over' : ''}`} onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={e => { e.preventDefault(); setDragOver(false); acceptFile(e.dataTransfer.files); }}>
          <span className="mt-kb-dropzone__icon">📦</span>
          <span className="mt-kb-dropzone__text">Drag & drop your bundle here</span>
          <span className="mt-kb-dropzone__or">or</span>
          <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => inputRef.current?.click()}>
            Browse Files
          </button>
          <input ref={inputRef} type="file" accept=".zip,.html,.htm" style={{ display: 'none' }} onChange={e => acceptFile(e.target.files)}/>
          <span className="mt-kb-dropzone__hint" style={{ fontSize: 11, opacity: 0.7 }}>ZIP or HTML (Max 10 MB)</span>
        </div>)}
      {error && (<div className="mt-error-text" style={{ marginTop: 6 }}>{error}</div>)}
      {widgetFile && (<div className="mt-kb-file-list">
          <div className="mt-kb-file-item">
            <span className="mt-kb-file-item__icon">{isZip ? '📦' : '🧩'}</span>
            <span className="mt-kb-file-item__name">{widgetFile.name}</span>
            <span className="mt-kb-file-item__size">{formatBytes(widgetFile.size)}</span>
            <button type="button" className="mt-kb-file-item__remove" onClick={clear} aria-label={`Remove ${widgetFile.name}`}>×</button>
          </div>
        </div>)}
      {widgetFile && isZip && (<p className="mt-step-subtitle" style={{ margin: '8px 0 0' }}>
          The bundle is validated, scanned for what it needs to run, and flattened when you save.
        </p>)}
      {widgetFile && preview.html && (<div className="wg-bundle-preview">
          <span className="wg-bundle-preview__label">Preview (sandboxed, built exactly as it will run — no permissions until installed)</span>
          <WidgetFrame html={preview.html} manifest={null} height={320} title="Widget preview" autoResize={false} ephemeralStorage/>
        </div>)}
      {widgetFile && (<div className="wg-bundle-meta" style={{ marginTop: 8 }}>
          {preview.loading && (<span className="mt-step-subtitle" style={{ margin: 0 }}>Scanning your code for what it needs…</span>)}
          {preview.error && (<span className="mt-error-text" style={{ display: 'block', whiteSpace: 'pre-line' }}>{preview.error}</span>)}
          {!preview.loading && !preview.error && (<>
              <span className="mt-step-subtitle" style={{ margin: '0 0 6px', display: 'block' }}>This will request:</span>
              <WidgetPermissionList manifest={{ name: '', version: '', entry: '', permissions: preview.permissions as WidgetPermission[] }} variant="chips"/>
              {preview.warnings.length > 0 && (<ul className="mt-step-subtitle" style={{ margin: '6px 0 0', color: '#fbbf24', paddingLeft: 18 }}>
                  {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>)}
            </>)}
        </div>)}
      {!widgetFile && existingWidgetManifest && (<div className="wg-bundle-meta">
          <span className="mt-review-skill-chip">🧩 {existingWidgetManifest.name}@{existingWidgetVersion || existingWidgetManifest.version}</span>
          {(existingWidgetManifest.permissions ?? []).map(p => (<span key={p} className="mt-review-skill-chip">{p}</span>))}
          <span className="mt-step-subtitle" style={{ margin: 0, alignSelf: 'center' }}>Current bundle kept — upload a new file to replace it.</span>
        </div>)}
      <WidgetSettingsFields settings={widgetSettings} setSettings={setWidgetSettings}/>
      <WidgetConnectionsEditor connections={widgetConnections} setConnections={setWidgetConnections}/>
      {!widgetFile && widgetSettingsChangedSinceUpload(existingWidgetManifest, existingWidgetVersion, widgetSettings, widgetConnections) && (<p className="mt-step-subtitle" style={{ margin: '-4px 0 0', color: '#fbbf24' }}>
          You changed the sections above. They're saved together with a bundle upload — choose a file above (even the same one again) to apply them, or they won't be saved.
        </p>)}
    </div>);
};
const Step2Info: React.FC<{
    selectedType: ToolType;
    name: string;
    setName: (v: string) => void;
    category: string;
    setCategory: (v: string) => void;
    icon: string;
    setIcon: (v: string) => void;
    description: string;
    setDescription: (v: string) => void;
    skills: string;
    setSkills: (v: string) => void;
    skillContent: string;
    setSkillContent: (v: string) => void;
    kbFiles: File[];
    setKbFiles: (v: File[]) => void;
    editingId: string | null;
    existingSkillKbFiles: {
        id: string;
        filename: string;
    }[];
    setExistingSkillKbFiles: (v: {
        id: string;
        filename: string;
    }[]) => void;
    visibility: Visibility;
    setVisibility: (v: Visibility) => void;
    mcpUrl: string;
    setMcpUrl: (v: string) => void;
    mcpHeaders: McpHeaderField[];
    setMcpHeaders: (v: McpHeaderField[]) => void;
    agentId: string;
    setAgentId: (v: string) => void;
    agentShareToken: string;
    setAgentShareToken: (v: string) => void;
    agentPreview: AgentShareSnapshot | null;
    setAgentPreview: (v: AgentShareSnapshot | null) => void;
    agentFrozenSnapshot: AgentShareSnapshot | null;
    setAgentFrozenSnapshot: (v: AgentShareSnapshot | null) => void;
    widgetFile: File | null;
    setWidgetFile: (f: File | null) => void;
    widgetLocalHtml: string | null;
    setWidgetLocalHtml: (html: string | null) => void;
    existingWidgetManifest: WidgetManifest | null;
    existingWidgetVersion: string;
    widgetConnections: WidgetConnection[];
    setWidgetConnections: (c: WidgetConnection[]) => void;
    widgetSettings: WidgetSettingsState;
    setWidgetSettings: (s: WidgetSettingsState) => void;
    groups: string[];
    setGroups: (g: string[]) => void;
    onBack: () => void;
    onNext: () => void;
    /** Which part of the form to show: 'setup' is what the tool is, 'info' is how it's
     *  listed. The API Integration flow still uses the combined 'all' form. */
    section?: 'all' | 'setup' | 'info';
    iconUrl?: string;
    onIconFile?: (file: File) => void;
    onClearIcon?: () => void;
    iconError?: string | null;
    /** Agent Setup: build a new agent here ('new') or wrap an existing one ('existing'),
     *  and the agent built here, if any. Kept by the wizard so Back returns to them. */
    agentMode?: 'new' | 'existing';
    setAgentMode?: (mode: 'new' | 'existing') => void;
    builtAgentId?: string;
    setBuiltAgentId?: (id: string) => void;
}> = ({ selectedType, name, setName, category, setCategory, icon, setIcon, description, setDescription, skills, setSkills, skillContent, setSkillContent, kbFiles, setKbFiles, editingId, existingSkillKbFiles, setExistingSkillKbFiles, visibility, setVisibility, mcpUrl, setMcpUrl, mcpHeaders, setMcpHeaders, agentId, setAgentId, agentShareToken, setAgentShareToken, agentPreview, setAgentPreview, agentFrozenSnapshot, setAgentFrozenSnapshot, widgetFile, setWidgetFile, widgetLocalHtml, setWidgetLocalHtml, existingWidgetManifest, existingWidgetVersion, widgetConnections, setWidgetConnections, widgetSettings, setWidgetSettings, groups, setGroups, onBack, onNext, section = 'all', iconUrl, onIconFile, onClearIcon, iconError, agentMode = 'existing', setAgentMode = () => { }, builtAgentId = '', setBuiltAgentId = () => { }, }) => {
    const [dwAgents, setDwAgents] = useState<DwAgentRecord[]>([]);
    const [dwAgentsLoading, setDwAgentsLoading] = useState(false);
    const [dwAgentsError, setDwAgentsError] = useState('');
    const [agentPreviewLoading, setAgentPreviewLoading] = useState(false);
    const [agentPreviewError, setAgentPreviewError] = useState('');
    const [agentUpdating, setAgentUpdating] = useState(false);
    const [builtAgentSyncing, setBuiltAgentSyncing] = useState(false);
    const [builtAgentError, setBuiltAgentError] = useState('');
    // Agents come from Digital Workforce, which has to be set up for this person first. Setup
    // normally ran at sign-in; this joins that run, or runs it, the same way the Digital
    // Workforce page does. A failure is shown - an empty list used to look like "no agents".
    const loadDwAgents = useCallback(() => {
        setDwAgentsLoading(true);
        setDwAgentsError('');
        ensureDwConfigured()
            .then(() => listDwAgents(false, true))
            .then(list => {
            const own = list.filter(a => !a.is_system);
            setDwAgents(own);
            // Nothing to pick from yet: start on the editor.
            if (own.length === 0 && !editingId && !builtAgentId)
                setAgentMode('new');
            return own;
        })
            .catch((err: unknown) => {
                setDwAgents([]);
                setDwAgentsError(`Couldn't load your agents.${err instanceof Error && err.message ? ` ${err.message}` : ''}`);
            })
            .finally(() => setDwAgentsLoading(false));
    }, [editingId, builtAgentId, setAgentMode]);
    useEffect(() => {
        if (selectedType === 'agent' && section !== 'info')
            loadDwAgents();
    }, [selectedType, section, loadDwAgents]);
    // The editor made the agent in Digital Workforce. Keep the list current, so the agent
    // shows under "Use one of my agents" and its name counts as taken.
    const handleAgentCreated = (newAgentId: string) => {
        setBuiltAgentId(newAgentId);
        listDwAgents(false, true)
            .then(list => setDwAgents(list.filter(a => !a.is_system)))
            .catch(() => { });
    };
    const mintAgentShare = async (id: string, snap: AgentShareSnapshot | null) => {
        const share = await shareAgent(id, 'anyone');
        setAgentShareToken(share.token);
        setAgentFrozenSnapshot(snap);
    };
    useEffect(() => {
        // Setup only: the Info step mounts this form too, and shouldn't mint another share.
        if (section === 'info')
            return;
        if (selectedType !== 'agent' || !agentId) {
            setAgentPreview(null);
            return;
        }
        let cancelled = false;
        setAgentPreviewLoading(true);
        setAgentPreviewError('');
        previewAgentShare(agentId)
            .then(async ({ snapshot }) => {
            if (cancelled)
                return;
            setAgentPreview(snapshot);
            setName(snapshot.name || '');
            setDescription(snapshot.description || '');
            // An agent built here is still being equipped; it's shared when Setup is done.
            if (!editingId && agentMode !== 'new') {
                setAgentUpdating(true);
                try {
                    await mintAgentShare(agentId, snapshot);
                }
                catch (err: any) {
                    if (!cancelled)
                        setAgentPreviewError(err.message || 'Failed to sync this agent');
                }
                finally {
                    if (!cancelled)
                        setAgentUpdating(false);
                }
            }
        })
            .catch((err: any) => { if (!cancelled) {
            setAgentPreview(null);
            setAgentPreviewError(err.message || 'Failed to load this agent');
        } })
            .finally(() => { if (!cancelled)
            setAgentPreviewLoading(false); });
        return () => { cancelled = true; };
    }, [selectedType, agentId, editingId, agentMode, section]);
    const snapshotsMatch = (a: AgentShareSnapshot | null, b: AgentShareSnapshot | null) => {
        if (!a || !b)
            return false;
        return JSON.stringify({ system: a.system, model: a.model, description: a.description, skills: a.skills, mcp_servers: a.mcp_servers, integrations: a.integrations ?? [], user_rules: a.user_rules })
            === JSON.stringify({ system: b.system, model: b.model, description: b.description, skills: b.skills, mcp_servers: b.mcp_servers, integrations: b.integrations ?? [], user_rules: b.user_rules });
    };
    const upToDate = !!agentShareToken && snapshotsMatch(agentPreview, agentFrozenSnapshot);
    const refreshAgentPreview = useCallback(() => {
        if (!agentId)
            return;
        previewAgentShare(agentId)
            .then(({ snapshot }) => setAgentPreview(snapshot))
            .catch(() => { });
    }, [agentId, setAgentPreview]);
    const handleUpdateAgentShare = async () => {
        if (!agentId)
            return;
        setAgentUpdating(true);
        try {
            const { snapshot } = await previewAgentShare(agentId);
            setAgentPreview(snapshot);
            await mintAgentShare(agentId, snapshot);
        }
        catch (err: any) {
            alert(err.message || 'Failed to sync this agent');
        }
        finally {
            setAgentUpdating(false);
        }
    };
    const [skillPreviewMode, setSkillPreviewMode] = useState<'edit' | 'preview'>('edit');
    const skillTextEditorRef = useRef<HTMLTextAreaElement>(null);
    const insertSkillMd = useCallback((before: string, after: string, linePrefix = '') => {
        const el = skillTextEditorRef.current;
        if (!el)
            return;
        const { selectionStart: s, selectionEnd: e, value } = el;
        const selected = value.slice(s, e);
        const replacement = linePrefix
            ? `${linePrefix}${selected || 'text'}`
            : `${before}${selected || 'text'}${after}`;
        setSkillContent(value.slice(0, s) + replacement + value.slice(e));
        setTimeout(() => {
            const newPos = s + replacement.length;
            el.setSelectionRange(newPos, newPos);
            el.focus();
        }, 0);
    }, [setSkillContent]);
    const [deletingKbFileId, setDeletingKbFileId] = useState<string | null>(null);
    const removeExistingKbFile = async (fileId: string) => {
        if (!editingId)
            return;
        setDeletingKbFileId(fileId);
        try {
            await apiDeleteSkillKbFile(editingId, fileId);
            setExistingSkillKbFiles(existingSkillKbFiles.filter(f => f.id !== fileId));
        }
        catch (err: any) {
            alert(err.message || 'Failed to remove Knowledge Base file');
        }
        finally {
            setDeletingKbFileId(null);
        }
    };
    const [kbDragOver, setKbDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const iconInputRef = useRef<HTMLInputElement>(null);
    const typeInfo = TOOL_TYPES.find(t => t.type === selectedType)!;
    const displayIcon = icon.trim() || <TypeGlyph type={selectedType}/>;
    // Mirror what the upsert endpoint actually requires, so the wizard cannot
    // reach Review and Save in a state the backend will reject: a name always,
    // skill content for a skill, an MCP URL for an MCP server, a resolved share
    // token for an agent. Previously only the name and the agent token were
    // checked, so a skill with an empty body sailed through to Save and came
    // back as a bare 400.
    const setupHint = selectedType === 'skill' && !skillContent.trim()
        ? 'Add the skill content to continue.'
        : selectedType === 'mcp_server' && !mcpUrl.trim()
            ? 'Add the MCP server URL to continue.'
            : selectedType === 'agent' && agentMode === 'new' && !editingId
                ? (builtAgentId ? '' : 'Create the agent to continue.')
                : selectedType === 'agent' && !agentShareToken
                ? 'Select an agent to continue.'
                : selectedType === 'widget' && !widgetFile && !existingWidgetManifest
                    ? 'Upload a .zip or .html bundle to continue.'
                    : '';
    const infoHint = !name.trim()
        ? 'Give the tool a name to continue.'
        : validateGroups(selectedType, groups, selectedType === 'widget' ? widgetSettings.surfaces : undefined);
    const missingFieldHint = section === 'setup' ? setupHint : section === 'info' ? infoHint : (infoHint || setupHint);
    const canContinue = !missingFieldHint && !builtAgentSyncing;
    // Leaving Setup with an agent built here: share it now, with everything attached to it
    // in the editor, the same way picking an existing agent does.
    const handleNext = async () => {
        if (section !== 'info' && selectedType === 'agent' && agentMode === 'new' && !editingId && builtAgentId) {
            setBuiltAgentSyncing(true);
            setBuiltAgentError('');
            try {
                const { snapshot } = await previewAgentShare(builtAgentId);
                setAgentPreview(snapshot);
                setName(snapshot.name || '');
                setDescription(snapshot.description || '');
                await mintAgentShare(builtAgentId, snapshot);
                setAgentId(builtAgentId);
            }
            catch (err: unknown) {
                setBuiltAgentError(err instanceof Error && err.message ? err.message : "Couldn't get the agent ready to publish. Please try again.");
                return;
            }
            finally {
                setBuiltAgentSyncing(false);
            }
        }
        onNext();
    };
    const KB_ALLOWED_EXTENSIONS = ['.pdf', '.txt'];
    const KB_MAX_BYTES = 10 * 1024 * 1024;
    const [kbError, setKbError] = useState('');
    const addFiles = (incoming: FileList | null) => {
        if (!incoming)
            return;
        setKbError('');
        const accepted: File[] = [];
        for (const f of Array.from(incoming)) {
            if (kbFiles.some(ex => ex.name === f.name))
                continue;
            const ext = f.name.slice(f.name.lastIndexOf('.')).toLowerCase();
            if (!KB_ALLOWED_EXTENSIONS.includes(ext)) {
                setKbError(`"${f.name}" isn't a supported type — only PDF and TXT files are accepted.`);
                continue;
            }
            if (f.size > KB_MAX_BYTES) {
                setKbError(`"${f.name}" is larger than the 10MB limit.`);
                continue;
            }
            accepted.push(f);
        }
        if (accepted.length)
            setKbFiles([...kbFiles, ...accepted]);
    };
    const addHeaderRow = () => setMcpHeaders([...mcpHeaders, { key: '', value: '', requiredFromInstaller: false }]);
    const updateHeaderRow = (idx: number, changes: Partial<McpHeaderField>) => setMcpHeaders(mcpHeaders.map((h, i) => i === idx ? { ...h, ...changes } : h));
    const removeHeaderRow = (idx: number) => setMcpHeaders(mcpHeaders.filter((_, i) => i !== idx));
    const renderScopeToggle = (idx: number, currentScope?: FieldScope) => (<div className="mt-visibility-group" style={{ marginTop: 6 }}>
      {([
            ['shared', 'One value for the whole agency'],
            ['per_user', 'Each person enters their own'],
        ] as const).map(([val, label]) => (<button key={val} type="button" className={`mt-visibility-btn ${(currentScope ?? 'shared') === val ? 'mt-visibility-btn--active' : ''}`} onClick={() => updateHeaderRow(idx, { scope: val })}>
          {label}
        </button>))}
    </div>);
    const isApiKeyShape = (headers: McpHeaderField[]) => headers.length === 1 && headers[0].key === 'Authorization' && headers[0].requiredFromInstaller;
    const [authMode, setAuthMode] = useState<'none' | 'apiKey' | 'advanced'>(() => {
        if (mcpHeaders.length === 0)
            return 'none';
        return isApiKeyShape(mcpHeaders) ? 'apiKey' : 'advanced';
    });
    const selectAuthMode = (mode: 'none' | 'apiKey' | 'advanced') => {
        setAuthMode(mode);
        if (mode === 'none') {
            setMcpHeaders([]);
        }
        else if (mode === 'apiKey' && !isApiKeyShape(mcpHeaders)) {
            setMcpHeaders([{ key: 'Authorization', value: '', requiredFromInstaller: true, label: 'API Key', hint: '' }]);
        }
        else if (mode === 'advanced' && mcpHeaders.length === 0) {
            setMcpHeaders([{ key: '', value: '', requiredFromInstaller: false }]);
        }
    };
    return (<motion.div key="step2" className="mt-step-content" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.22 }}>
      <h2 className="mt-step-title">{section === 'setup' ? `Set up your ${typeInSentence(typeInfo.label)}` : section === 'info' ? 'Listing info' : 'Tool details'}</h2>
      <p className="mt-step-subtitle">
        {section === 'setup'
            ? `What your ${typeInSentence(typeInfo.label)} is and how it works. You'll describe it for the Marketplace in the next steps.`
            : section === 'info'
                ? 'How your listing appears in the Marketplace: its name, category, icon and who can see it.'
                : `Describe your ${typeInfo.label.toLowerCase()} so others know what it does.`}
      </p>

      <div className="mt-form">
        {section !== 'info' && selectedType === 'agent' && (<>
            <div className="mt-field mt-field--full">
              <label className="mt-label">Digital Workforce agent <span className="mt-required">*</span></label>
              {!editingId && (<p className="mt-step-subtitle" style={{ margin: '0 0 8px' }}>
                  Build a new agent here, or use one you already have. Either way it lives in your Digital Workforce, and the listing ships its agents.md, skills, MCP servers and API integrations.
                </p>)}
              {!editingId && (<div className="ms-agent-mode" role="group" aria-label="Agent source">
                  <button type="button" aria-pressed={agentMode === 'new'} onClick={() => setAgentMode('new')}>Build a new agent</button>
                  <button type="button" aria-pressed={agentMode === 'existing'} onClick={() => setAgentMode('existing')}>Use one of my agents</button>
                </div>)}
              {editingId ? (<>
                  <div className="ms-agent-state">
                    <div>
                      <strong>This listing ships a copy of {agentPreview?.name || name || 'its agent'}.</strong>
                      <span>{upToDate ? 'The listing matches the agent.' : "You've changed the agent since the listing was last updated. Pull the changes in so installers get them."}</span>
                    </div>
                    <button type="button" className={`mt-btn ${upToDate ? 'mt-btn--secondary' : 'mt-btn--primary'} mt-btn--sm`} disabled={!agentId || agentPreviewLoading || agentUpdating || upToDate} onClick={handleUpdateAgentShare}>
                      {agentUpdating ? 'Pulling in…' : upToDate ? 'Up to date' : 'Pull in latest'}
                    </button>
                  </div>
                  {agentId ? (<NewAgentEditor agentId={agentId} takenNames={dwAgents.map(a => a.name)} onCreated={() => { }} onActivity={refreshAgentPreview}/>)
                : agentFrozenSnapshot ? (<div className="ms-agent-state ms-agent-state--error"><span>This listing's agent is no longer in your Digital Workforce, so it can't be edited here.</span></div>)
                    : (<p style={{ color: 'var(--text-tertiary)', fontSize: 12.5 }}>Loading agent…</p>)}
                  {agentPreviewError && <p className="mt-error-text" style={{ marginTop: 6 }}>{agentPreviewError}</p>}
                </>) : agentMode === 'new' ? (<>
                  <NewAgentEditor agentId={builtAgentId} takenNames={dwAgents.map(a => a.name)} onCreated={handleAgentCreated}/>
                  {builtAgentError && <p className="mt-error-text">{builtAgentError}</p>}
                </>) : (<>
              {dwAgentsError && (<div className="ms-agent-state ms-agent-state--error">
                  <span>{dwAgentsError}</span>
                  <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={loadDwAgents}>Try again</button>
                </div>)}
              {!dwAgentsLoading && !dwAgentsError && dwAgents.length === 0 && (<div className="ms-agent-state">
                  <div>
                    <strong>You don't have any agents of your own yet.</strong>
                    <span>The built-in agents can't be listed. Build one to publish it here.</span>
                  </div>
                  <button type="button" className="mt-btn mt-btn--primary mt-btn--sm" onClick={() => setAgentMode('new')}>Build a new agent</button>
                </div>)}
              <div className="mt-field-row" style={{ alignItems: 'flex-start' }}>
                {dwAgentsLoading ? (<p style={{ color: 'var(--text-tertiary)', fontSize: 12.5 }}>Getting your agents ready…</p>) : !dwAgentsError && dwAgents.length > 0 ? (<select className="mt-select" style={{ flex: 1 }} value={agentId} onChange={e => setAgentId(e.target.value)}>
                    <option value="">Select an agent…</option>
                    {dwAgents.map(a => <option key={a.agent_id} value={a.agent_id}>{a.name}</option>)}
                  </select>) : null}
                {agentUpdating ? (<span style={{ fontSize: 12.5, color: 'var(--text-tertiary)', alignSelf: 'center' }}>Syncing…</span>) : null}
              </div>
              {agentPreviewError && <p className="mt-error-text" style={{ marginTop: 6 }}>{agentPreviewError}</p>}
              </>)}
            </div>

            {agentId && agentMode === 'existing' && !editingId && (<div className="mt-field mt-field--full">
                <label className="mt-label">Preview</label>
                {agentPreviewLoading ? (<p style={{ color: 'var(--text-tertiary)', fontSize: 12.5 }}>Loading agent…</p>) : agentPreview ? (<AgentDraftPreview system={agentPreview.system ?? ''} skills={agentPreview.skills} mcpServers={agentPreview.mcp_servers} integrations={agentPreview.integrations} userRules={agentPreview.user_rules}/>) : null}
              </div>)}
          </>)}

        {section !== 'setup' && (<>
        <div className="mt-field-row">
          <div className="mt-field mt-field--grow">
            <label className="mt-label">Name <span className="mt-required">*</span></label>
            <input className="mt-input" type="text" placeholder={`My ${typeInfo.label}`} value={name} onChange={e => setName(e.target.value)} disabled={selectedType === 'agent'}/>
            {selectedType === 'agent' && (<p className="mt-step-subtitle" style={{ margin: '4px 0 0' }}>Matches the selected agent's name.</p>)}
          </div>
          <div className="mt-field">
            <label className="mt-label">Category</label>
            <select className="mt-select" value={category} onChange={e => setCategory(e.target.value)}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {section === 'info' ? (<>
        <div className="mt-field mt-field--full">
          <label className="mt-label">Icon</label>
          <p className="mt-step-subtitle" style={{ margin: '0 0 8px' }}>Your logo or product icon. Square images work best - anything else is cropped to fit.</p>
          <div className="ms-icon-upload">
            <ListingIcon iconUrl={iconUrl} emoji={displayIcon} className="ms-icon-upload__preview" alt="Icon preview"/>
            <div className="ms-icon-upload__actions">
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => iconInputRef.current?.click()}>{iconUrl ? 'Replace image' : 'Upload image'}</button>
                {iconUrl && (<button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={onClearIcon}>Remove</button>)}
              </div>
              <span className="mt-field-hint">PNG, JPG or WebP. Shown on your card, store page and install prompt.</span>
            </div>
            <input ref={iconInputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={e => { const file = e.target.files?.[0]; if (file && onIconFile) onIconFile(file); e.target.value = ''; }}/>
          </div>
          {iconError && (<p className="mt-error-text" style={{ marginTop: 6 }}>{iconError}</p>)}
        </div>
          </>) : (<>
        <div className="mt-field mt-field--full">
          <label className="mt-label">Icon</label>
          <div className="mt-icon-picker">
            {ICON_OPTIONS.map(emoji => (<button key={emoji} type="button" className={`mt-icon-option ${displayIcon === emoji ? 'mt-icon-option--selected' : ''}`} onClick={() => setIcon(emoji)} title={emoji}>
                {emoji}
              </button>))}
          </div>
        </div>

          </>)}
        </>)}

        {section === 'all' && (<>
        <div className="mt-field mt-field--full">
          <label className="mt-label">Description</label>
          <textarea className="mt-textarea" placeholder="What does this tool do? Who is it for?" rows={5} value={description} onChange={e => setDescription(e.target.value)} disabled={selectedType === 'agent'}/>
          {selectedType === 'agent' && (<p className="mt-step-subtitle" style={{ margin: '4px 0 0' }}>Matches the selected agent's description.</p>)}
        </div>
        </>)}

        {section !== 'info' && (<>
        {selectedType === 'mcp_server' ? (<>
            <div className="mt-field mt-field--full">
              <label className="mt-label">Server URL <span className="mt-required">*</span></label>
              <input className="mt-input" type="text" placeholder="https://example.com/mcp" value={mcpUrl} onChange={e => setMcpUrl(e.target.value)}/>
            </div>

            <div className="mt-field mt-field--full">
              <label className="mt-label">Authentication</label>
              <div className="mt-visibility-group">
                {([
                ['none', 'No key needed'],
                ['apiKey', 'Requires an API key'],
                ['advanced', 'Advanced'],
            ] as const).map(([val, label]) => (<button key={val} type="button" className={`mt-visibility-btn ${authMode === val ? 'mt-visibility-btn--active' : ''}`} onClick={() => selectAuthMode(val)}>
                    {label}
                  </button>))}
              </div>

              {authMode === 'apiKey' && (<div style={{ marginTop: 10 }}>
                  <p className="mt-step-subtitle" style={{ margin: '0 0 8px' }}>
                    {(mcpHeaders[0]?.scope ?? 'shared') === 'shared'
                    ? 'An Agency Admin enters this once when installing for the whole agency — everyone uses that same value automatically. A personal (non-agency) install still prompts the installer directly.'
                    : "Each person supplies their own — at install time for a personal install, or afterward for an agency install (until they do, they'll see a \"needs your credentials\" prompt)."}
                  </p>
                  {renderScopeToggle(0, mcpHeaders[0]?.scope)}
                  <label className="mt-label" style={{ marginTop: 8 }}>Field label</label>
                  <input className="mt-input" type="text" placeholder="API Key" value={mcpHeaders[0]?.label ?? ''} onChange={e => updateHeaderRow(0, { label: e.target.value })}/>
                  <label className="mt-label" style={{ marginTop: 8 }}>Instructions for installer</label>
                  <input className="mt-input" type="text" placeholder='e.g. "Find yours at firecrawl.dev/app/api-keys"' value={mcpHeaders[0]?.hint ?? ''} onChange={e => updateHeaderRow(0, { hint: e.target.value })}/>
                </div>)}

              {authMode === 'advanced' && (<div style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span className="mt-step-subtitle" style={{ margin: 0 }}>Custom HTTP headers</span>
                    <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={addHeaderRow}>
                      + Add header
                    </button>
                  </div>
                  <p className="mt-step-subtitle" style={{ margin: '4px 0 8px' }}>
                    Check "Installer supplies" for any header whose value should never be stored here.
                  </p>
                  {mcpHeaders.map((header, idx) => (<div key={idx} className="mt-header-row-group" style={{ marginBottom: 8 }}>
                      <div className="mt-field-row" style={{ alignItems: 'center' }}>
                        <input className="mt-input" type="text" placeholder="Header name" style={{ flex: 1 }} value={header.key} onChange={e => updateHeaderRow(idx, { key: e.target.value })}/>
                        <input className="mt-input" type="text" style={{ flex: 1 }} placeholder={header.requiredFromInstaller ? 'Supplied by installer' : 'Value'} value={header.value} disabled={header.requiredFromInstaller} onChange={e => updateHeaderRow(idx, { value: e.target.value })}/>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, whiteSpace: 'nowrap' }}>
                          <input type="checkbox" checked={header.requiredFromInstaller} onChange={e => updateHeaderRow(idx, { requiredFromInstaller: e.target.checked, value: e.target.checked ? '' : header.value })}/>
                          Installer supplies
                        </label>
                        <button type="button" className="mt-kb-file-item__remove" onClick={() => removeHeaderRow(idx)} aria-label="Remove header">×</button>
                      </div>
                      {header.requiredFromInstaller && (<>
                          {renderScopeToggle(idx, header.scope)}
                          <input className="mt-input" type="text" style={{ marginTop: 6 }} placeholder='Field label shown to the installer, e.g. "API Key"' value={header.label ?? ''} onChange={e => updateHeaderRow(idx, { label: e.target.value })}/>
                          <input className="mt-input" type="text" style={{ marginTop: 6 }} placeholder='Instructions for the installer, e.g. "Bearer <your API key> — find it under Settings → API Keys"' value={header.hint ?? ''} onChange={e => updateHeaderRow(idx, { hint: e.target.value })}/>
                        </>)}
                    </div>))}
                </div>)}
            </div>
          </>) : selectedType === 'skill' ? (<div className="mt-field mt-field--full">
            <label className="mt-label">Skill content <span className="mt-required">*</span></label>
            <p className="mt-step-subtitle" style={{ margin: '0 0 8px' }}>
              Written as a SKILL.md file — describe what the agent should do and how.
            </p>
            <SkillEditorBody mode={skillPreviewMode} onModeChange={setSkillPreviewMode} body={skillContent} onBodyChange={setSkillContent} textEditorRef={skillTextEditorRef} insertMd={insertSkillMd}/>
          </div>) : selectedType === 'agent' ? null : selectedType === 'widget' ? (<WidgetBundleField widgetFile={widgetFile} setWidgetFile={setWidgetFile} widgetLocalHtml={widgetLocalHtml} setWidgetLocalHtml={setWidgetLocalHtml} existingWidgetManifest={existingWidgetManifest} existingWidgetVersion={existingWidgetVersion} widgetConnections={widgetConnections} setWidgetConnections={setWidgetConnections} widgetSettings={widgetSettings} setWidgetSettings={setWidgetSettings}/>) : (<div className="mt-field mt-field--full">
            <label className="mt-label">{selectedType === 'integration' ? 'Capabilities' : 'Skills'}</label>
            <textarea className="mt-textarea" placeholder={selectedType === 'integration' ? 'What agents can do with this API, one per line…' : 'List the skills this tool uses or provides, one per line…'} rows={5} value={skills} onChange={e => setSkills(e.target.value)}/>
            {selectedType === 'integration' && (<p className="mt-field-hint">Shown to people browsing the Marketplace. Filled in from the endpoints to start with.</p>)}
          </div>)}

        {selectedType !== 'mcp_server' && selectedType !== 'agent' && selectedType !== 'integration' && selectedType !== 'widget' && (<div className="mt-field mt-field--full">
            <label className="mt-label">Knowledge Base</label>
            <div className={`mt-kb-dropzone ${kbDragOver ? 'mt-kb-dropzone--over' : ''}`} onDragOver={e => { e.preventDefault(); setKbDragOver(true); }} onDragLeave={() => setKbDragOver(false)} onDrop={e => { e.preventDefault(); setKbDragOver(false); addFiles(e.dataTransfer.files); }}>
              <span className="mt-kb-dropzone__icon">📂</span>
              <span className="mt-kb-dropzone__text">Drag & drop files here</span>
              <span className="mt-kb-dropzone__or">or</span>
              <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => fileInputRef.current?.click()}>
                Browse Files
              </button>
              <input ref={fileInputRef} type="file" multiple accept=".pdf,.txt" style={{ display: 'none' }} onChange={e => addFiles(e.target.files)}/>
              <span className="mt-kb-dropzone__hint" style={{ fontSize: 11, opacity: 0.7 }}>PDF, TXT (Max 10 MB)</span>
            </div>
            {kbError && (<div className="mt-error-text" style={{ marginTop: 6 }}>{kbError}</div>)}
            {selectedType === 'skill' && existingSkillKbFiles.length > 0 && (<div className="mt-kb-file-list">
                {existingSkillKbFiles.map(f => (<div key={f.id} className="mt-kb-file-item">
                    <span className="mt-kb-file-item__icon">📄</span>
                    <span className="mt-kb-file-item__name">{f.filename}</span>
                    <button type="button" className="mt-kb-file-item__remove" disabled={deletingKbFileId === f.id} onClick={() => removeExistingKbFile(f.id)} aria-label={`Remove ${f.filename}`}>
                      {deletingKbFileId === f.id ? '…' : '×'}
                    </button>
                  </div>))}
              </div>)}
            {kbFiles.length > 0 && (<div className="mt-kb-file-list">
                {kbFiles.map((f, idx) => (<div key={f.name + idx} className="mt-kb-file-item">
                    <span className="mt-kb-file-item__icon">📄</span>
                    <span className="mt-kb-file-item__name">{f.name}</span>
                    <span className="mt-kb-file-item__size">{(f.size / 1024).toFixed(1)} KB</span>
                    <button type="button" className="mt-kb-file-item__remove" onClick={() => setKbFiles(kbFiles.filter((_, i) => i !== idx))} aria-label={`Remove ${f.name}`}>×</button>
                  </div>))}
              </div>)}
          </div>)}

        </>)}

        {/* Only widgets have a choice: skills, agents and MCP servers always land in Digital Workforce. */}
        {section !== 'setup' && selectedType === 'widget' && (<div className="mt-field mt-field--full">
          <label className="mt-label">Where it's added</label>
          <p className="mt-step-subtitle" style={{ margin: '0 0 8px' }}>
            Where installers see it. Placement changes save with your next bundle upload.
          </p>
          <GroupPicker type={selectedType} groups={groups} setGroups={setGroups} widgetSurfaces={widgetSettings.surfaces} setWidgetSurfaces={surfaces => setWidgetSettings({ ...widgetSettings, surfaces: surfaces as WidgetSurface[] })}/>
        </div>)}

        {section !== 'setup' && (<div className="mt-field mt-field--full">
          <label className="mt-label">Visibility</label>
          <div className="mt-visibility-group">
            {([['just_me', '📝 Personal'], ['my_agency', '🏢 My Agency'], ['published', '🌐 Global']] as const)
                .map(([val, label]) => (<button key={val} type="button" className={`mt-visibility-btn ${visibility === val ? 'mt-visibility-btn--active' : ''}`} onClick={() => setVisibility(val)}>
                  {label}
                </button>))}
          </div>
          {selectedType === 'integration' && (<p className="mt-field-hint">
              Personal: just you. My Agency: colleagues can install it once an Agency Admin installs it for the agency.
              Global: any agency can install it once a platform reviewer approves it, and each one calls the API with its own key - never yours.
            </p>)}
          <div role="status">
            {visibility === 'published' && (<p className="mt-visibility-note">
                <span aria-hidden="true">⚠ </span>Global tools are visible to every agency on the platform, so this doesn't publish
                immediately -- saving submits it for a platform admin's review. It stays private to
                you until approved.
              </p>)}
          </div>
        </div>)}
      </div>

      <div className="mt-actions">
        <div className="mt-actions__left">
          <button type="button" className="mt-btn mt-btn--secondary" onClick={onBack}>← Back</button>
          {!canContinue && missingFieldHint && (<span className="mt-field-hint">{missingFieldHint}</span>)}
        </div>
        <button type="button" className="mt-btn mt-btn--primary" disabled={!canContinue} onClick={() => { void handleNext(); }}>
          {builtAgentSyncing ? 'Getting it ready…' : 'Next →'}
        </button>
      </div>
    </motion.div>);
};
// The publish wizard's last step: the listing's own store page (StorePage in edit
// mode) with open slots for the video, screenshots, description and release notes,
// so the publisher sees exactly what installers will. Replaces the old Review card.
const StorePageStep: React.FC<{
    selectedType: ToolType;
    name: string;
    category: string;
    icon: string;
    visibility: Visibility;
    description: string;
    setDescription: (v: string) => void;
    storeDraft: StoreDraft;
    setStoreDraft: React.Dispatch<React.SetStateAction<StoreDraft>>;
    agentPreview: AgentShareSnapshot | null;
    mcpUrl: string;
    mcpHeaders: McpHeaderField[];
    skillContent: string;
    kbFileNames: string[];
    widgetFile: File | null;
    widgetLocalHtml: string | null;
    widgetManifest: WidgetManifest | null;
    widgetSettings: WidgetSettingsState;
    groups: string[];
    isEditing: boolean;
    saving: boolean;
    onBack: () => void;
    onSave: () => void;
    /** API integrations: what it calls, shown as "What's included". */
    includesOverride?: React.ReactNode;
    /** API integrations: what the AI description draft is built from. */
    aiFacts?: string[];
    /** Notes, errors and warnings shown above the buttons. */
    footer?: React.ReactNode;
}> = ({ selectedType, name, category, icon, visibility, description, setDescription, storeDraft, setStoreDraft, agentPreview, mcpUrl, mcpHeaders, skillContent, kbFileNames, widgetFile, widgetLocalHtml, widgetManifest, widgetSettings, groups, isEditing, saving, onBack, onSave, includesOverride, aiFacts, footer }) => {
    const typeInfo = TOOL_TYPES.find(t => t.type === selectedType)!;
    const displayIcon = icon.trim() || <TypeGlyph type={selectedType}/>;
    const [mediaError, setMediaError] = useState<string | null>(null);
    const [drafting, setDrafting] = useState(false);
    // Agent listings keep their own store copy: the agent's description is the agent's
    // business, and Update would overwrite it from Digital Workforce anyway.
    const isAgent = selectedType === 'agent';
    const storeDescription = isAgent ? (storeDraft.storeDescription ?? description) : description;
    const setStoreDescription = (v: string) => isAgent ? setStoreDraft(d => ({ ...d, storeDescription: v })) : setDescription(v);
    const addScreenshots = async (files: FileList) => {
        setMediaError(null);
        const added: string[] = [];
        for (const file of Array.from(files)) {
            try {
                added.push(await screenshotFromFile(file));
            }
            catch (err) {
                setMediaError(`${file.name}: ${err instanceof Error ? err.message : "couldn't be added."}`);
            }
        }
        if (added.length)
            setStoreDraft(d => ({ ...d, screenshots: [...d.screenshots, ...added] }));
    };
    const moveScreenshot = (index: number, direction: -1 | 1) => setStoreDraft(d => {
        const next = [...d.screenshots];
        const target = index + direction;
        if (target < 0 || target >= next.length)
            return d;
        [next[index], next[target]] = [next[target], next[index]];
        return { ...d, screenshots: next };
    });
    // What the AI draft is built from: whatever the listing already contains.
    const facts = (): string[] => {
        if (aiFacts)
            return aiFacts;
        if (selectedType === 'agent' && agentPreview) {
            return [
                ...agentPreview.skills.map(sk => `Comes with the ${sk.name} skill`),
                ...agentPreview.mcp_servers.map(m => `Connected to ${m.name}`),
                ...(agentPreview.integrations ?? []).map(i => `Calls the ${i.name} API`),
            ];
        }
        if (selectedType === 'skill') {
            const summary = extractSkillDescription(skillContent);
            return [summary ?? '', ...kbFileNames.map(n => `Uses ${n} as reference`)];
        }
        if (selectedType === 'mcp_server') {
            try {
                return [`Connects your agents to ${new URL(mcpUrl).hostname}`];
            }
            catch {
                return [];
            }
        }
        if (selectedType === 'widget')
            return (widgetManifest?.surfaces ?? []).map(sf => `Shows up as a ${sf} widget`);
        return [];
    };
    const draftWithAi = async () => {
        if (storeDescription.trim() && !window.confirm('Replace the current store description with a new AI draft?'))
            return;
        setDrafting(true);
        try {
            setStoreDescription(await draftMarketplaceDescription({ name, type: selectedType, facts: facts() }));
        }
        catch (err: any) {
            alert(err?.message || 'Could not draft the listing description.');
        }
        finally {
            setDrafting(false);
        }
    };
    const includes = (() => {
        if (includesOverride)
            return includesOverride;
        if (selectedType === 'agent') {
            return agentPreview
                ? (<AgentComponents snapshot={agentPreview} instructions={<AgentDraftPreview system={agentPreview.system ?? ''} skills={agentPreview.skills} mcpServers={agentPreview.mcp_servers} integrations={agentPreview.integrations} userRules={agentPreview.user_rules} instructionsOnly/>}/>)
                : (<p className="ms-muted">Pick an agent in Setup to see what it includes.</p>);
        }
        if (selectedType === 'mcp_server') {
            return (<>
                <McpDetailBody url={mcpUrl} headers={mcpHeaders}/>
                <p className="ms-muted">The server's tools are read and listed here when you publish.</p>
              </>);
        }
        if (selectedType === 'widget') {
            return (<>
                {widgetManifest && !widgetFile
                    ? (<div><div className="mt-detail-field-label">It can</div><WidgetPermissionList manifest={widgetManifest} variant="chips"/></div>)
                    : (<p className="ms-muted">What your widget is allowed to access is detected from its code when you save.</p>)}
                {widgetLocalHtml && (<div className="wg-bundle-preview">
                    <span className="wg-bundle-preview__label">Sandboxed preview</span>
                    <WidgetFrame html={widgetLocalHtml} manifest={null} height={280} title="Widget preview" autoResize={false}/>
                  </div>)}
              </>);
        }
        if (selectedType === 'skill') {
            return (<>
                <p className="ms-muted">A skill your agents follow whenever the task comes up. Your instructions stay private - installers never see them.</p>
                {kbFileNames.length > 0 && (<div>
                    <div className="mt-detail-field-label">Knowledge Base</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {kbFileNames.map(n => (<span key={n} className="mt-review-skill-chip">📄 {n}</span>))}
                    </div>
                  </div>)}
              </>);
        }
        return (<p className="ms-muted">No details available.</p>);
    })();
    const previewListing: StoreListing = { iconUrl: storeDraft.iconUrl, video: null, screenshots: storeDraft.screenshots, reviews: [], releases: [], mcpTools: [], sample: false };
    return (<motion.div key="step4" className="mt-step-content ms-step" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.22 }}>
      <h2 className="mt-step-title">Store page</h2>
      <p className="mt-step-subtitle">This is your listing exactly as installers will see it. Add a video and screenshots, and tell them what it does.</p>

      <StorePage mode="edit" name={name} emoji={displayIcon} iconUrl={storeDraft.iconUrl} typeKey={selectedType} typeLabel={typeInfo.label} category={category} visibilityLabel={visLabel(visibility)} publisherName={localStorage.getItem('agency') || 'Your agency'} isOwnAgency groups={<GroupChips groups={groupsForSave(selectedType, groups)} type={selectedType}/>} description={storeDescription} listing={previewListing} includes={includes} whereItGoes={whereItGoes({ type: selectedType, groups: groupsForSave(selectedType, groups), surfaces: selectedType === 'widget' ? widgetSettings.surfaces : undefined })} edit={{
            videoUrl: storeDraft.videoUrl ?? '',
            onVideoUrlChange: url => setStoreDraft(d => ({ ...d, videoUrl: url })),
            screenshots: storeDraft.screenshots,
            onAddScreenshots: files => { void addScreenshots(files); },
            onRemoveScreenshot: index => setStoreDraft(d => ({ ...d, screenshots: d.screenshots.filter((_, i) => i !== index) })),
            onMoveScreenshot: moveScreenshot,
            mediaError,
            onDescriptionChange: setStoreDescription,
            descriptionHint: isAgent ? "Shown on your store page only. Your agent's own description isn't changed." : undefined,
            onDraftWithAi: () => { void draftWithAi(); },
            drafting,
            version: storeDraft.version ?? '',
            onVersionChange: v => setStoreDraft(d => ({ ...d, version: v })),
            releaseNotes: storeDraft.releaseNotes ?? '',
            onReleaseNotesChange: v => setStoreDraft(d => ({ ...d, releaseNotes: v })),
            lastVersion: storeDraft.lastVersion,
        }}/>

      {visibility === 'published' && (<p className="mt-step-subtitle" style={{ margin: '14px 0 0' }}>
          🌐 Saving submits this for Momentum AMP review. Other agencies see it once it's approved — until then it stays visible to your agency if it already was, otherwise just to you.
        </p>)}
      {!isEditing && selectedType !== 'agent' && (<p className="mt-step-subtitle" style={{ margin: '4px 0 0' }}>Saving installs this for you automatically.</p>)}
      {footer}

      <div className="mt-actions">
        <button type="button" className="mt-btn mt-btn--secondary" onClick={onBack} disabled={saving}>
          ← Back
        </button>
        <button type="button" className="mt-btn mt-btn--primary" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : isEditing ? 'Update' : visibility === 'published' ? 'Submit for review' : 'Publish'}
        </button>
      </div>
    </motion.div>);
};
const InstalledView: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [tools, setTools] = useState<InstalledTool[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [uninstallingId, setUninstallingId] = useState<string | null>(null);
    useEffect(() => {
        setLoading(true);
        setError(null);
        apiFetchInstalledTools()
            .then(setTools)
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, []);
    const toolId = searchParams.get('toolId');
    const selectedTool = toolId ? (tools.find(t => t.id === toolId) ?? null) : null;
    const handleUninstall = useCallback(async (id: string, forAgency: boolean) => {
        setUninstallingId(id);
        try {
            if (forAgency) {
                await apiUninstallToolForAgency(id);
            }
            else {
                await apiUninstallTool(id);
            }
            // Widgets keep a small per-user store in this browser (Momentum.storage); drop it
            // with the install so nothing lingers. No-op for other tool types.
            clearWidgetStorage(currentUserKey(), id);
            setTools(prev => prev.filter(t => t.id !== id));
            setSearchParams({ view: 'installed' });
        }
        catch (err: any) {
            alert(err.message || 'Failed to uninstall tool');
        }
        finally {
            setUninstallingId(null);
        }
    }, [setSearchParams]);
    if (loading) {
        return (<div className="mt-installed-grid">
        {Array.from({ length: 6 }).map((_, i) => (<SkeletonLoader key={i} height={180} lines={3}/>))}
      </div>);
    }
    if (error) {
        return (<div className="mt-empty-state">
        <div className="mt-empty-state__icon">⚠️</div>
        <div className="mt-empty-state__title">Failed to load</div>
        <div className="mt-empty-state__subtitle">{error}</div>
      </div>);
    }
    if (selectedTool) {
        return (<AnimatePresence mode="wait">
        <InstalledToolDetail key={selectedTool.id} tool={selectedTool} onBack={() => setSearchParams({ view: 'installed' })} onUninstall={handleUninstall} uninstalling={uninstallingId === selectedTool.id} onSaved={patch => setTools(prev => prev.map(t => t.id === selectedTool.id ? { ...t, ...patch } : t))}/>
      </AnimatePresence>);
    }
    if (tools.length === 0) {
        return (<div className="mt-empty-state">
        <div className="mt-empty-state__icon">📦</div>
        <div className="mt-empty-state__title">No tools installed</div>
        <div className="mt-empty-state__subtitle">Tools you install will appear here</div>
      </div>);
    }
    return (<div className="mt-installed-grid">
      {tools.map(tool => (<InstalledToolCard key={tool.id} tool={tool} onClick={() => setSearchParams({ view: 'installed', toolId: tool.id })}/>))}
    </div>);
};
// Shown while editing an approved Global integration - these changes reach other agencies only after review.
const GLOBAL_CHANGE_NOTE = "This integration is Global. Changing its base URL, authentication or endpoints sends it back for review, and it's paused for other agencies until a reviewer approves it again. Your own agency keeps using it.";
// What installing for the whole agency means for each type, said before anyone commits to it.
function agencyInstallNote(tool: InstallableTool): string {
    switch (tool.type) {
        case 'agent':
            return "Creates this agent for everyone in your agency. Anyone who hasn't set up Digital Workforce yet gets it when they do.";
        case 'integration':
            return tool.installerKeyScope === 'per_user'
                ? 'Everyone in your agency can use it. Each person adds their own API key.'
                : 'Everyone in your agency can use it, with one API key for the whole agency.';
        case 'widget':
            return 'Everyone in your agency gets it, with the permissions you grant here.';
        case 'workflow':
            return "Adds a draft to each person's Automation Center. Anyone without the Automation Center is skipped.";
        default:
            return (tool.mcpHeaders ?? []).some(h => h.requiredFromInstaller && h.scope === 'per_user')
                ? 'Everyone in your agency gets it. Each person enters their own credentials afterwards.'
                : 'Everyone in your agency gets it.';
    }
}
const MarketplaceInstallModal: React.FC<{
    tool: InstallableTool;
    iconUrl?: string;
    initialMode: 'personal' | 'agency';
    /** Agency Admins choose who gets it at the top of the prompt. Leave unset to install for initialMode only. */
    chooseAudience?: { installedForMe: boolean; installedForAgency: boolean; personalBlockedReason?: string };
    onCancel: () => void;
    onConfirm: (fieldValues: Record<string, string>, connectionValues: Record<string, string> | undefined, mode: 'personal' | 'agency') => void;
    installing: boolean;
}> = ({ tool, iconUrl, initialMode, chooseAudience, onCancel, onConfirm, installing }) => {
    const [mode, setMode] = useState<'personal' | 'agency'>(initialMode);
    const audienceUnavailable = !!chooseAudience && (mode === 'personal'
        ? chooseAudience.installedForMe || !!chooseAudience.personalBlockedReason
        : chooseAudience.installedForAgency);
    const requiredFields = (tool.mcpHeaders ?? []).filter(h => h.requiredFromInstaller && (mode === 'personal' || h.scope !== 'per_user'));
    const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(requiredFields.map(f => [f.key, ''])));
    // Widgets only: manifest.connections the installer is asked to supply a key/secret for.
    // "shared" scope only for an agency install — a per_user one is left for each person.
    const connectionFields = (tool.widgetManifest?.connections ?? []).filter(c => mode === 'personal' || c.scope !== 'per_user');
    const [connectionValues, setConnectionValues] = useState<Record<string, string>>(() => Object.fromEntries(connectionFields.map(c => [c.key, ''])));
    const [showConnectDocs, setShowConnectDocs] = useState(false);
    // Only another agency's Global tools need the trust acknowledgement - not your own agency's.
    const myCompanyId = currentCompanyId();
    const requiresTrust = tool.visibility === 'published' && !(tool.companyId && myCompanyId && tool.companyId === myCompanyId);
    const [trusted, setTrusted] = useState(false);
    // API Integrations: collect the installer's own key, or (Install for agency) their agency's.
    const [keyStatus, setKeyStatus] = useState<InstallerCredentialStatus | null>(null);
    const [keyValue, setKeyValue] = useState('');
    const [keyError, setKeyError] = useState<string | null>(null);
    const [savingKey, setSavingKey] = useState(false);
    // Agency install only: the admin chose to use the key they saved for themselves as the agency's.
    const [useMyKey, setUseMyKey] = useState(false);
    useEffect(() => {
        if (tool.type !== 'integration' || !tool.apiIntegrationId)
            return;
        getMyCredential(tool.apiIntegrationId).then(setKeyStatus).catch(err => setKeyError(err instanceof Error ? err.message : "Couldn't check the key this needs."));
    }, [tool.type, tool.apiIntegrationId]);
    // Until the key check comes back we can't know whether a key is needed, so Install
    // waits for it - otherwise an install could go through with no key asked for.
    const keyPending = tool.type === 'integration' && !!tool.apiIntegrationId && !keyStatus;
    // The agency's key: an Agency Admin sees it directly (agency_key); otherwise it's the
    // key their own calls use when that is the agency's.
    const agencyKeySet = keyStatus?.agency_key ? keyStatus.agency_key.set : keyStatus?.source === 'agency';
    const agencyKeyLast4 = keyStatus?.agency_key ? keyStatus.agency_key.last4 : (keyStatus?.source === 'agency' ? keyStatus.last4 : null);
    // Install for agency with one key per agency: the admin enters the agency's key (unless
    // it's already set). A personal install: the installer's own key, unless one already covers them.
    const keyNeeded = !!keyStatus?.required && (mode === 'agency'
        ? keyStatus.scope === 'agency' && !agencyKeySet
        : !keyStatus.set);
    const canUseMyKey = keyNeeded && mode === 'agency' && !!keyStatus?.own_key?.set;
    const keysAfterInstall = !!keyStatus?.required && mode === 'agency' && keyStatus.scope === 'per_user';
    const agencyKeyReused = !!keyStatus?.required && mode === 'agency' && keyStatus.scope === 'agency' && agencyKeySet;
    const busy = installing || savingKey;
    // Widgets run third-party code: show every permission in plain language and make the
    // installer acknowledge the ones that reach agency data or external sites.
    const isWidget = tool.type === 'widget';
    // The installer can grant a subset of what the manifest asks for; the acknowledgement
    // and the install both use the trimmed ("effective") manifest.
    const manifestPermissions = isWidget ? (tool.widgetManifest?.permissions ?? []) : [];
    const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(() => new Set(manifestPermissions));
    const effectiveManifest = isWidget
        ? applyInstallControls(tool.widgetManifest, { ...DEFAULT_CONTROLS, grantedPermissions: grantedFromSelection(tool.widgetManifest, selectedPermissions) })
        : null;
    const requiresPermissionAck = isWidget && widgetNeedsAcknowledgement(effectiveManifest);
    const [permissionsAccepted, setPermissionsAccepted] = useState(false);
    const canSubmit = requiredFields.every(f => values[f.key]?.trim())
        && connectionFields.every(c => !c.required || connectionValues[c.key]?.trim())
        && (!requiresTrust || trusted) && (!requiresPermissionAck || permissionsAccepted)
        && !keyPending && !audienceUnavailable
        && (!keyNeeded || (canUseMyKey && useMyKey) || !!keyValue.trim());
    // Grants are always persisted explicitly (even "all"), plus the list the person decided on,
    // so a later upload asking for more gets nothing new until they approve it.
    const confirmValues = (): Record<string, string> => {
        if (!isWidget) return values;
        const granted = grantedFromSelection(tool.widgetManifest, selectedPermissions);
        return { ...values, [CONTROL_KEYS.permissions]: JSON.stringify(granted), [CONTROL_KEYS.decided]: JSON.stringify(manifestPermissions) };
    };
    const confirm = async () => {
        if (keyNeeded && tool.apiIntegrationId) {
            setSavingKey(true);
            setKeyError(null);
            try {
                setKeyStatus(canUseMyKey && useMyKey
                    ? await copyMyKeyToAgency(tool.apiIntegrationId)
                    : await saveMyCredential(tool.apiIntegrationId, keyValue.trim(), mode === 'agency' ? 'agency' : 'me'));
            }
            catch (err) {
                setKeyError(err instanceof Error ? err.message : "Couldn't save the key.");
                return;
            }
            finally {
                setSavingKey(false);
            }
        }
        onConfirm(confirmValues(), connectionFields.length > 0 ? connectionValues : undefined, mode);
    };
    const displayIcon = tool.icon.trim() || <TypeGlyph type={tool.type}/>;
    return (<motion.div className="mt-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} onClick={e => { if (e.target === e.currentTarget && !installing)
        onCancel(); }}>
      <motion.div className="mt-modal" initial={{ opacity: 0, y: 12, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.97 }} transition={{ duration: 0.2 }}>
        <div className="mt-modal__header">
          <div className="mt-modal__header-info">
            <ListingIcon iconUrl={iconUrl} emoji={displayIcon} className="mt-modal__icon" alt={`${tool.name} icon`}/>
            <h3 className="mt-modal__title">{mode === 'agency' && !chooseAudience ? `Install ${tool.name} for your agency` : `Install ${tool.name}`}</h3>
          </div>
          <button type="button" className="mt-modal__close" onClick={onCancel} disabled={installing} aria-label="Close">×</button>
        </div>
        {chooseAudience && (<div className="ms-audience" role="radiogroup" aria-label="Who gets it">
            <span className="ms-audience__label">Who gets it?</span>
            <div className="ms-audience__options">
              {([
                ['personal', 'Just me', chooseAudience.installedForMe ? 'Already installed for you.' : chooseAudience.personalBlockedReason ?? 'Only you get it. You can install it for your agency later.', chooseAudience.installedForMe || !!chooseAudience.personalBlockedReason],
                ['agency', `Everyone at ${localStorage.getItem('agency') || 'your agency'}`, chooseAudience.installedForAgency ? 'Already installed for your agency.' : agencyInstallNote(tool), chooseAudience.installedForAgency],
            ] as const).map(([value, title, note, unavailable]) => (<button key={value} type="button" role="radio" aria-checked={mode === value} disabled={unavailable || busy} className={`ms-audience__option${mode === value ? ' ms-audience__option--on' : ''}`} onClick={() => setMode(value)}>
                  <span className="ms-audience__radio" aria-hidden="true"/>
                  <span className="ms-audience__title">{title}</span>
                  <span className="ms-audience__note">{note}</span>
                </button>))}
            </div>
          </div>)}
        {tool.description && !chooseAudience && (<p className="mt-step-subtitle" style={{ marginBottom: 4 }}>{tool.description}</p>)}
        {tool.type === 'mcp_server' && (<McpDetailBody url={tool.mcpUrl} headers={tool.mcpHeaders}/>)}
        {tool.type === 'integration' && (<IntegrationDetailBody apiIntegrationId={tool.apiIntegrationId}/>)}
        {keyNeeded && keyStatus && (<div className="mt-field mt-field--full">
            <label className="mt-label" htmlFor="ai-install-key">{mode === 'agency' ? "Your agency's API key" : 'Your API key'} <span className="mt-required">*</span></label>
            <p className="mt-field-hint">
              {mode === 'agency'
                ? "Calls from everyone in your agency use this key. It's never used by anyone outside your agency."
                : "Calls from you use this key. It's never used by anyone else - not even this integration's creator."}
              {' '}Stored encrypted; only the last 4 characters are ever shown.
            </p>
            {canUseMyKey && (<label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, cursor: 'pointer', margin: '0 0 8px' }}>
                <input type="checkbox" checked={useMyKey} onChange={e => setUseMyKey(e.target.checked)} disabled={busy} style={{ marginTop: 2 }}/>
                Use my saved key (ending {keyStatus?.own_key?.last4}) as the agency's key
              </label>)}
            {!(canUseMyKey && useMyKey) && (<IntegrationKeyInput id="ai-install-key" value={keyValue} onChange={setKeyValue} disabled={busy}/>)}
          </div>)}
        {agencyKeyReused && (<p className="mt-step-subtitle">Your agency's key{agencyKeyLast4 ? ` (ending ${agencyKeyLast4})` : ''} will be used. An Agency Admin can change it from the Installed tab.</p>)}
        {keysAfterInstall && (<p className="mt-step-subtitle">Each person in your agency adds their own API key after install, from their Installed tab.</p>)}
        {keyError && <p className="ai-error" role="alert">{keyError}</p>}
        {isWidget && (<div style={{ margin: '4px 0 12px' }}>
            <span className="wg-perm-title">{mode === 'agency' ? 'This widget will be able to (for everyone in your agency)' : 'This widget will be able to'}</span>
            <WidgetPermissionList manifest={tool.widgetManifest} emptyText="Nothing beyond drawing its own UI — no agency data, no network, no access to Momentum AMP" selectable={manifestPermissions.length > 0} selected={selectedPermissions} onToggle={(key, on) => setSelectedPermissions(prev => { const next = new Set(prev); if (on) next.add(key); else next.delete(key); return next; })}/>
            {manifestPermissions.length > 0 && (<p className="wg-controls__hint" style={{ marginTop: 6 }}>Untick anything you don't want to grant — the widget still installs, that capability is simply refused when it asks. You can change this later under Installed → Configuration.</p>)}
            {requiresPermissionAck && (<label className="wg-perm-ack">
                <input type="checkbox" checked={permissionsAccepted} onChange={e => setPermissionsAccepted(e.target.checked)}/>
                <span>{widgetAcknowledgementText(effectiveManifest, mode === 'agency')}</span>
              </label>)}
          </div>)}
        {requiredFields.length > 0 && (<p className="mt-step-subtitle">
            {mode === 'agency'
              ? 'These shared values apply to everyone in your agency. Anyone with a per-user field will be asked to fill in their own after install.'
              : 'This tool needs a few values from you before it can connect.'}
          </p>)}
        <div className="mt-form">
          {requiredFields.map(f => (<div key={f.key} className="mt-field mt-field--full">
              <label className="mt-label">{f.label || f.key} <span className="mt-required">*</span></label>
              {f.hint && <p className="mt-field-hint">{f.hint}</p>}
              <input className="mt-input" type="text" value={values[f.key] ?? ''} onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}/>
            </div>))}
          {connectionFields.length > 0 && (<>
              <p className="mt-step-subtitle" style={{ marginTop: connectionFields.length && requiredFields.length ? 8 : 0 }}>
                Connect to other services{mode === 'agency' ? ' (shared with your whole agency)' : ''} — Momentum AMP holds these keys for the widget; it never sees them itself.
                {' '}<button type="button" onClick={() => setShowConnectDocs(true)} style={{ color: 'inherit', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}>Learn more</button>.
              </p>
              {connectionFields.map(c => (<div key={c.key} className="mt-field mt-field--full">
                  <label className="mt-label">{c.label} {c.required && <span className="mt-required">*</span>}</label>
                  {c.hint && <p className="mt-field-hint">{c.hint}</p>}
                  <input className="mt-input" type="password" autoComplete="off" value={connectionValues[c.key] ?? ''} onChange={e => setConnectionValues(v => ({ ...v, [c.key]: e.target.value }))}/>
                </div>))}
              {showConnectDocs && (<WidgetDocsModal title="Connecting a widget to an outside service" url={WIDGET_INSTALL_DOCS_URL} onClose={() => setShowConnectDocs(false)}/>)}
            </>)}
          {requiresTrust && (<label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={trusted} onChange={e => setTrusted(e.target.checked)} style={{ marginTop: 2 }}/>
              I trust this community shared item and understand it comes from another agency in the Momentum AMP community, not from Momentum AMP directly.
            </label>)}
        </div>
        <div className="mt-actions">
          <button type="button" className="mt-btn mt-btn--secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="mt-btn mt-btn--primary" disabled={!canSubmit || busy} onClick={confirm}>
            {savingKey ? 'Saving key…' : installing ? 'Installing…' : keyPending && !keyError ? 'Checking key…' : mode === 'agency' ? 'Install for agency' : 'Install for me'}
          </button>
        </div>
      </motion.div>
    </motion.div>);
};
// Who published a listing, in plain words, for the Browse card and store page: the
// publishing agency's name from the API (card C), else the creator's name, else a
// generic label.
function publisherLabel(tool: Pick<MyTool, 'publisherName' | 'creatorName' | 'isOwnAgency' | 'companyId' | 'visibility'>): string {
    if (tool.publisherName)
        return tool.publisherName;
    if (tool.creatorName)
        return tool.creatorName;
    const own = tool.isOwnAgency ?? (!!tool.companyId && tool.companyId === currentCompanyId());
    if (own)
        return localStorage.getItem('agency') || 'Your agency';
    return tool.visibility === 'published' ? 'Community publisher' : 'Your agency';
}
// Store copy for cards: the listing's description without Markdown symbols.
function plainDescription(text: string): string {
    return text.replace(/[*_`#>]/g, '').replace(/^\s*-\s+/gm, '').replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').replace(/\s+/g, ' ').trim();
}
// Where a listing lands once it's installed. Skills and MCP servers go to the installer's
// library in Digital Workforce, to turn on per agent; agents are created there. Only widgets
// have publisher-chosen places (surfaces, Toolbox). A listing saved before groups existed
// reads as its type's defaults.
function whereItGoes(tool: { type: ToolType; groups?: string[]; surfaces?: string[] }): React.ReactNode {
    const groups = tool.groups && tool.groups.length > 0 ? tool.groups : defaultGroups(tool.type);
    const has = (key: string) => groups.includes(key);
    const lines: React.ReactNode[] = [];
    switch (tool.type) {
        case 'skill':
        case 'mcp_server': {
            const tab = tool.type === 'skill' ? 'Skills' : 'Tools';
            lines.push(<>Added to your <strong>library in Digital Workforce</strong>. Turn it on for any agent from that agent's {tab} tab.</>);
            break;
        }
        case 'agent':
            lines.push(<>Creates a <strong>new agent</strong> in your Digital Workforce, with its own copies of the skills it includes. Changes you make to it later stay yours.</>);
            break;
        case 'workflow':
            lines.push(<>Adds to your <strong>Automation Center</strong> as a draft. Turn it on there when you're ready.</>);
            break;
        case 'integration':
            lines.push(<>Adds to your <strong>API integrations</strong>. Turn it on for the agents you choose. You may be asked for your own API key.</>);
            break;
        case 'widget': {
            const surfaces = (tool.surfaces ?? []).map(s => groupLabel(SURFACE_TO_GROUP[s] ?? s));
            lines.push(<>Shows up in Momentum AMP{surfaces.length ? <>: <strong>{surfaces.join(', ')}</strong></> : ''}. You choose what it's allowed to access when you install it.</>);
            break;
        }
        default:
            break;
    }
    if (tool.type === 'widget' && has(GROUP_KEYS.TOOLBOX))
        lines.push(<>Also listed in <strong>Toolbox</strong>.</>);
    if (lines.length === 0)
        return null;
    return (<>{lines.map((line, i) => (<p key={i}>{line}</p>))}</>);
}
// The success toast after an install. Skills and MCP servers land in the installer's
// library in Digital Workforce rather than on an agent, so the toast says where to find them.
function installSuccessMessage(tool: Pick<MyTool, 'name' | 'type'>, mode: 'personal' | 'agency'): string {
    const toLibrary = tool.type === 'skill' || tool.type === 'mcp_server';
    if (mode === 'agency') {
        const parts = [`${tool.name} installed for your agency.`];
        if (toLibrary)
            parts.push("It's in each member's library in Digital Workforce. Turn it on for any agent.");
        return parts.join(' ');
    }
    if (toLibrary)
        return `${tool.name}: Added to your library in Digital Workforce. Turn it on for any agent.`;
    return `${tool.name} installed successfully.`;
}
// An agent listing's parts, the way an installer scans them: skills, connected MCP servers
// and API integrations first, the agent's full instructions folded underneath. Each part
// opens its detail popup; showSkillContent is forwarded to it (false on Browse - see
// AgentBundleItemModal for why).
const AgentComponents: React.FC<{ snapshot: AgentShareSnapshot | null; error?: string | null; instructions: React.ReactNode; showSkillContent?: boolean }> = ({ snapshot, error, instructions, showSkillContent = true }) => {
    const [selectedItem, setSelectedItem] = useState<BundleItem | null>(null);
    if (error)
        return (<p className="ms-muted">{error}</p>);
    if (!snapshot)
        return (<p className="ms-muted">Loading what this agent includes…</p>);
    const integrations = snapshot.integrations ?? [];
    const nothingExtra = snapshot.skills.length === 0 && snapshot.mcp_servers.length === 0 && integrations.length === 0;
    const mcpNote = (m: AgentShareSnapshot['mcp_servers'][number]) => m.platform_provided
        ? 'MCP server - included with every agent, no setup'
        : m.needs_auth ? 'MCP server - you add your own key' : 'MCP server';
    return (<>
        {snapshot.skills.length > 0 && (<div>
            <div className="mt-detail-field-label">Skills ({snapshot.skills.length})</div>
            <ul className="ms-tool-list">
              {snapshot.skills.map(sk => {
                const summary = extractSkillDescription(sk.content);
                return (<li key={sk.name} className="ms-tool-list__item--link">
                    <button type="button" className="ms-tool-list__btn" aria-haspopup="dialog" onClick={() => setSelectedItem({ kind: 'skill', name: sk.name, content: sk.content })}>
                      <strong className="ms-tool-list__name">{sk.name}</strong>{summary && <span>{summary}</span>}
                    </button>
                  </li>);
            })}
            </ul>
          </div>)}
        {snapshot.mcp_servers.length > 0 && (<div>
            <div className="mt-detail-field-label">Connected to ({snapshot.mcp_servers.length})</div>
            <ul className="ms-tool-list">
              {snapshot.mcp_servers.map(m => (<li key={m.url} className="ms-tool-list__item--link">
                  <button type="button" className="ms-tool-list__btn" aria-haspopup="dialog" onClick={() => setSelectedItem({ kind: 'mcp', name: m.name, url: m.url, needs_auth: m.needs_auth, platform_provided: m.platform_provided })}>
                    <strong className="ms-tool-list__name">{m.name}</strong><span>{mcpNote(m)}</span>
                  </button>
                </li>))}
            </ul>
          </div>)}
        {integrations.length > 0 && (<div>
            <div className="mt-detail-field-label">API integrations ({integrations.length})</div>
            <ul className="ms-tool-list">
              {integrations.map(i => (<li key={i.slug} className="ms-tool-list__item--link">
                  <button type="button" className="ms-tool-list__btn" aria-haspopup="dialog" onClick={() => setSelectedItem({ kind: 'integration', integration: i })}>
                    <strong className="ms-tool-list__name">{i.name}</strong>{i.needs_auth && <span>You add your own API key</span>}
                  </button>
                </li>))}
            </ul>
          </div>)}
        {nothingExtra && (<p className="ms-muted">Just the agent itself - no extra skills or connections.</p>)}
        <details className="ms-fold">
          <summary>Show the agent's instructions</summary>
          <div className="ms-fold__body">{instructions}</div>
        </details>
        {selectedItem && (<AgentBundleItemModal item={selectedItem} onClose={() => setSelectedItem(null)} showSkillContent={showSkillContent}/>)}
      </>);
};
const AgentListingIncludes: React.FC<{ token: string }> = ({ token }) => {
    const [snapshot, setSnapshot] = useState<AgentShareSnapshot | null>(null);
    const [error, setError] = useState<string | null>(null);
    useEffect(() => {
        let cancelled = false;
        resolveAgentShare(token)
            .then(r => { if (!cancelled) setSnapshot(r.snapshot); })
            .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : "This agent's contents aren't available."); });
        return () => { cancelled = true; };
    }, [token]);
    // showSkillContent=false: this is the marketplace browse-to-install surface - the one
    // context skill.md must stay hidden in, per AgentBundleItemModal's comment.
    return (<AgentComponents snapshot={snapshot} error={error} showSkillContent={false} instructions={<AgentBundlePreview token={token} showSkillContent={false} instructionsOnly/>}/>);
};
// What a Browse listing brings with it - the per-type detail bodies, laid out for the store page.
const ListingIncludes: React.FC<{ tool: MyTool; listing: StoreListing }> = ({ tool, listing }) => {
    const sample = getSampleListing(tool.id);
    if (sample?.widget) {
        return (<>
            <div>
              <div className="mt-detail-field-label">It can</div>
              <WidgetPermissionList manifest={tool.widgetManifest} variant="chips"/>
            </div>
            <div>
              <div className="mt-detail-field-label">Preview</div>
              <img className="ms-sample-preview" src={sample.widget.preview} alt={`${tool.name} preview`}/>
            </div>
          </>);
    }
    if (sample?.workflow) {
        return (<>
            <div>
              <div className="mt-detail-field-label">Starts when</div>
              <p className="ms-muted" style={{ color: 'var(--text-primary)' }}>{sample.workflow.trigger}</p>
            </div>
            <div>
              <div className="mt-detail-field-label">Steps ({sample.workflow.steps.length})</div>
              <ol className="ms-tool-list ms-step-list">
                {sample.workflow.steps.map((st, i) => (<li key={i}><span>{st}</span></li>))}
              </ol>
            </div>
          </>);
    }
    if (tool.type === 'agent') {
        return tool.agentShareToken
            ? (<AgentListingIncludes token={tool.agentShareToken}/>)
            : (<p className="ms-muted">This agent's contents aren't available.</p>);
    }
    if (tool.type === 'mcp_server') {
        return (<>
            {listing.mcpTools.length > 0 && (<div>
                <div className="mt-detail-field-label">Tools ({listing.mcpTools.length})</div>
                <ul className="ms-tool-list">
                  {listing.mcpTools.map(t => (<li key={t.name}><code>{t.name}</code><span>{t.description}</span></li>))}
                </ul>
              </div>)}
            <McpDetailBody url={tool.mcpUrl} headers={tool.mcpHeaders}/>
          </>);
    }
    if (tool.type === 'integration')
        return (<IntegrationDetailBody apiIntegrationId={tool.apiIntegrationId}/>);
    if (tool.type === 'widget') {
        return (<>
            <div>
              <div className="mt-detail-field-label">It can</div>
              <WidgetPermissionList manifest={tool.widgetManifest} variant="chips"/>
            </div>
            <WidgetScanReport toolId={tool.id} manifest={tool.widgetManifest} compact/>
            <WidgetPreview toolId={tool.id} manifest={tool.widgetManifest} height={320} title={`${tool.name} preview`}/>
          </>);
    }
    if (tool.type === 'skill') {
        return (<>
            <p className="ms-muted">A skill your agents follow whenever the task comes up. Its instructions stay private to the publisher.</p>
            {tool.kbFileNames.length > 0 && (<div>
                <div className="mt-detail-field-label">Knowledge Base</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {tool.kbFileNames.map(n => (<span key={n} className="mt-review-skill-chip">📄 {n}</span>))}
                </div>
              </div>)}
          </>);
    }
    return (<p className="ms-muted">No details available.</p>);
};
const fallbackStoreListing = (tool: MyTool): StoreListing => {
    const listing = getStoreListing({
        id: tool.id,
        name: tool.name,
        type: tool.type,
        createdAt: tool.createdAt,
        widgetVersion: tool.widgetVersion,
    });
    return tool.iconUrl ? { ...listing, iconUrl: tool.iconUrl } : listing;
};

const MarketplaceCard: React.FC<{
    tool: MyTool;
    listing: StoreListing;
    onViewDetail: () => void;
    onInstall: () => void;
    installing: boolean;
    installed: boolean;
}> = ({ tool, listing, onViewDetail, onInstall, installing, installed }) => {
    const isAgencyOwner = readIsAgencyOwner();
    const typeInfo = TOOL_TYPES.find(t => t.type === tool.type)!;
    const displayIcon = tool.icon.trim() || <TypeGlyph type={tool.type}/>;
    // Integrations store real third-party API credentials and make live outbound calls -
    // an individual can't personally install one until their own agency has approved it via
    // "Install for agency", regardless of marketplace visibility (MyAgency or Published) -
    // unless it's another agency's Global integration where each user brings their own key.
    const needsAgencyApproval = tool.type === 'integration' && !tool.isAgencyInstalled && tool.installerKeyScope !== 'per_user';
    const reviewCount = listing.reviews.length;
    const avg = reviewCount ? listing.reviews.reduce((s, r) => s + r.rating, 0) / reviewCount : 0;
    return (<div className="mt-inst-card" onClick={onViewDetail} style={{ cursor: 'pointer' }}>
      <div className="mt-inst-card__top-row">
        <ListingIcon iconUrl={listing.iconUrl} emoji={displayIcon} className="mt-inst-card__logo" alt={`${tool.name} icon`}/>
      </div>
      <div className="mt-inst-card__name">{tool.name}</div>
      <PublisherLabel name={publisherLabel(tool)} isOwnAgency={tool.isOwnAgency} className="ms-card-publisher" prefix="By "/>
      <GroupChips groups={tool.groups} type={tool.type}/>
      {reviewCount > 0 && (<div className="ms-card-meta">
          <span className="ms-stars__on">★</span> {avg.toFixed(1)} <span>({reviewCount})</span>
        </div>)}
      <p className="mt-inst-card__desc">{plainDescription(listing.storeDescription ?? tool.description)}</p>
      <div className="mt-inst-card__footer">
        <span className={`mt-badge mt-badge--type mt-badge--type-${tool.type}`}>{typeInfo.label}</span>
        {tool.type === 'widget' && tool.widgetVersion && (<span className="mt-badge mt-badge--category">v{tool.widgetVersion}</span>)}
        <span className="mt-badge mt-badge--visibility">{visLabel(tool.visibility)}</span>
        <span className="mt-inst-card__footer-spacer" />
        <InstallButton size="sm" isAgencyOwner={isAgencyOwner} installed={installed} isAgencyInstalled={!!tool.isAgencyInstalled} installing={installing} personalBlockedReason={needsAgencyApproval ? 'Ask an Agency Admin to install this for your agency first.' : undefined} onInstall={onInstall}/>
      </div>
    </div>);
};
// A Browse listing's store page (StorePage in view mode), driven by the URL
// (?view=marketplace&toolId=<id>) so a refresh or a shared link still resolves it
// once the tool list has loaded. This is Browse, not an editor - the only actions
// are installing and, once installed, reviewing.
const MarketplaceToolDetail: React.FC<{
    tool: MyTool;
    listing: StoreListing;
    isAgencyOwner: boolean;
    onBack: () => void;
    onInstall: () => void;
    onManageAgents: () => void;
    onSubmitReview: (rating: number, comment: string) => void;
    onPublisherClick: (publisher: string) => void;
    installing: boolean;
    installed: boolean;
}> = ({ tool, listing, isAgencyOwner, onBack, onInstall, onManageAgents, onSubmitReview, onPublisherClick, installing, installed }) => {
    const typeInfo = TOOL_TYPES.find(t => t.type === tool.type)!;
    const displayIcon = tool.icon.trim() || <TypeGlyph type={tool.type}/>;
    // Integrations store real third-party API credentials and make live outbound calls -
    // an individual can't personally install one until their own agency has approved it via
    // "Install for agency", regardless of marketplace visibility (MyAgency or Published) -
    // unless it's another agency's Global integration where each user brings their own key.
    const needsAgencyApproval = tool.type === 'integration' && !tool.isAgencyInstalled && tool.installerKeyScope !== 'per_user';
    return (<motion.div className="mt-inst-detail-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} transition={{ duration: 0.2 }}>
      <button type="button" className="mt-inst-back-btn" onClick={onBack}>
        ← Back to Browse
      </button>
      <StorePage mode="view" name={tool.name} emoji={displayIcon} iconUrl={listing.iconUrl} typeKey={tool.type} typeLabel={typeInfo.label} category={tool.category} visibilityLabel={visLabel(tool.visibility)} publisherName={publisherLabel(tool)} isOwnAgency={tool.isOwnAgency} onPublisherClick={tool.publisherName ? () => onPublisherClick(tool.publisherName!) : undefined} groups={<GroupChips groups={tool.groups} type={tool.type}/>} updatedAt={tool.updatedAt ?? tool.createdAt} description={listing.storeDescription ?? tool.description} listing={listing} includes={<ListingIncludes tool={tool} listing={listing}/>} whereItGoes={whereItGoes({ type: tool.type, groups: tool.groups, surfaces: tool.widgetManifest?.surfaces })} canReview={installed && !tool.isMine} onSubmitReview={onSubmitReview} actions={<>
            <InstallButton showStatus isAgencyOwner={isAgencyOwner} installed={installed} isAgencyInstalled={!!tool.isAgencyInstalled} installing={installing} personalBlockedReason={needsAgencyApproval ? 'Ask an Agency Admin to install this for your agency first.' : undefined} onInstall={onInstall}/>
            {tool.type === 'integration' && installed && (<button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={onManageAgents}>
                Enable for agents…
              </button>)}
          </>}/>
    </motion.div>);
};
const MarketplaceView: React.FC = () => {
    const isAgencyOwner = readIsAgencyOwner();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tools, setTools] = useState<MyTool[]>([]);
    const [installedIds, setInstalledIds] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [installingId, setInstallingId] = useState<string | null>(null);
    const [pendingInstall, setPendingInstall] = useState<MyTool | null>(null);
    const [pendingInstallMode, setPendingInstallMode] = useState<'personal' | 'agency'>('personal');
    const [enableForAgentsTool, setEnableForAgentsTool] = useState<MyTool | null>(null);
    // Detail selection lives in the URL, same pattern as InstalledView's
    // ?view=installed&toolId=<id> — a refresh or a shared link resolves once
    // `tools` has loaded, rather than being lost like the old popup's local state.
    const toolId = searchParams.get('toolId');
    const selectedTool = toolId ? (tools.find(t => t.id === toolId) ?? null) : null;
    const [toast, setToast] = useState<{
        type: 'success' | 'error';
        message: string;
    } | null>(null);
    const [typeFilter, setTypeFilter] = useState<'all' | ToolType>('all');
    const [search, setSearch] = useState('');
    // Store page content per listing (icon, media, reviews, versions). Bumped after a
    // review is posted so the page re-reads it.
    const [storeVersion, setStoreVersion] = useState(0);
    const [listings, setListings] = useState<Map<string, StoreListing>>(() => new Map());
    useEffect(() => {
        let cancelled = false;
        const map = new Map<string, StoreListing>();
        tools.forEach(t => map.set(t.id, fallbackStoreListing(t)));
        setListings(map);
        Promise.all(tools.filter(t => !isSampleListing(t.id)).map(async tool => {
            try { return [tool.id, await getMarketplaceStore(tool.id)] as const; }
            catch { return null; }
        })).then(rows => {
            if (cancelled) return;
            setListings(current => {
                const next = new Map(current);
                rows.forEach(row => { if (row) next.set(row[0], row[1]); });
                return next;
            });
        });
        return () => { cancelled = true; };
    }, [tools, storeVersion]);
    const submitReview = async (tool: MyTool, rating: number, comment: string) => {
        try {
            await saveMarketplaceReview(tool.id, rating, comment);
            setStoreVersion(v => v + 1);
        } catch (err: any) {
            setToast({ type: 'error', message: err?.message || 'Failed to save the review.' });
        }
    };
    // Publisher filter lives in the URL (?publisher=) so the detail page's "Published by" link
    // and a refresh both keep it; opening/closing a listing carries it along.
    const publisherFilter = searchParams.get('publisher') ?? ALL_PUBLISHERS;
    const groupFilter = searchParams.get('group') ?? ALL_GROUPS;
    const browseParams = useCallback((extra: Record<string, string> = {}, publisher: string = publisherFilter, group: string = groupFilter): Record<string, string> => ({
        view: 'marketplace', ...(publisher ? { publisher } : {}), ...(group ? { group } : {}), ...extra,
    }), [publisherFilter, groupFilter]);
    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        Promise.all([apiFetchMarketplaceTools().then(list => isStoreMockEnabled() ? withMockListings(list) : list), apiFetchInstalledTools().catch(() => [])])
            .then(([marketplaceTools, installed]) => {
            setTools(marketplaceTools);
            setInstalledIds(new Set(installed.map(t => t.id)));
        })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);
    const runInstall = useCallback(async (tool: MyTool, fieldValues?: Record<string, string>, mode: 'personal' | 'agency' = 'personal', connectionValues?: Record<string, string>) => {
        setInstallingId(tool.id);
        try {
            if (tool.type === 'widget')
                fieldValues = withRecordedGrants(fieldValues, tool.widgetManifest);
            if (mode === 'agency') {
                await apiInstallToolForAgency(tool.id, fieldValues, connectionValues);
                setToast({ type: 'success', message: installSuccessMessage(tool, 'agency') });
                // InstallToolForAgency also installs a personal row for the admin who
                // triggered it, so installedIds (set below) and isAgencyInstalled both flip.
                setTools(prev => prev.map(t => t.id === tool.id ? { ...t, isAgencyInstalled: true } : t));
            }
            else {
                await apiInstallTool(tool.id, fieldValues, undefined, connectionValues);
                setToast({ type: 'success', message: installSuccessMessage(tool, 'personal') });
                if (tool.type === 'integration')
                    setEnableForAgentsTool(tool);
            }
            setPendingInstall(null);
            setInstalledIds(prev => new Set(prev).add(tool.id));
        }
        catch (err: unknown) {
            const msg = err instanceof Error ? err.message : '';
            setToast({ type: 'error', message: msg === 'already_installed' ? 'This tool is already installed.' : (msg || 'Failed to install tool.') });
        }
        finally {
            setInstallingId(null);
            setTimeout(() => setToast(null), 4000);
        }
    }, []);
    const handleAgentInstallForAgency = async (tool: MyTool) => {
        setInstallingId(tool.id);
        try {
            const response = await apiInstallAgentForAgency(tool.id);
            const summary = summarizeAgentAgencyInstall(response);
            const parts = [`${summary.created} agent(s) created for your agency.`];
            if (summary.pendingSetup)
                parts.push(`${summary.pendingSetup} teammate(s) aren't set up yet and will get it automatically at their next sign-in.`);
            if (summary.alreadyInstalled)
                parts.push(`${summary.alreadyInstalled} already had it.`);
            if (summary.failed)
                parts.push(`${summary.failed} couldn't be created.`);
            // Bundled API integrations are installed for the agency; any that take a key
            // need one (the agency's, or each person's) before calls work.
            const needKey = response.results.find(r => r.integrations_needing_key?.length)?.integrations_needing_key ?? [];
            if (needKey.length)
                parts.push(`${needKey.join(', ')} still ${needKey.length === 1 ? 'needs' : 'need'} an API key - add it in Marketplace > Installed.`);
            setToast({ type: summary.failed ? 'error' : 'success', message: parts.join(' ') });
        }
        catch (err: unknown) {
            const msg = err instanceof Error ? err.message : '';
            setToast({ type: 'error', message: msg || 'Failed to install agent for agency.' });
        }
        finally {
            setInstallingId(null);
            setTimeout(() => setToast(null), 5000);
        }
    };
    // Integrations store real third-party API credentials - an individual can't install one
    // until their agency has (unless each person brings their own key).
    const personalBlock = (tool: MyTool): string | undefined => tool.type === 'integration' && !tool.isAgencyInstalled && tool.installerKeyScope !== 'per_user'
        ? 'Ask an Agency Admin to install this for your agency first.'
        : undefined;
    // Install: Agency Admins always get the prompt, which starts with who gets it. Everyone
    // else installs for themselves, through the prompt only when it has something to ask.
    const startInstall = (tool: MyTool) => {
        if (isSampleListing(tool.id)) {
            setToast({ type: 'success', message: 'This is a sample listing - there is nothing to install.' });
            setTimeout(() => setToast(null), 4000);
            return;
        }
        if (!isAgencyOwner) {
            handleInstallClick(tool, 'personal');
            return;
        }
        setPendingInstallMode(installedIds.has(tool.id) || personalBlock(tool) ? 'agency' : 'personal');
        setPendingInstall(tool);
    };
    // After the prompt: agents keep their own install paths, everything else installs here.
    const finishInstall = (tool: MyTool, values: Record<string, string>, connectionValues: Record<string, string> | undefined, mode: 'personal' | 'agency') => {
        if (tool.type === 'agent' && tool.agentShareToken) {
            setPendingInstall(null);
            if (mode === 'agency')
                handleAgentInstallForAgency(tool);
            else
                navigate(ROUTES.SHARE_AGENT_RESOLVE.replace(':token', tool.agentShareToken));
            return;
        }
        runInstall(tool, values, mode, connectionValues);
    };
    const handleInstallClick = (tool: MyTool, mode: 'personal' | 'agency' = 'personal') => {
        if (installedIds.has(tool.id))
            return;
        if (tool.type === 'agent' && tool.agentShareToken) {
            if (mode === 'agency') {
                handleAgentInstallForAgency(tool);
                return;
            }
            navigate(ROUTES.SHARE_AGENT_RESOLVE.replace(':token', tool.agentShareToken));
            return;
        }
        const relevantHeaders = (tool.mcpHeaders ?? []).filter(h => mode === 'personal' || h.scope !== 'per_user');
        const hasRequiredFields = relevantHeaders.some(h => h.requiredFromInstaller);
        // A published listing is community-shared (came from another agency, or
        // at least isn't confirmed same-agency) — always confirm via the trust
        // checkbox before installing, not only when there happen to be required
        // MCP credential fields to collect.
        // Widgets that ask for any permission always get the prompt, so the installer sees
        // (and can trim) what they are granting.
        const widgetAsksPermissions = tool.type === 'widget' && (tool.widgetManifest?.permissions?.length ?? 0) > 0;
        // A connection key can only be typed in the prompt (or later under Configuration) —
        // skipping it installed a widget whose Momentum.connect() then failed with "connect
        // first", so offer the prompt for optional connections too.
        const widgetNeedsConnections = tool.type === 'widget' && (tool.widgetManifest?.connections ?? [])
            .some(c => mode === 'personal' || c.scope !== 'per_user');
        // An integration may need an API key - the installer's own or (agency install) the
        // agency's - and only the prompt asks for it. Skipping it installed My Agency
        // integrations with no key, so every call failed until someone found Installed.
        if (((tool.type === 'mcp_server' || tool.type === 'widget') && hasRequiredFields) || widgetAsksPermissions || widgetNeedsConnections || tool.visibility === 'published' || tool.type === 'integration') {
            setPendingInstallMode(mode);
            setPendingInstall(tool);
        }
        else {
            runInstall(tool, undefined, mode);
        }
    };
    if (loading) {
        return (<div className="mt-installed-grid">
        {Array.from({ length: 6 }).map((_, i) => (<SkeletonLoader key={i} height={180} lines={3}/>))}
      </div>);
    }
    if (error) {
        return (<div className="mt-empty-state">
        <div className="mt-empty-state__icon">⚠️</div>
        <div className="mt-empty-state__title">Failed to load</div>
        <div className="mt-empty-state__subtitle">{error}</div>
      </div>);
    }
    if (selectedTool) {
        return (<>
        <AnimatePresence mode="wait">
          <MarketplaceToolDetail key={selectedTool.id} tool={selectedTool} listing={listings.get(selectedTool.id) ?? fallbackStoreListing(selectedTool)} isAgencyOwner={isAgencyOwner} installing={installingId === selectedTool.id} installed={installedIds.has(selectedTool.id)} onBack={() => setSearchParams(browseParams())} onPublisherClick={publisher => setSearchParams(browseParams({}, publisher))} onInstall={() => startInstall(selectedTool)} onManageAgents={() => setEnableForAgentsTool(selectedTool)} onSubmitReview={(rating, comment) => submitReview(selectedTool, rating, comment)}/>
        </AnimatePresence>

        <AnimatePresence>
          {enableForAgentsTool?.apiIntegrationId && (<AgentEnablementModal toolName={enableForAgentsTool.name} integrationId={enableForAgentsTool.apiIntegrationId} onClose={() => setEnableForAgentsTool(null)}/>)}
        </AnimatePresence>

        <AnimatePresence>
          {pendingInstall && (<MarketplaceInstallModal key={pendingInstall.id} tool={pendingInstall} iconUrl={listings.get(pendingInstall.id)?.iconUrl} initialMode={pendingInstallMode} chooseAudience={isAgencyOwner ? { installedForMe: installedIds.has(pendingInstall.id), installedForAgency: !!pendingInstall.isAgencyInstalled, personalBlockedReason: personalBlock(pendingInstall) } : undefined} installing={installingId === pendingInstall.id} onCancel={() => setPendingInstall(null)} onConfirm={(values, connectionValues, chosen) => finishInstall(pendingInstall, values, connectionValues, chosen)}/>)}
        </AnimatePresence>

        <AnimatePresence>
        </AnimatePresence>

        <AnimatePresence>
          {toast && (<motion.div className={toast.type === 'success' ? 'success-toast' : 'error-toast'} initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.25 }}>
              <span>{toast.message}</span>
            </motion.div>)}
        </AnimatePresence>
      </>);
    }
    if (tools.length === 0) {
        return (<div className="mt-empty-state">
        <div className="mt-empty-state__icon">🛍️</div>
        <div className="mt-empty-state__title">Nothing here yet</div>
        <div className="mt-empty-state__subtitle">Tools shared by your agency or published to everyone will appear here</div>
      </div>);
    }
    const normalizedSearch = search.trim().toLowerCase();
    const browseFilteredTools = tools.filter(t => {
        const matchesType = typeFilter === 'all' || t.type === typeFilter;
        const matchesSearch = !normalizedSearch || t.name.toLowerCase().includes(normalizedSearch)
            || (t.publisherName ?? '').toLowerCase().includes(normalizedSearch);
        return matchesType && matchesSearch
            && matchesPublisher(t.publisherName, publisherFilter);
    });
    const browseTypeFilters: Array<{ value: 'all' | ToolType; label: string }> = [
        { value: 'all', label: 'All' },
        { value: 'skill', label: 'Skills' },
        { value: 'agent', label: 'Agents' },
        { value: 'mcp_server', label: 'MCP servers' },
        { value: 'integration', label: 'API integrations' },
        { value: 'widget', label: 'Widgets' },
        { value: 'workflow', label: 'Workflows' },
    ];
    return (<>
      <div className="mt-marketplace-controls">
        <div className="mt-marketplace-type-pills" role="group" aria-label="Filter marketplace by type">
          {browseTypeFilters.map(filter => (<button key={filter.value} type="button" className={`mt-marketplace-type-pill${typeFilter === filter.value ? ' mt-marketplace-type-pill--active' : ''}`} onClick={() => setTypeFilter(filter.value)}>
              {filter.label}
            </button>))}
        </div>
        <div className="mt-marketplace-search-filters">
          <input className="mt-marketplace-search" type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search by name or publisher" aria-label="Search marketplace by name or publisher" />
          {/* Set by clicking a publisher's name on a listing; there's no publisher dropdown. */}
          {publisherFilter && (<span className="mt-publisher-filter-chip">
              By {publisherFilter}
              <button type="button" aria-label={`Show listings from every publisher`} onClick={() => setSearchParams(browseParams({}, ALL_PUBLISHERS))}>×</button>
            </span>)}
        </div>
      </div>
      {browseFilteredTools.length === 0 ? (<div className="mt-empty-state">
          <div className="mt-empty-state__icon">🔍</div>
          <div className="mt-empty-state__title">No tools match these filters</div>
          <div className="mt-empty-state__subtitle">Try a different search, publisher or type.</div>
        </div>) : (<div className="mt-installed-grid">
        {browseFilteredTools.map(tool => (<MarketplaceCard key={tool.id} tool={tool} listing={listings.get(tool.id) ?? fallbackStoreListing(tool)} onViewDetail={() => setSearchParams(browseParams({ toolId: tool.id }))} installing={installingId === tool.id} installed={installedIds.has(tool.id)} onInstall={() => startInstall(tool)}/>))}
      </div>)}

      <AnimatePresence>
        {enableForAgentsTool?.apiIntegrationId && (<AgentEnablementModal toolName={enableForAgentsTool.name} integrationId={enableForAgentsTool.apiIntegrationId} onClose={() => setEnableForAgentsTool(null)}/>)}
      </AnimatePresence>

      <AnimatePresence>
        {pendingInstall && (<MarketplaceInstallModal key={pendingInstall.id} tool={pendingInstall} iconUrl={listings.get(pendingInstall.id)?.iconUrl} initialMode={pendingInstallMode} chooseAudience={isAgencyOwner ? { installedForMe: installedIds.has(pendingInstall.id), installedForAgency: !!pendingInstall.isAgencyInstalled, personalBlockedReason: personalBlock(pendingInstall) } : undefined} installing={installingId === pendingInstall.id} onCancel={() => setPendingInstall(null)} onConfirm={(values, connectionValues, chosen) => finishInstall(pendingInstall, values, connectionValues, chosen)}/>)}
      </AnimatePresence>

      <AnimatePresence>
      </AnimatePresence>

      <AnimatePresence>
        {toast && (<motion.div className={toast.type === 'success' ? 'success-toast' : 'error-toast'} initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.25 }}>
            <span>{toast.message}</span>
          </motion.div>)}
      </AnimatePresence>
    </>);
};
function toolAuthHeaders() {
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` };
}
interface ToolComment {
    id: string;
    authorName: string;
    message: string;
    // Set only on the note auto-posted alongside a Request Changes/Reject verdict -
    // lets this thread flag it as the official reason your submission was
    // returned, not an ordinary reply. Mirrors ReviewQueuePage's ReviewComment.
    relatedStatus?: 'changes_requested' | 'rejected' | null;
    createdAt: string;
}
function toolTimeAgo(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 60)
        return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24)
        return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}
function toolInitials(name: string) { return name.split(' ').map(p => p[0] ?? '').join('').toUpperCase().slice(0, 2); }
function toolAvatarColor(name: string) {
    const colors = ['#8b5cf6', '#14b8a6', '#f59e0b', '#3b82f6', '#ec4899', '#22d3ee'];
    let h = 0;
    for (let i = 0; i < name.length; i++)
        h = (h * 31 + name.charCodeAt(i)) & 0xffff;
    return colors[h % colors.length];
}
const PUBLISH_STATUS_CONFIG: Record<string, {
    label: string;
    color: string;
    bg: string;
    border: string;
}> = {
    pending: { label: '⏳ Pending Review', color: '#fbbf24', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' },
    approved: { label: '✓ Approved', color: '#34d399', bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.3)' },
    rejected: { label: '✗ Rejected', color: '#f87171', bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.28)' },
    changes_requested: { label: '⚠ Changes Requested', color: '#fb923c', bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.3)' },
    withdrawn: { label: 'Withdrawn', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', border: 'rgba(148,163,184,0.3)' },
};
const ShareModal: React.FC<{
    toolId: string;
    toolName: string;
    onClose: () => void;
}> = ({ toolId, toolName, onClose }) => {
    const [shares, setShares] = useState<Share[]>([]);
    const [loadingShares, setLoadingShares] = useState(true);
    const [scope, setScope] = useState<ShareScope>('agency');
    const [creating, setCreating] = useState(false);
    const [busyShareId, setBusyShareId] = useState<string | null>(null);
    const [copiedToken, setCopiedToken] = useState<string | null>(null);
    const [error, setError] = useState('');
    const loadShares = useCallback(() => {
        setLoadingShares(true);
        getShares(toolId)
            .then(setShares)
            .catch(() => setShares([]))
            .finally(() => setLoadingShares(false));
    }, [toolId]);
    useEffect(() => { loadShares(); }, [loadShares]);
    const shareUrl = (token: string) => `${window.location.origin}/share/${token}`;
    const handleCopy = (token: string) => {
        navigator.clipboard?.writeText(shareUrl(token)).catch(() => { });
        setCopiedToken(token);
        setTimeout(() => setCopiedToken(null), 2000);
    };
    const handleCreate = async () => {
        setCreating(true);
        setError('');
        try {
            await createShare(toolId, scope);
            loadShares();
        }
        catch (err: any) {
            setError(err.message || 'Failed to create share link');
        }
        finally {
            setCreating(false);
        }
    };
    const handleRevoke = async (shareId: string) => {
        setBusyShareId(shareId);
        try {
            await revokeShare(shareId);
            loadShares();
        }
        catch (err: any) {
            setError(err.message || 'Failed to revoke share link');
        }
        finally {
            setBusyShareId(null);
        }
    };
    const handleRegenerate = async (shareId: string) => {
        setBusyShareId(shareId);
        try {
            await regenerateShare(shareId);
            loadShares();
        }
        catch (err: any) {
            setError(err.message || 'Failed to regenerate share link');
        }
        finally {
            setBusyShareId(null);
        }
    };
    const activeShares = shares.filter(s => s.isActive);
    return (<motion.div className="mt-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} onClick={e => { if (e.target === e.currentTarget)
        onClose(); }}>
      <motion.div className="mt-modal" initial={{ opacity: 0, y: 12, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.97 }} transition={{ duration: 0.2 }}>
        <div className="mt-modal__header">
          <div className="mt-modal__header-info">
            <h3 className="mt-modal__title">Share "{toolName}"</h3>
          </div>
          <button type="button" className="mt-modal__close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <p className="mt-step-subtitle">
          Anyone who opens the link installs their own independent copy automatically — no separate
          import step. Works on drafts; sharing doesn't go through review.
        </p>

        {activeShares.length === 0 && (<div className="mt-form">
            <div className="mt-field mt-field--full">
              <label className="mt-label">Who can use this link?</label>
              <div className="mt-visibility-group" style={{ marginTop: 6 }}>
                <button type="button" className={`mt-visibility-btn ${scope === 'agency' ? 'mt-visibility-btn--active' : ''}`} onClick={() => setScope('agency')}>
                  Anyone in this agency with this link
                </button>
                <button type="button" className={`mt-visibility-btn ${scope === 'anyone' ? 'mt-visibility-btn--active' : ''}`} onClick={() => setScope('anyone')}>
                  Anyone with this link
                </button>
              </div>
            </div>
          </div>)}

        {error && <p style={{ color: '#f87171', fontSize: 12.5, margin: '4px 0' }}>{error}</p>}

        {loadingShares ? (<p className="mt-step-subtitle">Loading…</p>) : activeShares.length > 0 ? (<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {activeShares.map(s => (<div key={s.id} style={{
                    background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
                    borderRadius: 9, padding: '10px 13px', display: 'flex', flexDirection: 'column', gap: 8,
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span className="mt-badge mt-badge--visibility">
                    {s.scope === 'agency' ? 'Anyone in this agency' : 'Anyone with this link'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input readOnly value={shareUrl(s.token)} className="mt-input" style={{ fontSize: 12, flex: 1 }} onClick={e => (e.target as HTMLInputElement).select()}/>
                  <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => handleCopy(s.token)}>
                    {copiedToken === s.token ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
                <div className="mt-actions" style={{ marginTop: 0 }}>
                  <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" disabled={busyShareId === s.id} onClick={() => handleRegenerate(s.id)}>
                    {busyShareId === s.id ? 'Working…' : 'Regenerate'}
                  </button>
                  <button type="button" className="mt-btn mt-btn--secondary mt-btn--danger-outline mt-btn--sm" disabled={busyShareId === s.id} onClick={() => handleRevoke(s.id)}>
                    {busyShareId === s.id ? 'Working…' : 'Revoke'}
                  </button>
                </div>
              </div>))}
          </div>) : null}

        {activeShares.length === 0 && (<div className="mt-actions">
            <button type="button" className="mt-btn mt-btn--secondary" onClick={onClose} disabled={creating}>Cancel</button>
            <button type="button" className="mt-btn mt-btn--primary" onClick={handleCreate} disabled={creating}>
              {creating ? 'Creating…' : 'Create link'}
            </button>
          </div>)}
      </motion.div>
    </motion.div>);
};
// My Creations: the listing's store page - what installers see - with the creator's
// controls (Edit, Share) and a "Your listing" section: status, the review thread, and a
// nudge when the store page is still empty.
const ToolDetailPanel: React.FC<{
    tool: MyTool;
    onClose: () => void;
    onEdit: (tool: MyTool) => void;
}> = ({ tool, onClose, onEdit }) => {
    const typeInfo = TOOL_TYPES.find(t => t.type === tool.type)!;
    const displayIcon = tool.icon.trim() || <TypeGlyph type={tool.type}/>;
    const statusCfg = tool.publishStatus ? PUBLISH_STATUS_CONFIG[tool.publishStatus] : null;
    const [listing, setListing] = useState<StoreListing>(() => fallbackStoreListing(tool));
    useEffect(() => {
        let cancelled = false;
        getMarketplaceStore(tool.id).then(l => { if (!cancelled) setListing(l); }).catch(() => { });
        return () => { cancelled = true; };
    }, [tool.id]);
    const [comments, setComments] = useState<ToolComment[]>([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [commentsError, setCommentsError] = useState('');
    const [newComment, setNewComment] = useState('');
    const [sending, setSending] = useState(false);
    const feedRef = useRef<HTMLDivElement>(null);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    useEffect(() => {
        if (!tool.publishRequestId)
            return;
        setCommentsLoading(true);
        setCommentsError('');
        fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/ReviewQueue/${tool.publishRequestId}/comments`, { headers: toolAuthHeaders() })
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then(json => {
            const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
            setComments(list.map((c: any): ToolComment => ({
                id: String(c.id), authorName: c.authorName ?? 'Unknown',
                message: c.message ?? '', relatedStatus: c.relatedStatus ?? null,
                createdAt: c.createdAt ?? new Date().toISOString(),
            })));
        })
            .catch((status) => {
                setComments([]);
                setCommentsError(status === 403
                    ? "Couldn't load review comments - you may not have access to this thread."
                    : "Couldn't load review comments. Try again later.");
            })
            .finally(() => setCommentsLoading(false));
    }, [tool.publishRequestId]);
    useEffect(() => {
        if (feedRef.current)
            feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }, [comments]);
    const handleSend = async () => {
        const text = newComment.trim();
        if (!text || sending || !tool.publishRequestId)
            return;
        setSending(true);
        try {
            const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/ReviewQueue/${tool.publishRequestId}/comments`, {
                method: 'POST', headers: toolAuthHeaders(), body: JSON.stringify({ message: text }),
            });
            if (res.ok) {
                const data = await res.json();
                setComments(prev => [...prev, {
                        id: String(data.id ?? Date.now()), authorName: data.authorName ?? 'You',
                        message: text, createdAt: data.createdAt ?? new Date().toISOString(),
                    }]);
                setNewComment('');
            }
        }
        catch { }
        finally {
            setSending(false);
        }
    };
    const hasMedia = listing.screenshots.length > 0 || !!listing.video;
    const manage = (<section className="ms-section mt-inst-manage">
        <h2 className="ms-section__title">Your listing</h2>
        <div className="mt-detail-meta mt-inst-manage__meta">
          {statusCfg
                ? (<span style={{ padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: statusCfg.bg, border: `1px solid ${statusCfg.border}`, color: statusCfg.color }}>{statusCfg.label}</span>)
                : <span className="mt-badge mt-badge--status">{tool.visibility === 'published' ? 'Published' : 'Active'}</span>}
          <span className="mt-detail-dot">·</span>
          <span className="mt-badge mt-badge--visibility">{visLabel(tool.visibility)}</span>
          <span className="mt-detail-dot">·</span>
          <span className="mt-detail-dim">Created {formatDate(tool.createdAt)}</span>
        </div>
        {!hasMedia && (<div className="mt-listing-nudge">
            <span>No screenshots or video yet. A listing with them is easier to choose.</span>
            <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => onEdit(tool)}>Add screenshots</button>
          </div>)}
        {tool.publishRequestId ? (<>
            <h4 className="mt-detail-section-title">
              Review comments {comments.length > 0 && `(${comments.length})`}
            </h4>
            <div ref={feedRef} style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 12 }}>
              {commentsLoading ? (<p style={{ color: 'var(--text-tertiary)', fontSize: 12, textAlign: 'center', margin: '8px 0' }}>Loading…</p>) : commentsError ? (<p style={{ color: '#f87171', fontSize: 12, textAlign: 'center', margin: '8px 0' }}>{commentsError}</p>) : comments.length === 0 ? (<p style={{ color: 'var(--text-tertiary)', fontSize: 12, textAlign: 'center', margin: '8px 0' }}>No review comments yet.</p>) : comments.map(c => {
                const color = toolAvatarColor(c.authorName);
                // The note a reviewer attaches to Request Changes/Reject gets flagged here
                // too, so it doesn't read as an ordinary reply - this is the actual reason
                // your submission came back, not small talk in the thread.
                const verdictCfg = c.relatedStatus ? PUBLISH_STATUS_CONFIG[c.relatedStatus] : null;
                return (<div key={c.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <div style={{
                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                        background: color + '25', border: `1.5px solid ${color}55`,
                        color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 700,
                    }}>
                      {toolInitials(c.authorName)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                        <span style={{ color: 'var(--text-primary)', fontSize: 12, fontWeight: 600 }}>{c.authorName}</span>
                        <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>{toolTimeAgo(c.createdAt)}</span>
                        {verdictCfg && (<span style={{
                            padding: '1px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700,
                            background: verdictCfg.bg, border: `1px solid ${verdictCfg.border}`, color: verdictCfg.color,
                        }}>
                              {verdictCfg.label}
                            </span>)}
                      </div>
                      <div style={{
                        background: verdictCfg ? verdictCfg.bg : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${verdictCfg ? verdictCfg.border : 'rgba(255,255,255,0.07)'}`,
                        borderRadius: '0 8px 8px 8px', padding: '7px 10px',
                    }}>
                        <p style={{ color: 'var(--text-secondary)', fontSize: 12.5, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>{c.message}</p>
                      </div>
                    </div>
                  </div>);
            })}
            </div>

            
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <textarea value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSend();
        } }} placeholder="Reply to reviewer… (Ctrl+Enter to send)" rows={2} style={{
                flex: 1, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)',
                borderRadius: 9, color: 'var(--text-primary)', fontSize: 13, padding: '8px 11px',
                fontFamily: 'inherit', resize: 'none', outline: 'none', boxSizing: 'border-box',
            }}/>
              <button onClick={handleSend} disabled={!newComment.trim() || sending || !!commentsError} className="mt-btn mt-btn--primary" style={{ padding: '8px 14px', borderRadius: 9, flexShrink: 0, fontSize: 13, opacity: !newComment.trim() || sending || !!commentsError ? 0.45 : 1 }}>
                Send
              </button>
            </div>
          </>) : (<p className="mt-step-subtitle" style={{ margin: 0 }}>
            Submit this {typeInSentence(typeInfo.label)} for review to see reviewer comments here.
          </p>)}
      </section>);
    return (<motion.div className="mt-inst-detail-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} transition={{ duration: 0.2 }}>
      <button type="button" className="mt-inst-back-btn" onClick={onClose}>
        ← Back to My Creations
      </button>
      <StorePage mode="view" name={tool.name} emoji={displayIcon} iconUrl={listing.iconUrl} typeKey={tool.type} typeLabel={typeInfo.label} category={tool.category} visibilityLabel={visLabel(tool.visibility)} publisherName={publisherLabel(tool)} isOwnAgency={tool.isOwnAgency} groups={<GroupChips groups={tool.groups} type={tool.type}/>} updatedAt={tool.updatedAt ?? tool.createdAt} description={listing.storeDescription ?? tool.description} listing={listing} includes={<ListingIncludes tool={tool} listing={listing}/>} whereItGoes={whereItGoes({ type: tool.type, groups: tool.groups, surfaces: tool.widgetManifest?.surfaces })} canReview={false} manage={manage} actions={<div className="mt-inst-detail-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="mt-btn mt-btn--primary" onClick={() => onEdit(tool)}>Edit</button>
            {tool.type === 'skill' && (<button type="button" className="mt-btn mt-btn--secondary" onClick={() => setShareModalOpen(true)}>Share</button>)}
          </div>}/>
      <AnimatePresence>
        {shareModalOpen && (<ShareModal toolId={tool.id} toolName={tool.name} onClose={() => setShareModalOpen(false)}/>)}
      </AnimatePresence>
    </motion.div>);
};
type CreationsTab = 'all' | Visibility;
function visLabel(v: Visibility): string {
    if (v === 'just_me')
        return 'Personal';
    if (v === 'my_agency')
        return 'My Agency';
    return 'Published';
}
interface CreationsViewProps {
    tools: MyTool[];
    loading: boolean;
    onDelete: (id: string) => void;
    onEdit: (tool: MyTool) => void;
    onUpdate: (id: string, changes: Partial<MyTool>) => void;
    onInstall: (tool: MyTool) => void;
    onInstallForAgency: (tool: MyTool) => void;
    installingId: string | null;
    onUninstallForAgency: (tool: MyTool) => void;
    onViewDetail: (tool: MyTool) => void;
    onRefetchTools: () => void;
}
const CreationsView: React.FC<CreationsViewProps> = ({ tools, loading, onDelete, onEdit, onUpdate, onInstall, onInstallForAgency, installingId, onUninstallForAgency, onViewDetail, onRefetchTools }) => {
    const isAgencyOwner = readIsAgencyOwner();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    // What the signed-in person already has installed, so the row menu offers Open instead of
    // an Install that would only duplicate it. Refreshed after each install and save.
    const [installedIds, setInstalledIds] = useState<Set<string>>(new Set());
    const loadInstalledIds = useCallback(() => {
        apiFetchInstalledTools()
            .then(list => setInstalledIds(new Set(list.map(t => t.id))))
            .catch(() => { });
    }, []);
    useEffect(() => {
        if (!installingId)
            loadInstalledIds();
    }, [installingId, tools, loadInstalledIds]);
    useWidgetInstallsChanged(loadInstalledIds);
    const [activeTab, setActiveTab] = useState<CreationsTab>('all');
    // ?type=integration (e.g. from the old integrations list URL) pre-selects that filter.
    const [typeFilter, setTypeFilter] = useState<'all' | ToolType>(() => {
        const fromUrl = searchParams.get('type');
        return TOOL_TYPES.some(t => t.type === fromUrl) ? fromUrl as ToolType : 'all';
    });
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [menuPos, setMenuPos] = useState<{
        top: number;
        left: number;
    } | null>(null);
    const [menuBtnRect, setMenuBtnRect] = useState<{
        top: number;
        bottom: number;
        right: number;
    } | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
    const [shareToolId, setShareToolId] = useState<string | null>(null);
    const [shareAgentId, setShareAgentId] = useState<string | null>(null);
    const [shareResolving, setShareResolving] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const openShareModal = async (tool: MyTool) => {
        closeMenu();
        setShareToolId(tool.id);
        if (tool.type !== 'agent')
            return;
        setShareAgentId(null);
        if (!tool.agentShareToken)
            return;
        setShareResolving(true);
        try {
            const { source_agent_id } = await resolveAgentShare(tool.agentShareToken);
            setShareAgentId(source_agent_id ?? null);
        }
        catch {
            setShareAgentId(null);
        }
        finally {
            setShareResolving(false);
        }
    };
    const openMenu = useCallback((id: string, btn: HTMLButtonElement) => {
        const rect = btn.getBoundingClientRect();
        setMenuBtnRect({ top: rect.top, bottom: rect.bottom, right: rect.right });
        setMenuPos({ top: rect.bottom + 4, left: rect.right - 192 });
        setOpenMenuId(id);
    }, []);
    const closeMenu = useCallback(() => {
        setOpenMenuId(null);
        setMenuPos(null);
        setMenuBtnRect(null);
    }, []);
    // The menu's height depends on which items apply to the selected tool, so
    // it can't be predicted before it renders. Measure it once mounted and,
    // if opening below the button would run past the viewport (the case for
    // any row near the bottom of a scrolled page), flip it above the button
    // instead of leaving it rendered off-screen.
    useLayoutEffect(() => {
        if (!openMenuId || !menuBtnRect || !menuRef.current)
            return;
        const margin = 8;
        const menuHeight = menuRef.current.offsetHeight;
        if (menuBtnRect.bottom + 4 + menuHeight > window.innerHeight - margin) {
            setMenuPos(prev => prev ? { ...prev, top: Math.max(margin, menuBtnRect.top - menuHeight - 4) } : prev);
        }
    }, [openMenuId, menuBtnRect]);
    useEffect(() => {
        if (!openMenuId)
            return;
        const handler = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node))
                closeMenu();
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [openMenuId, closeMenu]);
    if (loading) {
        return (<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <SkeletonLoader height={70} lines={1}/>
        {Array.from({ length: 5 }).map((_, i) => (<SkeletonLoader key={i} height={48} lines={1}/>))}
      </div>);
    }
    if (tools.length === 0) {
        return (<div className="mt-empty-state">
        <div className="mt-empty-state__icon">🛠️</div>
        <div className="mt-empty-state__title">No tools yet</div>
        <div className="mt-empty-state__subtitle">Create your first tool to see it here</div>
        <button type="button" className="mt-btn mt-btn--primary" onClick={() => setSearchParams({ view: 'create' })}>
          Create New
        </button>
      </div>);
    }
    const counts = {
        total: tools.length,
        personal: tools.filter(t => t.visibility === 'just_me').length,
        agency: tools.filter(t => t.visibility === 'my_agency').length,
        published: tools.filter(t => t.visibility === 'published').length,
    };
    const filteredTools = (activeTab === 'all' ? tools : tools.filter(t => t.visibility === activeTab))
        .filter(t => typeFilter === 'all' || t.type === typeFilter);
    const typesPresent = Array.from(new Set(tools.map(t => t.type)));
    const CAT_TABS: {
        key: CreationsTab;
        label: string;
        count: number;
    }[] = [
        { key: 'all', label: 'All', count: counts.total },
        { key: 'just_me', label: 'Personal', count: counts.personal },
        { key: 'my_agency', label: 'Agency', count: counts.agency },
        { key: 'published', label: 'Published', count: counts.published },
    ];
    return (<div className="mt-creations-layout">
      <div className="mt-stat-cards">
        <div className="mt-stat-card mt-stat-card--total">
          <span className="mt-stat-card__value">{counts.total}</span>
          <span className="mt-stat-card__label">Total</span>
        </div>
        <div className="mt-stat-card mt-stat-card--personal">
          <span className="mt-stat-card__value">{counts.personal}</span>
          <span className="mt-stat-card__label">📝 Personal</span>
        </div>
        <div className="mt-stat-card mt-stat-card--agency">
          <span className="mt-stat-card__value">{counts.agency}</span>
          <span className="mt-stat-card__label">🏢 Agency</span>
        </div>
        <div className="mt-stat-card mt-stat-card--published">
          <span className="mt-stat-card__value">{counts.published}</span>
          <span className="mt-stat-card__label">🌐 Published</span>
        </div>
      </div>

      <div className="mt-table-section">
        <div className="mt-cat-tabs">
          {CAT_TABS.map(tab => (<button key={tab.key} type="button" className={`mt-cat-tab ${activeTab === tab.key ? 'mt-cat-tab--active' : ''}`} onClick={() => setActiveTab(tab.key)}>
              {tab.label}
              <span className="mt-cat-tab__count">{tab.count}</span>
            </button>))}
          {typesPresent.length > 1 && (<select className="mt-type-filter" value={typeFilter} onChange={e => setTypeFilter(e.target.value as 'all' | ToolType)} aria-label="Filter by type">
              <option value="all">All Types</option>
              {typesPresent.map(t => (<option key={t} value={t}>{TOOL_TYPES.find(ti => ti.type === t)!.label}</option>))}
            </select>)}
        </div>

        <div className="mt-table-wrapper">
          {filteredTools.length === 0 ? (<div className="mt-table-empty">No tools in this category.</div>) : (<table className="mt-table">
              <thead>
                <tr>
                  <th>Name</th><th>Type</th><th>Visibility</th><th>Status</th>
                  <th>Date</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filteredTools.map(tool => {
                const typeInfo = TOOL_TYPES.find(t => t.type === tool.type)!;
                const displayIcon = tool.icon.trim() || <TypeGlyph type={tool.type}/>;
                return (<tr key={tool.id} style={{ cursor: 'pointer' }} onClick={() => onViewDetail(tool)}>
                      <td>
                        <div className="mt-table-name-cell">
                          <ListingIcon iconUrl={tool.iconUrl} emoji={displayIcon} className="mt-table-icon" alt={`${tool.name} icon`}/>
                          <div>
                            <div className="mt-table-name">{tool.name}</div>
                            {tool.category && <div className="mt-table-category">{tool.category}</div>}
                            <GroupChips groups={tool.groups} type={tool.type}/>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`mt-badge mt-badge--type mt-badge--type-${tool.type}`}>{typeInfo.label}</span>
                        {tool.type === 'widget' && tool.widgetVersion && (<span className="mt-badge mt-badge--category" style={{ marginLeft: 6 }}>v{tool.widgetVersion}</span>)}
                      </td>
                      <td><span className="mt-badge mt-badge--visibility">{visLabel(tool.visibility)}</span></td>
                      <td>{(() => {
                        if (tool.type === 'integration' && tool.integrationStatus === 'draft')
                            return <span className="mt-badge mt-badge--status mt-badge--draft">Draft</span>;
                        if (tool.visibility === 'published')
                            return <span className="mt-badge mt-badge--status mt-badge--published">Published</span>;
                        if (tool.publishStatus === 'pending')
                            return <span className="mt-badge mt-badge--status mt-badge--pending">⏳ Pending Review</span>;
                        if (tool.publishStatus === 'changes_requested')
                            return <span className="mt-badge mt-badge--status mt-badge--changes">Changes Requested</span>;
                        if (tool.publishStatus === 'rejected')
                            return <span className="mt-badge mt-badge--status mt-badge--rejected">Rejected</span>;
                        return <span className="mt-badge mt-badge--status">Active</span>;
                    })()}</td>
                      <td><span className="mt-table-date">{formatDate(tool.createdAt)}</span></td>
                      <td>
                        <button type="button" className="mt-kebab-btn" aria-label="Actions" onClick={e => { e.stopPropagation(); openMenu(tool.id, e.currentTarget); }}>
                          ⋯
                        </button>
                      </td>
                    </tr>);
            })}
              </tbody>
            </table>)}
        </div>
      </div>

      {openMenuId && menuPos && ReactDOM.createPortal(<div ref={menuRef} className="mt-action-menu" style={{ top: menuPos.top, left: menuPos.left }}>
          {(() => {
                const tool = tools.find(t => t.id === openMenuId);
                if (!tool)
                    return null;
                return (<>
                <button type="button" className="mt-action-menu__item" onClick={() => { onEdit(tool); closeMenu(); }}>
                  ✏️ Edit
                </button>
                {tool.type === 'widget' && installedIds.has(tool.id) && (<button type="button" className="mt-action-menu__item" onClick={() => { closeMenu(); navigate(`/widgets/${tool.id}`); }}>
                    ▶️ Open
                  </button>)}
                {tool.type !== 'agent' && !installedIds.has(tool.id) && !(tool.type === 'integration' && tool.integrationStatus === 'draft') && (<button type="button" className="mt-action-menu__item" disabled={installingId === tool.id} onClick={() => { onInstall(tool); closeMenu(); }} title="Install this for your own AI agent">
                    {installingId === tool.id ? 'Installing…' : '▶️ Install'}
                  </button>)}
                {/* Widgets can't be shared by link (the server refuses) — publish to the agency instead. */}
                {tool.type !== 'widget' && !(tool.type === 'integration' && tool.integrationStatus === 'draft') && (<button type="button" className="mt-action-menu__item" onClick={() => openShareModal(tool)}>
                    🔗 Share
                  </button>)}
                <div className="mt-action-menu__divider"/>
                <button type="button" className="mt-action-menu__item mt-action-menu__item--danger" onClick={() => { setConfirmDeleteId(tool.id); closeMenu(); }}>
                  🗑️ Delete
                </button>
              </>);
            })()}
        </div>, document.body)}

      <AnimatePresence>
        {confirmDeleteId && (() => {
            const tool = tools.find(t => t.id === confirmDeleteId);
            if (!tool)
                return null;
            return (<motion.div className="mt-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} onClick={e => { if (e.target === e.currentTarget)
                setConfirmDeleteId(null); }}>
              <motion.div className="mt-modal" initial={{ opacity: 0, y: 12, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.97 }} transition={{ duration: 0.2 }}>
                <div className="mt-modal__header">
                  <div className="mt-modal__header-info">
                    <div className="mt-modal__icon">🗑️</div>
                    <h3 className="mt-modal__title">Delete "{tool.name}"?</h3>
                  </div>
                  <button type="button" className="mt-modal__close" onClick={() => setConfirmDeleteId(null)} aria-label="Close">×</button>
                </div>
                <p className="mt-step-subtitle">This can't be undone.</p>
                <div className="mt-actions">
                  <button type="button" className="mt-btn mt-btn--secondary" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                  <button type="button" className="mt-btn mt-btn--secondary mt-btn--danger-outline" onClick={() => { onDelete(tool.id); setConfirmDeleteId(null); }}>
                    Delete
                  </button>
                </div>
              </motion.div>
            </motion.div>);
        })()}
      </AnimatePresence>

      {shareToolId && (() => {
            const tool = tools.find(t => t.id === shareToolId);
            if (!tool)
                return null;
            const closeShare = () => { setShareToolId(null); setShareAgentId(null); onRefetchTools(); };
            if (tool.type === 'agent') {
                if (shareResolving || !shareAgentId)
                    return null;
                return (<AgentShareMenuModal agentId={shareAgentId} agentName={tool.name} isAgencyOwner={isAgencyOwner} existingTool={{ id: tool.id, visibility: tool.visibility, publishStatus: tool.publishStatus, isAgencyInstalled: tool.isAgencyInstalled }} onShareWithAgency={() => onUpdate(tool.id, { visibility: 'my_agency' })} onUninstallForAgency={() => onUninstallForAgency(tool)} onClose={closeShare}/>);
            }
            return (<ToolShareMenuModal tool={{ id: tool.id, name: tool.name, type: tool.type as 'skill' | 'mcp_server' | 'integration', visibility: tool.visibility, publishStatus: tool.publishStatus, isAgencyInstalled: tool.isAgencyInstalled }} isAgencyOwner={isAgencyOwner} onShareWithAgency={() => onUpdate(tool.id, { visibility: 'my_agency' })} onInstallForAgency={() => onInstallForAgency(tool)} onUninstallForAgency={() => onUninstallForAgency(tool)} onClose={closeShare}/>);
        })()}
    </div>);
};
const MyToolsPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const view = (searchParams.get('view') as WizardView) || 'creations';
    const location = useLocation();
    const [tools, setTools] = useState<MyTool[]>([]);
    const [loadingTools, setLoadingTools] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const pageRef = useRef<HTMLDivElement>(null);
    const [selectedType, setSelectedType] = useState<ToolType | null>(null);
    const [name, setName] = useState('');
    const [category, setCategory] = useState(CATEGORIES[0]);
    const [icon, setIcon] = useState('');
    const [description, setDescription] = useState('');
    const [skills, setSkills] = useState('');
    const [skillContent, setSkillContent] = useState('');
    const [kbFiles, setKbFiles] = useState<File[]>([]);
    const [visibility, setVisibility] = useState<Visibility>('just_me');
    const [existingKbFileNames, setExistingKbFileNames] = useState<string[]>([]);
    const [existingSkillKbFiles, setExistingSkillKbFiles] = useState<{
        id: string;
        filename: string;
    }[]>([]);
    const [mcpUrl, setMcpUrl] = useState('');
    const [mcpHeaders, setMcpHeaders] = useState<McpHeaderField[]>([]);
    const [agentShareToken, setAgentShareToken] = useState('');
    const [agentMode, setAgentMode] = useState<'new' | 'existing'>('existing');
    const [builtAgentId, setBuiltAgentId] = useState('');
    const [agentId, setAgentId] = useState('');
    const [agentPreview, setAgentPreview] = useState<AgentShareSnapshot | null>(null);
    const [agentFrozenSnapshot, setAgentFrozenSnapshot] = useState<AgentShareSnapshot | null>(null);
    // ── API Integration flow ──
    // The integration id and wizard step live in the URL (?view=create&type=integration
    // &id=<id>&step=<step>), so a refresh or a shared link resumes the same draft.
    const urlType = searchParams.get('type');
    const urlIntegrationId = searchParams.get('id');
    const integrationMode = view === 'create' && urlType === 'integration';
    const integrationStep = asIntegrationStep(searchParams.get('step'));
    // ── Publish wizard (every other type): its place is in the URL too (see WIZARD_STEP_KEYS).
    const wizardStep = view === 'create' && !integrationMode ? wizardStepFromParam(searchParams.get('step')) : 1;
    const urlWizardType = view === 'create' && !integrationMode ? asWizardType(urlType) : null;
    const urlEditId = view === 'create' && !integrationMode ? searchParams.get('edit') : null;
    const urlBuiltAgentId = view === 'create' && !integrationMode ? searchParams.get('agent') : null;
    const [editLoading, setEditLoading] = useState(false);
    // A step past Type needs a type; a bare ?step=info shows Type instead of a blank page.
    const shownStep = selectedType ? wizardStep : 1;
    // For one render after a refresh / Back / pasted link the URL is ahead of the state (the
    // restore effect fills it in): show the loading line rather than flash the Type step.
    const wizardCatchingUp = view === 'create' && !integrationMode
        && ((!!urlEditId && editingId !== urlEditId) || (!!urlWizardType && wizardStep > 1 && selectedType !== urlWizardType));
    const wizardBusy = editLoading || wizardCatchingUp;
    // Each step starts at the top: Next sits at the bottom of a long form, so the next step
    // otherwise opens scrolled past its content and looks blank.
    useEffect(() => {
        pageRef.current?.scrollTo({ top: 0 });
    }, [wizardStep]);
    /** Moves the wizard to `step` (a new history entry, so Back returns to the step before).
     *  `extra` sets (string) or removes (null) other params, e.g. the type on leaving step 1. */
    const goWizardStep = useCallback((step: number, extra: Record<string, string | null> = {}) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('view', 'create');
            const stepParam = wizardStepParam(step);
            if (stepParam)
                next.set('step', stepParam);
            else
                next.delete('step');
            Object.entries(extra).forEach(([key, value]) => value == null ? next.delete(key) : next.set(key, value));
            return next;
        });
    }, [setSearchParams]);
    const [integrationDetail, setIntegrationDetail] = useState<IntegrationDetail | null>(null);
    const [integrationWarnings, setIntegrationWarnings] = useState<string[]>([]);
    const [connectSettings, setConnectSettings] = useState<ConnectSettings | null>(null);
    // The integration's listing is Global right now (so changes to what it calls need a new review).
    const [integrationIsGlobal, setIntegrationIsGlobal] = useState(false);
    const [integrationLoadError, setIntegrationLoadError] = useState<string | null>(null);
    const [integrationDirty, setIntegrationDirty] = useState(false);
    const [importDirty, setImportDirty] = useState(false);
    const [publishError, setPublishError] = useState<string | null>(null);
    const [publishWarnings, setPublishWarnings] = useState<string[]>([]);
    const [creationEnableTool, setCreationEnableTool] = useState<InstallableTool | null>(null);
    const integrationUnsaved = integrationMode && (integrationDirty || importDirty);
    const [widgetFile, setWidgetFile] = useState<File | null>(null);
    const [widgetLocalHtml, setWidgetLocalHtml] = useState<string | null>(null);
    const [existingWidgetManifest, setExistingWidgetManifest] = useState<WidgetManifest | null>(null);
    const [existingWidgetVersion, setExistingWidgetVersion] = useState('');
    const [widgetConnections, setWidgetConnections] = useState<WidgetConnection[]>([]);
    const [widgetSettings, setWidgetSettings] = useState<WidgetSettingsState>(defaultWidgetSettings());
    // Store page content (icon, media, store description, release notes) - kept with the
    // listing in storeMock until it has a back end.
    const [storeDraft, setStoreDraft] = useState<StoreDraft>(emptyStoreDraft());
    const [iconError, setIconError] = useState<string | null>(null);
    const handleIconFile = useCallback(async (file: File) => {
        setIconError(null);
        try {
            const iconUrl = await iconFromFile(file);
            setStoreDraft(d => ({ ...d, iconUrl }));
        }
        catch (err) {
            setIconError(err instanceof Error ? err.message : "Couldn't use that image.");
        }
    }, []);
    const [groups, setGroups] = useState<string[]>([]);
    // A new item starts from its type's default groups (what that type did before groups existed).
    const selectType = useCallback((type: ToolType | null) => {
        setSelectedType(type);
        if (type)
            setGroups(defaultGroups(type));
    }, []);
    useEffect(() => {
        if (view !== 'creations')
            return;
        setLoadingTools(true);
        apiFetchCreations()
            .then(setTools)
            .catch(() => setTools([]))
            .finally(() => setLoadingTools(false));
    }, [view]);
    // Clears the wizard: on leaving Create New, and when the URL starts a new listing while an
    // edit is still loaded.
    const resetWizard = useCallback(() => {
        setEditingId(null);
        setSelectedType(null);
        setName('');
        setCategory(CATEGORIES[0]);
        setIcon('');
        setDescription('');
        setSkills('');
        setSkillContent('');
        setKbFiles([]);
        setExistingKbFileNames([]);
        setExistingSkillKbFiles([]);
        setVisibility('just_me');
        setMcpUrl('');
        setMcpHeaders([]);
        setAgentShareToken('');
        setAgentId('');
        setAgentMode('existing');
        setBuiltAgentId('');
        setAgentPreview(null);
        setAgentFrozenSnapshot(null);
        setIntegrationDetail(null);
        setIntegrationWarnings([]);
        setConnectSettings(null);
        setIntegrationLoadError(null);
        setIntegrationDirty(false);
        setImportDirty(false);
        setPublishError(null);
        setPublishWarnings([]);
        setWidgetFile(null);
        setWidgetLocalHtml(null);
        setExistingWidgetManifest(null);
        setExistingWidgetVersion('');
        setWidgetConnections([]);
        setWidgetSettings(defaultWidgetSettings());
        setStoreDraft(emptyStoreDraft());
        setIconError(null);
        setGroups([]);
    }, []);
    useEffect(() => {
        if (view !== 'create')
            resetWizard();
    }, [view, resetWizard]);
    const goIntegrationStep = useCallback((step: IntegrationStepKey, id?: number) => {
        const integrationId = id ?? integrationDetail?.integration.id;
        setSearchParams({ view: 'create', type: 'integration', ...(integrationId ? { id: String(integrationId) } : {}), step });
    }, [integrationDetail, setSearchParams]);
    // Fill the shared Info fields from an integration and its My Tools row.
    const applyIntegrationInfo = useCallback((detail: IntegrationDetail, row: MyTool | null, isNew: boolean) => {
        const { integration } = detail;
        setName(integration.name);
        setDescription(integration.description || '');
        setIcon(integration.icon || '🔌');
        setCategory(isNew ? CATEGORIES[0] : (integration.category || CATEGORIES[0]));
        setSkills(row?.skills || '');
        setVisibility(row?.visibility ?? 'just_me');
        setIntegrationIsGlobal(row?.visibility === 'published');
        setEditingId(isNew ? null : (row?.id ?? null));
        setConnectSettings(connectSettingsFrom(integration));
    }, []);
    const loadStoreDraft = useCallback(async (toolId: string) => {
        try {
            const listing = await getMarketplaceStore(toolId);
            const lastVersion = listing.releases[0]?.version || '1.0.0';
            const [major, minor] = lastVersion.split('.').map(n => parseInt(n, 10) || 0);
            setStoreDraft({
                iconUrl: listing.iconUrl,
                videoUrl: listing.video?.kind === 'embed' ? listing.video.url : undefined,
                screenshots: listing.screenshots,
                storeDescription: listing.storeDescription,
                lastVersion,
                version: listing.pendingVersion || `${major}.${minor + 1}.0`,
                releaseNotes: listing.pendingReleaseNotes || '',
            });
        } catch {
            setStoreDraft(emptyStoreDraft());
        }
    }, []);
    const loadIntegration = useCallback(async (id: number) => {
        setIntegrationLoadError(null);
        setIntegrationDetail(null);
        try {
            const [detail, mine] = await Promise.all([getIntegration(id), apiFetchMyTools().catch(() => [] as MyTool[])]);
            // An Agency Admin editing a colleague's integration won't find it in their own
            // creations; read its listing directly so visibility and capabilities carry over.
            const myToolsId = detail.integration.my_tools_id;
            const row = mine.find(t => t.apiIntegrationId === id)
                ?? (myToolsId ? await apiFetchMyTool(myToolsId).catch(() => null) : null);
            setIntegrationDetail(detail);
            setIntegrationWarnings([]);
            applyIntegrationInfo(detail, row, false);
            if (row)
                await loadStoreDraft(row.id);
            setIntegrationDirty(false);
        }
        catch (err: unknown) {
            setIntegrationLoadError(err instanceof Error ? err.message : "Couldn't load this integration.");
        }
    }, [applyIntegrationInfo, loadStoreDraft]);
    useEffect(() => {
        if (!integrationMode)
            return;
        setSelectedType('integration');
        if (urlIntegrationId && integrationDetail?.integration.id !== Number(urlIntegrationId) && !integrationLoadError) {
            loadIntegration(Number(urlIntegrationId));
        }
    }, [integrationMode, urlIntegrationId]);
    useEffect(() => {
        if (!integrationUnsaved)
            return;
        const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [integrationUnsaved]);
    const confirmLeaveIntegration = useCallback(() => {
        if (!integrationUnsaved)
            return true;
        if (!window.confirm('You have unsaved changes to this API integration. Leave without saving them?'))
            return false;
        setIntegrationDirty(false);
        setImportDirty(false);
        return true;
    }, [integrationUnsaved]);
    // Info and Connect edits are saved on publish; anything typed before then is unsaved.
    const trackIntegration = <T,>(setter: (v: T) => void) => (v: T) => {
        setter(v);
        if (integrationMode)
            setIntegrationDirty(true);
    };
    useEffect(() => {
        const prefillAgent = (location.state as {
            prefillAgent?: {
                agentId: string;
            };
        } | null)?.prefillAgent;
        if (view === 'create' && prefillAgent) {
            selectType('agent');
            setAgentId(prefillAgent.agentId);
            // One navigation: moves to Setup and drops the prefill state, so a refresh doesn't re-apply it.
            setSearchParams({ view: 'create', type: 'agent', step: wizardStepParam(2)! }, { replace: true, state: null });
        }
    }, [view, location.state, selectType, setSearchParams]);
    useEffect(() => {
        const prefillSkill = (location.state as {
            prefillSkill?: {
                name: string;
                skillContent: string;
            };
        } | null)?.prefillSkill;
        if (view === 'create' && prefillSkill) {
            selectType('skill');
            setName(prefillSkill.name);
            setSkillContent(prefillSkill.skillContent);
            setDescription(extractSkillDescription(prefillSkill.skillContent) || '');
            setSearchParams({ view: 'create', type: 'skill', step: wizardStepParam(2)! }, { replace: true, state: null });
        }
    }, [view, location.state, selectType, setSearchParams]);
    // Fills the wizard from a saved listing: from Edit, or on a refresh / pasted link with ?edit=<id>.
    const populateFromTool = useCallback(async (tool: MyTool) => {
        await loadStoreDraft(tool.id);
        setEditingId(tool.id);
        setSelectedType(tool.type);
        setGroups((tool.groups ?? defaultGroups(tool.type)).filter(g => !isSurfaceGroup(g)));
        setName(tool.name);
        setCategory(tool.category || CATEGORIES[0]);
        setIcon(tool.icon);
        setDescription(tool.description);
        setSkills(tool.skills);
        setSkillContent(tool.skillContent ?? '');
        setKbFiles([]);
        setExistingKbFileNames(tool.kbFileNames);
        setExistingSkillKbFiles([]);
        if (tool.type === 'skill') {
            apiGetSkillKbFiles(tool.id).then(setExistingSkillKbFiles).catch(() => setExistingSkillKbFiles([]));
        }
        setVisibility(tool.visibility);
        setMcpUrl(tool.mcpUrl ?? '');
        setMcpHeaders(tool.mcpHeaders ?? []);
        setAgentShareToken(tool.agentShareToken ?? '');
        setAgentId('');
        setAgentMode('existing');
        setBuiltAgentId('');
        setAgentPreview(null);
        setAgentFrozenSnapshot(null);
        setWidgetFile(null);
        setWidgetLocalHtml(null);
        setExistingWidgetManifest(tool.widgetManifest ?? null);
        setExistingWidgetVersion(tool.widgetVersion ?? '');
        setWidgetConnections(tool.widgetManifest?.connections ?? []);
        {
            const manifest = tool.widgetManifest;
            setWidgetSettings({
                version: tool.widgetVersion ?? manifest?.version ?? '',
                surfaces: ((manifest?.surfaces?.length ? manifest.surfaces : ['page']) as WidgetSurface[]),
                minHeight: manifest?.minHeight != null ? String(manifest.minHeight) : '',
                autoResize: manifest?.autoResize !== false,
            });
        }
        if (tool.type === 'agent' && tool.agentShareToken) {
            resolveAgentShare(tool.agentShareToken).then(({ source_agent_id, snapshot }) => {
                setAgentId(source_agent_id ?? '');
                setAgentFrozenSnapshot(snapshot);
            }).catch(() => { });
        }
    }, [loadStoreDraft]);
    const handleEdit = async (tool: MyTool) => {
        if (tool.type === 'integration') {
            await loadStoreDraft(tool.id);
            if (!tool.apiIntegrationId) {
                setPublishToast({ type: 'error', message: "This integration hasn't finished being set up." });
                setTimeout(() => setPublishToast(null), 4000);
                return;
            }
            setSearchParams({ view: 'create', type: 'integration', id: String(tool.apiIntegrationId), step: 'endpoints' });
            return;
        }
        await populateFromTool(tool);
        setSearchParams({ view: 'create', type: tool.type, step: wizardStepParam(2)!, edit: tool.id });
    };
    const handleUpdate = useCallback(async (id: string, changes: Partial<MyTool>) => {
        const tool = tools.find(t => t.id === id);
        if (!tool)
            return;
        const merged = { ...tool, ...changes };
        setTools(prev => prev.map(t => t.id === id ? merged : t));
        try {
            await apiUpsertTool({
                id: Number(id),
                type: TOOL_TYPE_TO_INT[merged.type],
                name: merged.name,
                category: merged.category,
                icon: merged.icon,
                description: merged.description,
                skills: merged.skills,
                kbFileNames: merged.kbFileNames,
                visibility: VISIBILITY_TO_INT[merged.visibility],
            });
        }
        catch {
            setTools(prev => prev.map(t => t.id === id ? tool : t));
        }
    }, [tools]);
    const [selectedTool, setSelectedTool] = useState<MyTool | null>(null);
    const [publishToast, setPublishToast] = useState<{
        type: 'success' | 'error';
        message: string;
    } | null>(null);
    // ── Wizard state from the URL + the tab's draft (refresh, Back/Forward, pasted links) ──
    const applyWizardDraft = useCallback((draft: WizardDraft) => {
        setName(draft.name);
        setCategory(draft.category);
        setIcon(draft.icon);
        setDescription(draft.description);
        setSkills(draft.skills);
        setSkillContent(draft.skillContent);
        setVisibility(draft.visibility);
        setMcpUrl(draft.mcpUrl);
        setMcpHeaders(draft.mcpHeaders);
        setAgentShareToken(draft.agentShareToken);
        setAgentId(draft.agentId);
        setAgentMode(draft.agentMode);
        setGroups(draft.groups);
        setWidgetSettings(draft.widgetSettings);
        setStoreDraft(draft.storeDraft);
        if (draft.fileNames.length > 0) {
            setPublishToast({ type: 'error', message: `Files aren't kept when the page reloads. Add them again: ${draft.fileNames.join(', ')}` });
            setTimeout(() => setPublishToast(null), 6000);
        }
    }, []);
    const editLoadRef = useRef<string | null>(null);
    useEffect(() => {
        if (view !== 'create' || integrationMode)
            return;
        if (urlEditId) {
            if (editingId === urlEditId || editLoadRef.current === urlEditId)
                return;
            editLoadRef.current = urlEditId;
            setEditLoading(true);
            const id = Number(urlEditId);
            (Number.isInteger(id) && id > 0 ? apiFetchMyTool(id) : Promise.resolve(null))
                .then(async tool => {
                    if (!tool || tool.type === 'integration')
                        throw new Error('not editable here');
                    await populateFromTool(tool);
                    const draft = readWizardDraft(wizardDraftKey(tool.id, tool.type));
                    if (draft)
                        applyWizardDraft(draft);
                })
                .catch(() => {
                    setPublishToast({ type: 'error', message: "Couldn't open that listing for editing." });
                    setTimeout(() => setPublishToast(null), 4000);
                    setSearchParams({ view: 'creations' }, { replace: true });
                })
                .finally(() => {
                    editLoadRef.current = null;
                    setEditLoading(false);
                });
            return;
        }
        // The URL is a new listing but the wizard still holds an edit (e.g. "+ Create New"
        // while editing): start clean, so saving can't overwrite the listing that was open.
        if (editingId) {
            resetWizard();
            return;
        }
        // A new listing's type comes back from the URL after a refresh, Back or Forward.
        if (urlWizardType && urlWizardType !== selectedType && (wizardStep > 1 || !selectedType)) {
            selectType(urlWizardType);
            const draft = readWizardDraft(wizardDraftKey(null, urlWizardType));
            if (draft)
                applyWizardDraft(draft);
        }
        if (urlBuiltAgentId && urlBuiltAgentId !== builtAgentId) {
            setAgentMode('new');
            setBuiltAgentId(urlBuiltAgentId);
        }
    }, [view, integrationMode, urlEditId, urlWizardType, urlBuiltAgentId, wizardStep, selectedType, editingId, builtAgentId,
        populateFromTool, applyWizardDraft, resetWizard, selectType, setSearchParams]);
    // Keep what's been typed, per tab, until the listing is saved (cleared in handleSave).
    useEffect(() => {
        if (view !== 'create' || integrationMode || !selectedType || wizardStep < 2 || editLoading)
            return;
        // Mid-restore (the URL moved but the state hasn't caught up yet): don't overwrite the draft.
        if (urlEditId ? editingId !== urlEditId : (editingId || urlWizardType !== selectedType))
            return;
        const timer = setTimeout(() => writeWizardDraft({
            key: wizardDraftKey(editingId, selectedType),
            name, category, icon, description, skills, skillContent, visibility, mcpUrl, mcpHeaders,
            agentShareToken, agentId, agentMode, groups, widgetSettings, storeDraft,
            fileNames: [...kbFiles.map(f => f.name), ...(widgetFile ? [widgetFile.name] : [])],
        }), 300);
        return () => clearTimeout(timer);
    }, [view, integrationMode, selectedType, wizardStep, editLoading, editingId, urlEditId, urlWizardType,
        name, category, icon, description, skills, skillContent, visibility, mcpUrl, mcpHeaders,
        agentShareToken, agentId, agentMode, groups, widgetSettings, storeDraft, kbFiles, widgetFile]);
    // An agent built inside the wizard already exists in Digital Workforce: keep its id in the URL.
    const rememberBuiltAgent = useCallback((id: string) => {
        setBuiltAgentId(id);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (id)
                next.set('agent', id);
            else
                next.delete('agent');
            return next;
        }, { replace: true });
    }, [setSearchParams]);
    const [installingCreationId, setInstallingCreationId] = useState<string | null>(null);
    const [pendingCreationInstall, setPendingCreationInstall] = useState<InstallableTool | null>(null);
    const [pendingCreationInstallMode, setPendingCreationInstallMode] = useState<'personal' | 'agency'>('personal');
    const runCreationInstall = useCallback(async (tool: InstallableTool, fieldValues?: Record<string, string>, connectionValues?: Record<string, string>, mode: 'personal' | 'agency' = 'personal') => {
        setInstallingCreationId(tool.id);
        try {
            if (tool.type === 'widget')
                fieldValues = withRecordedGrants(fieldValues, tool.widgetManifest);
            if (mode === 'agency') {
                await apiInstallToolForAgency(tool.id, fieldValues, connectionValues);
                setPendingCreationInstall(null);
                setTools(prev => prev.map(t => t.id === tool.id ? { ...t, isAgencyInstalled: true } : t));
                setPublishToast({ type: 'success', message: installSuccessMessage(tool, 'agency') });
                return;
            }
            await apiInstallTool(tool.id, fieldValues, undefined, connectionValues);
            setPendingCreationInstall(null);
            setPublishToast({ type: 'success', message: installSuccessMessage(tool, 'personal') });
            if (tool.type === 'integration' && tool.apiIntegrationId)
                setCreationEnableTool(tool);
        }
        catch (err: unknown) {
            const msg = err instanceof Error ? err.message : '';
            if (msg === 'already_installed' && tool.type === 'integration' && tool.apiIntegrationId) {
                // Already installed (creators are installed on publish) - go straight to turning it on for agents.
                setCreationEnableTool(tool);
                return;
            }
            setPublishToast({ type: 'error', message: msg === 'already_installed' ? 'You already have this installed.' : (msg || 'Failed to install tool.') });
        }
        finally {
            setInstallingCreationId(null);
            setTimeout(() => setPublishToast(null), 4000);
        }
    }, []);
    const handleInstall = useCallback((tool: InstallableTool) => {
        if (tool.type === 'agent')
            return;
        const hasRequiredFields = (tool.mcpHeaders ?? []).some(h => h.requiredFromInstaller);
        const widgetAsksPermissions = tool.type === 'widget' && (tool.widgetManifest?.permissions?.length ?? 0) > 0;
        const widgetNeedsConnections = tool.type === 'widget' && (tool.widgetManifest?.connections ?? []).length > 0;
        // Integrations always get the prompt: it asks for the API key when one is needed.
        if (((tool.type === 'mcp_server' || tool.type === 'widget') && hasRequiredFields) || widgetAsksPermissions || widgetNeedsConnections || tool.type === 'integration') {
            setPendingCreationInstallMode('personal');
            setPendingCreationInstall(tool);
        }
        else {
            runCreationInstall(tool);
        }
    }, [runCreationInstall]);
    // Install for Agency from a share menu. It used to install straight away, so it never
    // asked for the agency's API key or an MCP server's shared fields - and a creator has no
    // other way to install their own tool for the agency (Browse doesn't list your own tools).
    // Skills have nothing to ask for, so they still install directly.
    const handleInstallForAgency = useCallback((tool: InstallableTool) => {
        if (tool.type === 'skill') {
            runCreationInstall(tool, undefined, undefined, 'agency');
            return;
        }
        setPendingCreationInstallMode('agency');
        setPendingCreationInstall(tool);
    }, [runCreationInstall]);
    // ?installForAgency=<toolId>: Digital Workforce's share menus send integrations and MCP
    // servers here, so installing for the agency always goes through the same prompt.
    const installForAgencyId = searchParams.get('installForAgency');
    useEffect(() => {
        if (!installForAgencyId || view !== 'creations' || loadingTools)
            return;
        const tool = tools.find(t => t.id === installForAgencyId);
        const next = new URLSearchParams(searchParams);
        next.delete('installForAgency');
        setSearchParams(next, { replace: true });
        if (tool && !tool.isAgencyInstalled)
            handleInstallForAgency(tool);
    }, [installForAgencyId, view, loadingTools, tools, searchParams, setSearchParams, handleInstallForAgency]);
    const handleUninstallForAgency = useCallback(async (tool: MyTool) => {
        try {
            await apiUninstallToolForAgency(tool.id);
            setTools(prev => prev.map(t => t.id === tool.id ? { ...t, isAgencyInstalled: false } : t));
            setPublishToast({ type: 'success', message: `${tool.name} uninstalled for your agency.` });
        }
        catch (err: unknown) {
            const msg = err instanceof Error ? err.message : '';
            setPublishToast({ type: 'error', message: msg || 'Failed to uninstall for agency.' });
        }
        setTimeout(() => setPublishToast(null), 4000);
    }, []);
    const handleSave = useCallback(async () => {
        if (!selectedType)
            return;
        const willAutoInstall = !editingId && selectedType !== 'agent';
        if (selectedType === 'widget' && !widgetFile
            && widgetSettingsChangedSinceUpload(existingWidgetManifest, existingWidgetVersion, widgetSettings, widgetConnections)
            && !window.confirm("Your widget setting changes (version, surfaces, sizing, connections) only save with a bundle upload, and you haven't chosen a file.\n\nSave the rest of your changes without them?"))
            return;
        // Any save of a Global tool goes back through review (and drops it to My Agency until
        // approved) — even a description-only edit. That happened silently; ask first.
        if (editingId && tools.find(t => t.id === editingId)?.visibility === 'published' && visibility === 'published'
            && !window.confirm(selectedType === 'widget'
                ? 'This widget is Global. Saving any change (including where it\'s added) sends it back for review: it moves to My Agency and leaves Toolbox until a reviewer approves it again. Agencies that already installed it keep running the approved version.\n\nSave and resubmit?'
                : 'This tool is Global. Saving any change (including where it\'s added) sends it back for review: it moves to My Agency, and is off Global and Toolbox, until a reviewer approves it again.\n\nSave and resubmit?'))
            return;
        setSaving(true);
        const kbFileNames = kbFiles.length > 0
            ? kbFiles.map(f => f.name)
            : existingKbFileNames;
        const submitForGlobalReview = visibility === 'published';
        // While a Global request waits for review the tool can't be Published yet, but it
        // shouldn't disappear from people who already have it: one already shared with the
        // agency (or already published) stays visible to the agency until it's approved.
        const currentVisibility = editingId ? tools.find(t => t.id === editingId)?.visibility : undefined;
        const visibilityWhileInReview: Visibility = currentVisibility === 'my_agency' || currentVisibility === 'published' ? 'my_agency' : 'just_me';
        if (selectedType === 'agent' && !agentShareToken) {
            alert('Pick an agent and click Update before saving.');
            setSaving(false);
            return;
        }
        try {
            const payload = {
                id: editingId ? Number(editingId) : undefined,
                type: TOOL_TYPE_TO_INT[selectedType],
                name: name.trim(),
                category,
                icon: icon.trim(),
                description: description.trim(),
                skills: skills.trim(),
                kbFileNames,
                visibility: VISIBILITY_TO_INT[submitForGlobalReview ? visibilityWhileInReview : visibility],
                ...(selectedType === 'mcp_server' ? {
                    mcpUrl: mcpUrl.trim(),
                    mcpHeadersTemplate: mcpHeaders
                        .filter(h => h.key.trim())
                        .map(h => ({ [h.key.trim()]: h.requiredFromInstaller ? '' : h.value })),
                    configSchema: mcpHeaders
                        .filter(h => h.key.trim())
                        .map(h => ({
                        key: h.key.trim(), label: h.label?.trim() || h.key.trim(), type: 'text' as const,
                        requiredFromInstaller: h.requiredFromInstaller, hint: h.hint?.trim() || undefined,
                        scope: h.requiredFromInstaller ? (h.scope || 'shared') : undefined,
                    })),
                } : {}),
                ...(selectedType === 'skill' ? {
                    skillContent: skillContent.trim(),
                } : {}),
                ...(selectedType === 'agent' ? {
                    agentShareToken,
                } : {}),
                groups: groupsForSave(selectedType, groups),
            };
            const savedId = await apiUpsertTool(payload);
            await saveMarketplaceStore(String(savedId), storeDraft);
            const savedVisibility: Visibility = submitForGlobalReview ? visibilityWhileInReview : visibility;
            let contentOk = true;
            if (selectedType === 'skill' && kbFiles.length > 0) {
                const failures: string[] = [];
                for (const file of kbFiles) {
                    try {
                        await apiUploadSkillKbFile(savedId, file);
                    }
                    catch (err: any) {
                        failures.push(file.name);
                    }
                }
                setKbFiles([]);
                if (failures.length) {
                    contentOk = false;
                    alert(`Saved, but failed to upload: ${failures.join(', ')}. You can retry from the tool's detail page.`);
                }
            }
            let bundleOk = true;
            let widgetNeedsInstallConfig = false;
            let uploadedManifest: WidgetManifest | null = null;
            if (selectedType === 'widget' && widgetFile) {
                try {
                    const parsedMinHeight = widgetSettings.minHeight.trim() ? Number(widgetSettings.minHeight) : undefined;
                    const overrides: WidgetBundleOverrides = {
                        connections: widgetConnections.filter(c => c.key.trim() && c.baseUrl.trim()),
                        surfaces: widgetSettings.surfaces,
                        version: widgetSettings.version.trim() || undefined,
                        minHeight: Number.isFinite(parsedMinHeight) ? parsedMinHeight : undefined,
                        autoResize: widgetSettings.autoResize,
                    };
                    const uploaded = await uploadWidgetBundle(String(savedId), widgetFile, overrides);
                    uploadedManifest = uploaded.manifest;
                    setWidgetFile(null);
                    setWidgetLocalHtml(null);
                    setExistingWidgetManifest(uploaded.manifest);
                    setExistingWidgetVersion(uploaded.version);
                    setWidgetConnections(uploaded.manifest.connections ?? []);
                    setWidgetSettings(s => ({
                        ...s,
                        version: uploaded.manifest.version ?? s.version,
                        surfaces: ((uploaded.manifest.surfaces?.length ? uploaded.manifest.surfaces : ['page']) as WidgetSurface[]),
                    }));
                    widgetNeedsInstallConfig = (uploaded.manifest.config ?? []).some(f => f.requiredFromInstaller);
                    if (uploaded.warnings.length) {
                        alert(`Bundle saved with warnings:\n• ${uploaded.warnings.slice(0, 8).join('\n• ')}`);
                    }
                    if (uploaded.visibilityDemoted && !submitForGlobalReview) {
                        alert('This widget was Global. The new code has not been reviewed, so it is back to My Agency until you submit it for review again. Other agencies that installed it keep running the approved version in the meantime.');
                    }
                    else if (uploaded.pendingWithdrawn && !submitForGlobalReview) {
                        alert('Your pending review request was withdrawn because the code changed. Submit for review again when the new version is ready.');
                    }
                }
                catch (err: any) {
                    bundleOk = false;
                    alert(`Saved, but the widget bundle was rejected: ${err.message}\nEdit the widget to upload a corrected bundle.`);
                }
            }
            if (submitForGlobalReview && bundleOk && contentOk) {
                try {
                    await apiSubmitPublishRequest(String(savedId));
                }
                catch (err: any) {
                    alert(err.message === 'already_pending'
                        ? 'Saved. This tool already has a pending review request.'
                        : (err.message || 'Saved, but failed to submit for review. You can retry from Submit for Review.'));
                }
            }
            else if (visibility !== 'published' && bundleOk && contentOk) {
                await releaseMarketplaceListing(String(savedId));
            }
            if (editingId) {
                setTools(prev => prev.map(t => t.id === editingId
                    ? {
                        ...t, type: selectedType, name: name.trim(), category, icon: icon.trim(), description: description.trim(),
                        skills: skills.trim(), kbFileNames, visibility: savedVisibility,
                        skillContent: selectedType === 'skill' ? skillContent.trim() : t.skillContent,
                        publishStatus: submitForGlobalReview ? 'pending' : t.publishStatus,
                        groups: [...(t.groups ?? []).filter(isSurfaceGroup), ...groupsForSave(selectedType, groups)],
                    }
                    : t));
            }
            else {
                // The tool already exists by now. A failed refresh used to surface as a save
                // failure and keep the wizard open, so the next Save created a duplicate tool.
                try {
                    setTools(await apiFetchCreations());
                }
                catch {
                    setPublishToast({ type: 'error', message: 'Saved — but your list couldn’t refresh. Reload the page to see it.' });
                    setTimeout(() => setPublishToast(null), 5000);
                }
            }
            if (willAutoInstall && bundleOk && selectedType === 'widget' && widgetNeedsInstallConfig) {
                setPublishToast({ type: 'success', message: 'Widget saved. Install it from My Creations to provide its settings.' });
                setTimeout(() => setPublishToast(null), 5000);
            }
            else if (willAutoInstall && bundleOk) {
                // The uploaded manifest lets handleInstall open the same permission/connection prompt
                // any other widget install gets; without it, every permission was silently granted
                // (and future ones auto-granted, since nothing was recorded as decided).
                handleInstall({ id: String(savedId), name: name.trim(), type: selectedType, icon: icon.trim(), mcpHeaders, visibility: savedVisibility, description: description.trim(), mcpUrl, widgetManifest: uploadedManifest, groups: groupsForSave(selectedType, groups) });
            }
            clearWizardDraft();
            setSearchParams({ view: 'creations' });
        }
        catch (err: any) {
            alert(err.message || 'Failed to save tool');
        }
        finally {
            setSaving(false);
        }
    }, [
        selectedType, editingId, name, category, icon, description, skills, skillContent,
        kbFiles, existingKbFileNames, visibility, mcpUrl, mcpHeaders, agentShareToken, widgetFile,
        widgetConnections, widgetSettings, existingWidgetManifest, existingWidgetVersion, setSearchParams, handleInstall,
        tools, storeDraft, groups,
    ]);
    const handleDelete = useCallback(async (id: string) => {
        const deleted = tools.find(t => t.id === id);
        setTools(prev => prev.filter(t => t.id !== id));
        try {
            await apiDeleteTool(id);
            setPublishToast({ type: 'success', message: `${deleted?.name ?? 'Tool'} deleted.` });
        }
        catch (err: any) {
            setPublishToast({ type: 'error', message: err.message || 'Failed to delete tool.' });
            apiFetchCreations().then(setTools).catch(() => { });
        }
        setTimeout(() => setPublishToast(null), 4000);
    }, [tools]);
    const finishIntegrationPublish = useCallback(async (toolName: string, integrationId: number, message?: string) => {
        setIntegrationDirty(false);
        setImportDirty(false);
        const fresh = await apiFetchCreations().catch(() => [] as MyTool[]);
        setTools(fresh);
        setPublishToast({ type: 'success', message: message ?? `${toolName} is saved and installed for you.` });
        setTimeout(() => setPublishToast(null), 4000);
        setSearchParams({ view: 'creations' });
        setSelectedTool(fresh.find(t => t.apiIntegrationId === integrationId) ?? null);
    }, [setSearchParams]);
    const handlePublishIntegration = useCallback(async () => {
        if (!integrationDetail || !connectSettings)
            return;
        const { integration } = integrationDetail;
        const problem = !name.trim() ? 'Give the integration a name on the Info step.' : connectProblem(connectSettings, integration);
        if (problem) {
            setPublishError(problem);
            return;
        }
        setSaving(true);
        setPublishError(null);
        setPublishWarnings([]);
        try {
            const wantsGlobal = visibility === 'published';
            // The store page (logo, media, description, version) is saved with the listing row.
            // When the row already exists it goes first, so a Global submission's review snapshot
            // includes it; a first publish creates the row, so it's saved right after.
            const existingListingId = integration.my_tools_id ?? null;
            if (existingListingId)
                await saveMarketplaceStore(String(existingListingId), storeDraft);
            const { integration: saved, warnings, publish_status, keys_removed } = await publishIntegration(integration.id, {
                visibility: wantsGlobal ? 'global' : visibility === 'my_agency' ? 'my_agency' : 'just_me',
                installer_key_scope: connectSettings.authType !== 'none' ? connectSettings.keyScope : undefined,
                name: name.trim(),
                description: description.trim(),
                icon: icon.trim() || '🔌',
                category,
                skills: skills.trim(),
                base_url: connectSettings.baseUrl.trim(),
                auth: authPayload(connectSettings),
            });
            setIntegrationDetail({ ...integrationDetail, integration: { ...integration, ...saved } });
            setConnectSettings({ ...connectSettings, secret: '' });
            const listingId = existingListingId ?? saved.my_tools_id ?? null;
            if (listingId && !existingListingId)
                await saveMarketplaceStore(String(listingId), storeDraft);
            // Only Global listings are reviewed, so every other save records its version now (Global ones on approval).
            if (listingId && visibility !== 'published')
                await releaseMarketplaceListing(String(listingId)).catch(() => { });
            setIntegrationDirty(false);
            if (warnings.length > 0) {
                setPublishWarnings(Array.from(new Set(warnings)));
                return;
            }
            // Changing who enters the key removes the keys saved the old way.
            const keysNote = keys_removed ? ` ${keys_removed} saved ${keys_removed === 1 ? 'key that no longer applies was' : 'keys that no longer apply were'} removed.` : '';
            await finishIntegrationPublish(saved.name, integration.id, wantsGlobal && publish_status === 'pending'
                ? `${saved.name} is saved and submitted for Global review. Other agencies can install it once a reviewer approves it.${keysNote}`
                : keysNote ? `${saved.name} is saved.${keysNote}` : undefined);
        }
        catch (err: unknown) {
            setPublishError(err instanceof Error ? err.message : 'Saving failed. Please try again.');
        }
        finally {
            setSaving(false);
        }
    }, [integrationDetail, connectSettings, name, description, icon, category, skills, visibility, finishIntegrationPublish, storeDraft]);
    const globalChangeCallout = integrationIsGlobal
        ? <div className="ai-callout ai-callout--warn" style={{ marginBottom: 12 }}>{GLOBAL_CHANGE_NOTE}</div>
        : null;
    const renderIntegrationStep = () => {
        if (integrationLoadError) {
            return (<div className="mt-step-content">
            <h2 className="mt-step-title">Couldn't open this integration</h2>
            <p className="ai-error">{integrationLoadError}</p>
            <div className="mt-actions">
              <button type="button" className="mt-btn mt-btn--secondary" onClick={() => setSearchParams({ view: 'creations' })}>← Back to My Creations</button>
              <button type="button" className="mt-btn mt-btn--primary" onClick={() => urlIntegrationId && loadIntegration(Number(urlIntegrationId))}>Try again</button>
            </div>
          </div>);
        }
        if (urlIntegrationId && !integrationDetail) {
            return <div className="mt-step-content"><p className="ai-progress"><span className="ai-spinner" aria-hidden="true"/>Loading integration…</p></div>;
        }
        const step: IntegrationStepKey = integrationDetail ? integrationStep : 'import';
        if (step === 'import' || !integrationDetail || !connectSettings) {
            return (<ImportStep key="integration-import" existing={integrationDetail} onDirtyChange={setImportDirty} onBack={() => {
                    if (integrationDetail) {
                        if (confirmLeaveIntegration())
                            setSearchParams({ view: 'creations' });
                        return;
                    }
                    if (!confirmLeaveIntegration())
                        return;
                    setSearchParams({ view: 'create' });
                }} onNext={() => goIntegrationStep('endpoints')} onParsed={(detail, warnings) => {
                    const isNew = !integrationDetail;
                    setIntegrationDetail(detail);
                    setIntegrationWarnings(warnings);
                    setImportDirty(false);
                    if (isNew)
                        applyIntegrationInfo(detail, null, true);
                    else
                        setConnectSettings(connectSettingsFrom(detail.integration));
                    goIntegrationStep('endpoints', detail.integration.id);
                }}/>);
        }
        if (step === 'endpoints') {
            return (<>{globalChangeCallout}<EndpointsStep key="integration-endpoints" detail={integrationDetail} warnings={integrationWarnings} onChange={setIntegrationDetail} onBack={() => goIntegrationStep('import')} onNext={() => {
                    if (!skills.trim()) {
                        setSkills(integrationDetail.endpoints.map(ep => ep.summary || ep.description || ep.name).filter(Boolean).join('\n'));
                    }
                    goIntegrationStep('info');
                }}/></>);
        }
        if (step === 'info') {
            return (<Step2Info key="integration-info" selectedType="integration" groups={groups} setGroups={setGroups} name={name} setName={trackIntegration(setName)} category={category} setCategory={trackIntegration(setCategory)} icon={icon} setIcon={trackIntegration(setIcon)} description={description} setDescription={trackIntegration(setDescription)} skills={skills} setSkills={trackIntegration(setSkills)} skillContent="" setSkillContent={() => { }} kbFiles={[]} setKbFiles={() => { }} editingId={null} existingSkillKbFiles={[]} setExistingSkillKbFiles={() => { }} visibility={visibility} setVisibility={trackIntegration(setVisibility)} mcpUrl="" setMcpUrl={() => { }} mcpHeaders={[]} setMcpHeaders={() => { }} agentId="" setAgentId={() => { }} agentShareToken="" setAgentShareToken={() => { }} agentPreview={null} setAgentPreview={() => { }} agentFrozenSnapshot={null} setAgentFrozenSnapshot={() => { }} widgetFile={null} setWidgetFile={() => { }} widgetLocalHtml={null} setWidgetLocalHtml={() => { }} existingWidgetManifest={null} existingWidgetVersion="" widgetConnections={[]} setWidgetConnections={() => { }} widgetSettings={widgetSettings} setWidgetSettings={() => { }} section="info" iconUrl={storeDraft.iconUrl} onIconFile={file => { setIntegrationDirty(true); void handleIconFile(file); }} onClearIcon={() => { setIntegrationDirty(true); setStoreDraft(d => ({ ...d, iconUrl: undefined })); }} iconError={iconError} onBack={() => goIntegrationStep('endpoints')} onNext={() => goIntegrationStep('connect')}/>);
        }
        if (step === 'connect') {
            return (<>{globalChangeCallout}<ConnectStep key="integration-connect" detail={integrationDetail} settings={connectSettings} onSettingsChange={trackIntegration(setConnectSettings)} onDetailChange={setIntegrationDetail} onBack={() => goIntegrationStep('info')} onNext={() => goIntegrationStep('review')}/></>);
        }
        const { integration, endpoints } = integrationDetail;
        const isLive = integration.status === 'published';
        // Store page edits aren't tracked by the Info / Connect fields: count them as unsaved too.
        const setIntegrationStoreDraft: React.Dispatch<React.SetStateAction<StoreDraft>> = value => {
            setStoreDraft(value);
            setIntegrationDirty(true);
        };
        return (<StorePageStep key="integration-review" selectedType="integration" name={name} category={category} icon={icon} visibility={visibility} description={description} setDescription={trackIntegration(setDescription)} storeDraft={storeDraft} setStoreDraft={setIntegrationStoreDraft} agentPreview={null} mcpUrl="" mcpHeaders={[]} skillContent="" kbFileNames={[]} widgetFile={null} widgetLocalHtml={null} widgetManifest={null} widgetSettings={widgetSettings} groups={groups} isEditing={isLive} saving={saving} onBack={() => goIntegrationStep('connect')} onSave={handlePublishIntegration} aiFacts={endpoints.map(ep => ep.summary || ep.description || ep.name).filter(Boolean).slice(0, 12).map(what => `Lets agents ${what.charAt(0).toLowerCase()}${what.slice(1)}`)} includesOverride={<>
                <dl className="ai-detail-grid">
                  <dt>Calls</dt><dd className="ai-mono">{connectSettings.baseUrl.trim() || 'Not set'}</dd>
                  <dt>Authentication</dt><dd>{authSummary({ ...integration, auth_type: connectSettings.authType, auth_location: connectSettings.location, auth_param_name: connectSettings.paramName })}</dd>
                  <dt>Endpoints</dt><dd>{endpoints.length}</dd>
                  {connectSettings.authType !== 'none' && (<>
                      <dt>Others' API keys</dt>
                      <dd>{connectSettings.keyScope === 'per_user' ? 'Each person who installs it enters their own' : 'One per agency, entered by an Agency Admin'} - never yours</dd>
                    </>)}
                </dl>
                <IntegrationDetailBody apiIntegrationId={integration.id}/>
              </>} footer={<>
                {isLive && !integrationIsGlobal && (<p className="mt-step-subtitle" style={{ margin: '4px 0 0' }}>This integration is live - saving applies your changes to chats right away.</p>)}
                {integrationIsGlobal && (<p className="mt-step-subtitle" style={{ margin: '4px 0 0' }}>{GLOBAL_CHANGE_NOTE}</p>)}
                {publishError && <p className="ai-error" role="alert">{publishError}</p>}
                {publishWarnings.length > 0 && (<div className="ai-callout ai-callout--warn">
                    Saved, but:
                    <ul>{publishWarnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                    <div className="mt-actions" style={{ marginTop: 8 }}>
                      <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => setPublishWarnings([])}>Stay and fix</button>
                      <button type="button" className="mt-btn mt-btn--primary mt-btn--sm" onClick={() => finishIntegrationPublish(name.trim() || integration.name, integration.id)}>Continue anyway</button>
                    </div>
                  </div>)}
              </>}/>);
    };
    const tabs: {
        key: WizardView;
        label: string;
    }[] = [
        { key: 'creations', label: 'My Creations' },
        { key: 'installed', label: 'Installed' },
        { key: 'marketplace', label: 'Browse' },
        { key: 'create', label: '+ Create New' },
        { key: 'guide', label: 'Guide' },
    ];
    return (<div className="mt-page" ref={pageRef}>
      <div className="mt-header">
        <h1 className="mt-header__title">Marketplace</h1>
        <p className="mt-header__subtitle">Browse tools, or build, publish, and manage your own on Momentum AMP.</p>
      </div>

      <div className="mt-tabs">
        {tabs.map(tab => (<button key={tab.key} type="button" className={`mt-tab ${view === tab.key && !(tab.key === 'create' && editingId) ? 'mt-tab--active' : ''}`} onClick={() => {
                if (view === 'create' && !confirmLeaveIntegration())
                    return;
                setSearchParams({ view: tab.key });
            }}>
            {tab.key === 'create' && editingId ? 'Editing' : tab.label}
          </button>))}
      </div>

      {view === 'creations' && !selectedTool && (<CreationsView tools={tools} loading={loadingTools} onDelete={handleDelete} onEdit={handleEdit} onUpdate={handleUpdate} onInstall={handleInstall} onInstallForAgency={handleInstallForAgency} installingId={installingCreationId} onUninstallForAgency={handleUninstallForAgency} onViewDetail={setSelectedTool} onRefetchTools={() => { apiFetchCreations().then(setTools).catch(() => { }); }}/>)}

      <AnimatePresence>
        {creationEnableTool?.apiIntegrationId && (<AgentEnablementModal toolName={creationEnableTool.name} integrationId={creationEnableTool.apiIntegrationId} onClose={() => setCreationEnableTool(null)}/>)}
      </AnimatePresence>

      <AnimatePresence>
        {pendingCreationInstall && (<MarketplaceInstallModal key={`${pendingCreationInstall.id}-${pendingCreationInstallMode}`} tool={pendingCreationInstall} initialMode={pendingCreationInstallMode} installing={installingCreationId === pendingCreationInstall.id} onCancel={() => setPendingCreationInstall(null)} onConfirm={(values, connectionValues) => runCreationInstall(pendingCreationInstall, values, connectionValues, pendingCreationInstallMode)}/>)}
      </AnimatePresence>

      <AnimatePresence>
      </AnimatePresence>

      {view === 'installed' && <InstalledView />}

      {view === 'guide' && <MarketplaceGuide onNavigate={v => setSearchParams({ view: v })}/>}

      {view === 'marketplace' && <MarketplaceView />}

      <AnimatePresence mode="wait">
        {view === 'creations' && selectedTool && (<ToolDetailPanel key={selectedTool.id} tool={selectedTool} onClose={() => setSelectedTool(null)} onEdit={tool => { setSelectedTool(null); void handleEdit(tool); }}/>)}
      </AnimatePresence>

      <AnimatePresence>
        {publishToast && (<motion.div className={publishToast.type === 'success' ? 'success-toast' : 'error-toast'} initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.25 }}>
            <span>{publishToast.message}</span>
          </motion.div>)}
      </AnimatePresence>

      {view === 'create' && (<div className="mt-wizard">
          <StepIndicator current={integrationMode ? integrationStepNumber(integrationDetail ? integrationStep : 'import') : shownStep} steps={integrationMode || selectedType === 'integration' ? INTEGRATION_STEP_LABELS : undefined} {...(editingId && !wizardBusy ? (integrationMode
            // Editing: jump to any step after the type (and, for integrations, the docs import).
            ? integrationDetail ? { firstSelectable: 3, onSelect: (n: number) => goIntegrationStep(INTEGRATION_STEPS[n - 1].key) } : {}
            : { firstSelectable: 2, onSelect: (n: number) => goWizardStep(n) }) : {})}/>

          {integrationMode ? (<AnimatePresence mode="wait">{renderIntegrationStep()}</AnimatePresence>) : (<AnimatePresence mode="wait">
            {wizardBusy && (<div key="wizard-loading" className="mt-step-content"><p className="ai-progress"><span className="ai-spinner" aria-hidden="true"/>Loading…</p></div>)}
            {!wizardBusy && shownStep === 1 && (<Step1Type key="step1" selected={selectedType} onSelect={selectType} onNext={() => {
                    if (selectedType === 'integration') {
                        goIntegrationStep('import');
                        return;
                    }
                    goWizardStep(2, { type: selectedType, edit: null, ...(selectedType !== 'agent' ? { agent: null } : {}) });
                }}/>)}
            {!wizardBusy && shownStep === 2 && selectedType && (<Step2Info key="step2" selectedType={selectedType} name={name} setName={setName} category={category} setCategory={setCategory} icon={icon} setIcon={setIcon} description={description} setDescription={setDescription} skills={skills} setSkills={setSkills} skillContent={skillContent} setSkillContent={setSkillContent} kbFiles={kbFiles} setKbFiles={setKbFiles} editingId={editingId} existingSkillKbFiles={existingSkillKbFiles} setExistingSkillKbFiles={setExistingSkillKbFiles} visibility={visibility} setVisibility={setVisibility} mcpUrl={mcpUrl} setMcpUrl={setMcpUrl} mcpHeaders={mcpHeaders} setMcpHeaders={setMcpHeaders} agentId={agentId} setAgentId={setAgentId} agentShareToken={agentShareToken} setAgentShareToken={setAgentShareToken} agentPreview={agentPreview} setAgentPreview={setAgentPreview} agentFrozenSnapshot={agentFrozenSnapshot} setAgentFrozenSnapshot={setAgentFrozenSnapshot} widgetFile={widgetFile} setWidgetFile={setWidgetFile} widgetLocalHtml={widgetLocalHtml} setWidgetLocalHtml={setWidgetLocalHtml} existingWidgetManifest={existingWidgetManifest} existingWidgetVersion={existingWidgetVersion} widgetConnections={widgetConnections} setWidgetConnections={setWidgetConnections} widgetSettings={widgetSettings} setWidgetSettings={setWidgetSettings} groups={groups} setGroups={setGroups} section="setup" agentMode={agentMode} setAgentMode={setAgentMode} builtAgentId={builtAgentId} setBuiltAgentId={rememberBuiltAgent} onBack={() => editingId ? setSearchParams({ view: 'creations' }) : goWizardStep(1)} onNext={() => goWizardStep(3)}/>)}
            {!wizardBusy && shownStep === 3 && selectedType && (<Step2Info key="step3" selectedType={selectedType} name={name} setName={setName} category={category} setCategory={setCategory} icon={icon} setIcon={setIcon} description={description} setDescription={setDescription} skills={skills} setSkills={setSkills} skillContent={skillContent} setSkillContent={setSkillContent} kbFiles={kbFiles} setKbFiles={setKbFiles} editingId={editingId} existingSkillKbFiles={existingSkillKbFiles} setExistingSkillKbFiles={setExistingSkillKbFiles} visibility={visibility} setVisibility={setVisibility} mcpUrl={mcpUrl} setMcpUrl={setMcpUrl} mcpHeaders={mcpHeaders} setMcpHeaders={setMcpHeaders} agentId={agentId} setAgentId={setAgentId} agentShareToken={agentShareToken} setAgentShareToken={setAgentShareToken} agentPreview={agentPreview} setAgentPreview={setAgentPreview} agentFrozenSnapshot={agentFrozenSnapshot} setAgentFrozenSnapshot={setAgentFrozenSnapshot} widgetFile={widgetFile} setWidgetFile={setWidgetFile} widgetLocalHtml={widgetLocalHtml} setWidgetLocalHtml={setWidgetLocalHtml} existingWidgetManifest={existingWidgetManifest} existingWidgetVersion={existingWidgetVersion} widgetConnections={widgetConnections} setWidgetConnections={setWidgetConnections} widgetSettings={widgetSettings} setWidgetSettings={setWidgetSettings} groups={groups} setGroups={setGroups} section="info" iconUrl={storeDraft.iconUrl} onIconFile={file => { void handleIconFile(file); }} onClearIcon={() => setStoreDraft(d => ({ ...d, iconUrl: undefined }))} iconError={iconError} onBack={() => goWizardStep(2)} onNext={() => goWizardStep(4)}/>)}
            {!wizardBusy && shownStep === 4 && selectedType && (<StorePageStep key="step4" selectedType={selectedType} name={name} category={category} icon={icon} visibility={visibility} description={description} setDescription={setDescription} storeDraft={storeDraft} setStoreDraft={setStoreDraft} agentPreview={agentPreview} mcpUrl={mcpUrl} mcpHeaders={mcpHeaders} skillContent={skillContent} kbFileNames={kbFiles.length > 0 ? kbFiles.map(file => file.name) : existingKbFileNames} widgetFile={widgetFile} widgetLocalHtml={widgetLocalHtml} widgetManifest={existingWidgetManifest} widgetSettings={widgetSettings} groups={groups} isEditing={!!editingId} saving={saving} onBack={() => goWizardStep(3)} onSave={() => handleSave()}/>)}
          </AnimatePresence>)}
        </div>)}
    </div>);
};
export default MyToolsPage;
